import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import GTMIntegration from "./components/GTMIntegration";
import { crawlSite } from "./utils/crawlEngine";
import { rankPassages, buildPassages, buildLivePassages, scoreFromRank } from "./utils/sharkRank";
// ---------------------------------------------
// BRAND / THEME (v0 palette - Argon style)
// ---------------------------------------------
// ─── Shark Branding Solutions - Official Color System ───────────────────────
const T = {
  // Backgrounds - deep ocean, brand-aligned
  bg:              "#060C0F",                        // Near-black with cool teal undertone
  surface:         "rgba(10, 24, 32, 0.78)",         // Glassmorphism - teal-tinted dark
  surfaceBorder:   "rgba(21, 180, 214, 0.1)",        // Shark Blue tinted border
  surfaceBorderHover: "rgba(21, 180, 214, 0.28)",

  // Brand primaries
  white:           "#FFFFFF",
  black:           "#000000",
  navy:            "#040A0D",                        // Deepest dark

  // Primary: Shark Blue #15B4D6
  blue:            "#15B4D6",
  blueLight:       "#3DC5E2",                        // Hover/lighter variant
  blueDark:        "#0E8FAA",                        // Pressed/deeper variant
  blueGlow:        "rgba(21, 180, 214, 0.18)",

  // Secondary: Deep Teal Blue #1F7085
  tealDeep:        "#1F7085",
  tealDeepGlow:    "rgba(31, 112, 133, 0.25)",

  // Supporting
  mint:            "#4FD1A0",                        // Soft Yellow → success states
  yellow:          "#F9DB79",                        // Soft Yellow → warnings
  coral:           "#F7555F",                        // Coral Accent → errors/danger
  slate:           "#517F72",                        // Teal Green/Slate → neutral

  // Semantic aliases
  teal:            "#4FD1A0",                        // success = mint
  gold:            "#F9DB79",                        // warning = soft yellow
  red:             "#F7555F",                        // error = coral
  orange:          "#F9DB79",                        // orange → soft yellow

  // Text
  text:            "#F0F6F8",                        // Bright white with cool tint
  textMuted:       "#A8C4CC",                        // Muted - teal-tinted
  ghost:           "#527A85",                        // Ghost - mid teal-slate

  // Inputs & borders
  border:          "rgba(21, 180, 214, 0.12)",
  input:           "rgba(4, 16, 22, 0.88)",

  // Gradient shorthands
  gradBlue:        "linear-gradient(135deg, #15B4D6, #1F7085)",
  gradMint:        "linear-gradient(135deg, #4FD1A0, #15B4D6)",
  gradWarm:        "linear-gradient(135deg, #F9DB79, #F7555F)",
  gradSlate:       "linear-gradient(135deg, #517F72, #1F7085)",
};

// ---------------------------------------------
// PERSISTENCE
// ---------------------------------------------
const LS = {
  get: (k) => { try { const v = localStorage.getItem("sharkos_"+k); return v ? JSON.parse(v) : null; } catch { return null; } },
  set: (k,v) => { try { localStorage.setItem("sharkos_"+k, JSON.stringify(v)); } catch {} },
};

async function ipc(channel, ...args) {
  if (false) return; // data lives in localStorage via mocks below - see handleInject for direct sharkOS.invoke usage
  const mock = {
    "get-clients":        () => { const v=LS.get("clients"); return Array.isArray(v)?v:[]; },
    "save-client":        (c) => { const v=LS.get("clients"); const cs=Array.isArray(v)?v:[]; const i=cs.findIndex(x=>x.id===c.id); i>=0?cs.splice(i,1,c):cs.unshift(c); LS.set("clients",cs); return c; },
    "delete-client":      (id) => { const v=LS.get("clients"); const cs=Array.isArray(v)?v:[]; LS.set("clients",cs.filter(x=>x.id!==id)); return true; },
    "get-settings":       () => LS.get("settings") || {},
    "save-settings":      (s) => { LS.set("settings",s); return s; },
    "get-checklist":      (cid) => LS.get("checklist_"+cid) || {},
    "save-checklist":     ({clientId,state}) => { LS.set("checklist_"+clientId,state); return true; },
    "get-report-history": (cid) => LS.get("reports_"+cid) || [],
    "save-report":        ({clientId,report}) => { const rs=LS.get("reports_"+clientId)||[]; rs.unshift(report); LS.set("reports_"+clientId,rs); return report; },
    "run-audit":          () => new Promise(r=>setTimeout(()=>r({score:Math.floor(Math.random()*40)+55}),1800)),
    "gmb-sync":           () => new Promise(r=>setTimeout(()=>r({name:"Synced",phone:"(555) 000-0000",address:"Tampa Bay, FL",verified:true}),1200)),
    "deploy-gtm":         () => new Promise(r=>setTimeout(()=>r({success:true}),500)),
    "inject-schema":      () => new Promise(r=>setTimeout(()=>r({success:Math.random()>0.12}),800+Math.random()*500)),
    "generate-report":    () => new Promise(r=>setTimeout(()=>r({path:"report_"+Date.now()+".pdf"}),1500)),

  };
  const fn = mock[channel]; return fn ? fn(...args) : null;
}

// =============================================
// DEMO MODE — mock data, no API calls
// =============================================

const DEMO_CLIENTS = [
  {
    id:"d1", name:"Sunshine Dental Group", domain:"sunshinedental.com",
    businessType:"Dental Practice", city:"Wesley Chapel", score:91, grade:"A",
    gtm:"GTM-DEMO01", phone:"(813) 555-0191", address:"123 Bruce B Downs Blvd, Wesley Chapel, FL 33544",
    lastUpdated:"Mar 28, 2026", claimed:"yes", verified:"yes", rating:"4.9", reviews:"312",
    _badges:["schema_live","gtm_connected","faq_injected","reviewed","first_citation","streak_3"],
    _streak:4, _delta:+7, _level:5,
  },
  {
    id:"d2", name:"Elite Law Firm PA", domain:"elitelawfirm.com",
    businessType:"Law Firm", city:"Tampa", score:78, grade:"B",
    gtm:"GTM-DEMO02", phone:"(813) 555-0278", address:"400 N Ashley Dr, Tampa, FL 33602",
    lastUpdated:"Mar 25, 2026", claimed:"yes", verified:"yes", rating:"4.7", reviews:"89",
    _badges:["schema_live","gtm_connected","reviewed"],
    _streak:2, _delta:+12, _level:4,
  },
  {
    id:"d3", name:"Coastal MedSpa", domain:"coastalmedspa.com",
    businessType:"MedSpa", city:"St. Petersburg", score:67, grade:"C",
    gtm:"GTM-DEMO03", phone:"(727) 555-0367", address:"200 Beach Dr NE, St. Petersburg, FL 33701",
    lastUpdated:"Mar 20, 2026", claimed:"yes", verified:"no", rating:"4.5", reviews:"54",
    _badges:["schema_live","gtm_connected"],
    _streak:1, _delta:+3, _level:3,
  },
  {
    id:"d4", name:"Bay Area HVAC Pros", domain:"bayareahvac.com",
    businessType:"HVAC", city:"Land O Lakes", score:55, grade:"D",
    gtm:"GTM-DEMO04", phone:"(813) 555-0455", address:"5500 Land O Lakes Blvd, Land O Lakes, FL 34639",
    lastUpdated:"Mar 15, 2026", claimed:"yes", verified:"no", rating:"4.3", reviews:"27",
    _badges:["gtm_connected"],
    _streak:0, _delta:-2, _level:2,
  },
  {
    id:"d5", name:"Northshore Financial Group", domain:"northshorefinancial.com",
    businessType:"Financial Advisor", city:"Lutz", score:43, grade:"F",
    gtm:"", phone:"(813) 555-0543", address:"18920 N Dale Mabry Hwy, Lutz, FL 33548",
    lastUpdated:"Mar 10, 2026", claimed:"no", verified:"no", rating:"", reviews:"",
    _badges:[],
    _streak:0, _delta:null, _level:1,
  },
  {
    id:"d6", name:"Premier Roofing Co", domain:"premierroofing.com",
    businessType:"Roofing", city:"Wesley Chapel", score:82, grade:"B",
    gtm:"GTM-DEMO06", phone:"(813) 555-0682", address:"27000 Wesley Chapel Blvd, Wesley Chapel, FL 33544",
    lastUpdated:"Mar 27, 2026", claimed:"yes", verified:"yes", rating:"4.8", reviews:"143",
    _badges:["schema_live","gtm_connected","faq_injected","streak_3"],
    _streak:3, _delta:+5, _level:4,
  },
];

// Rich mock audit results per demo client
// Demo audit results — null means AuditTab uses its built-in demo data (AUDIT_DATA)
const DEMO_AUDITS = {
  d1: null, d2: null, d3: null, d4: null, d5: null, d6: null,
};

const DEMO_CRAWL = {
  d1: { success:true, jsonLdBlockCount:4, domain:"sunshinedental.com",
        localBusiness:{status:"Pass",detail:"Dentist schema found - all key fields present"},
        faqSchema:{status:"Pass",detail:"FAQPage schema found - 8 Q&As structured"},
        serviceSchema:{status:"Pass",detail:"Service/Offer schema found - 6 service blocks"},
        reviewSchema:{status:"Pass",detail:"AggregateRating found - 4.9 (312 reviews)"},
        contentFreshness:{status:"Pass",detail:"Content updated recently (2026-03-20)"},
        sitemap:{status:"Pass",detail:"Sitemap link found in page head"},
        qaContent:{status:"Pass",detail:"FAQ / Q&A content detected on page"},
        geoCoordinates:{status:"Pass",detail:"GeoCoordinates found - enables AI near me responses"} },
  d2: { success:true, jsonLdBlockCount:2, domain:"elitelawfirm.com",
        localBusiness:{status:"Pass",detail:"LegalService schema found - missing hours"},
        faqSchema:{status:"Fail",detail:"No FAQPage schema found"},
        serviceSchema:{status:"Pass",detail:"Service schema found - 3 practice areas"},
        reviewSchema:{status:"Pass",detail:"AggregateRating found - 4.7 (89 reviews)"},
        contentFreshness:{status:"Warning",detail:"Last updated 2026-02-14 - aim for monthly"},
        sitemap:{status:"Pass",detail:"Sitemap link found in page head"},
        qaContent:{status:"Warning",detail:"Possible FAQ content - add structured markup"},
        geoCoordinates:{status:"Fail",detail:"No GeoCoordinates - add lat/lng"} },
  d3: { success:true, jsonLdBlockCount:1, domain:"coastalmedspa.com",
        localBusiness:{status:"Warning",detail:"LocalBusiness schema found but missing: telephone, hours"},
        faqSchema:{status:"Fail",detail:"No FAQPage schema found"},
        serviceSchema:{status:"Fail",detail:"No Service schema"},
        reviewSchema:{status:"Warning",detail:"Review markup partial - no AggregateRating"},
        contentFreshness:{status:"Warning",detail:"Last updated 2026-01-30 - over 45 days ago"},
        sitemap:{status:"Warning",detail:"Sitemap referenced - submit to Search Console"},
        qaContent:{status:"Fail",detail:"No FAQ or Q&A content detected"},
        geoCoordinates:{status:"Fail",detail:"No GeoCoordinates"} },
  d4: { success:true, jsonLdBlockCount:0, domain:"bayareahvac.com",
        localBusiness:{status:"Fail",detail:"No LocalBusiness or Organization schema detected"},
        faqSchema:{status:"Fail",detail:"No FAQPage schema found"},
        serviceSchema:{status:"Fail",detail:"No Service schema"},
        reviewSchema:{status:"Fail",detail:"No AggregateRating schema"},
        contentFreshness:{status:"Fail",detail:"Content not updated since 2025-11-20 - over 90 days old"},
        sitemap:{status:"Fail",detail:"Sitemap not detected"},
        qaContent:{status:"Fail",detail:"No FAQ or Q&A content"},
        geoCoordinates:{status:"Fail",detail:"No GeoCoordinates"} },
  d5: { success:true, jsonLdBlockCount:0, domain:"northshorefinancial.com",
        localBusiness:{status:"Fail",detail:"No schema detected"},
        faqSchema:{status:"Fail",detail:"No FAQPage schema"},
        serviceSchema:{status:"Fail",detail:"No Service schema"},
        reviewSchema:{status:"Fail",detail:"No AggregateRating schema"},
        contentFreshness:{status:"Fail",detail:"No date signals found"},
        sitemap:{status:"Fail",detail:"Sitemap not detected"},
        qaContent:{status:"Fail",detail:"No FAQ or Q&A content"},
        geoCoordinates:{status:"Fail",detail:"No GeoCoordinates"} },
  d6: { success:true, jsonLdBlockCount:3, domain:"premierroofing.com",
        localBusiness:{status:"Pass",detail:"Roofing contractor schema found - all fields present"},
        faqSchema:{status:"Pass",detail:"FAQPage schema found - 5 Q&As"},
        serviceSchema:{status:"Pass",detail:"Service schema found - 4 service blocks"},
        reviewSchema:{status:"Warning",detail:"Reviews mentioned but no AggregateRating schema"},
        contentFreshness:{status:"Pass",detail:"Content updated recently (2026-03-22)"},
        sitemap:{status:"Pass",detail:"Sitemap found"},
        qaContent:{status:"Pass",detail:"FAQ content detected"},
        geoCoordinates:{status:"Warning",detail:"Partial geo data - missing longitude"} },
};

// Demo ipc — returns mock data, never hits real APIs or electron
async function demoIpc(channel, ...args) {
  await new Promise(r => setTimeout(r, 200 + Math.random()*300)); // realistic delay
  const mock = {
    "get-clients":        () => DEMO_CLIENTS,
    "save-client":        (c) => c,
    "delete-client":      () => true,
    "get-settings":       () => ({ anthropicApiKey:"", perplexityApiKey:"", userGoogleEmail:"demo@example.com" }),
    "save-settings":      (s) => s,
    "get-checklist":      (cid) => ({}),
    "save-checklist":     () => true,
    "get-report-history": (cid) => [],
    "save-report":        ({report}) => report,
    "run-audit":          () => ({ score: 72 }),
    "gmb-sync":           () => ({ name:"Demo Business", phone:"(813) 555-0000", address:"Tampa Bay, FL", verified:true }),
    "deploy-gtm":         () => ({ success:true }),
    "inject-schema":      () => ({ success:true }),
    "generate-report":    () => ({ path:"demo_report.pdf" }),
    "fetch-url":          () => ({ success:false, error:"Demo mode - live fetch disabled" }),
    "trigger-gtm-oauth":  () => ({ success:false, error:"Demo mode - OAuth disabled" }),
    "verify-gtm-access":  () => ({ success:false, error:"Demo mode - connect a real GTM account to verify" }),
    "list-gtm-tags":      () => ({ success:true, tags:[
      { tagId:"1001", name:"SharkOS - LocalBusiness - Sunshine Dental", type:"html", path:"accounts/1/containers/1/workspaces/1/tags/1001", paused:false },
      { tagId:"1002", name:"SharkOS - FAQPage - Patient Questions", type:"html", path:"accounts/1/containers/1/workspaces/1/tags/1002", paused:false },
      { tagId:"2001", name:"Google Analytics - GA4", type:"gaawe", path:"accounts/1/containers/1/workspaces/1/tags/2001", paused:false },
    ]}),
    "inject-podcast-schema": () => ({ success:true, injected:2, total:2, results:[] }),
    "bulk-inject-schema":    () => ({ success:true, results: DEMO_CLIENTS.map(c=>({ clientId:c.id, name:c.name, success:true, injected:2, total:2 })) }),
  };
  const fn = mock[channel];
  return fn ? fn(...(args)) : null;
}

// ---------------------------------------------
// GOOGLE OAUTH + MY BUSINESS API
// ---------------------------------------------
const GMB_CLIENT_ID = (LS.get("settings")?.gmbClientId) || "";
const GMB_SCOPES = [
  "https://www.googleapis.com/auth/business.manage",
  "openid","email","profile"
].join(" ");

// PKCE helpers
async function pkcePair() {
  const arr = crypto.getRandomValues(new Uint8Array(32));
  const verifier = btoa(String.fromCharCode(...arr)).replace(/\+/g,"-").replace(/\//g,"_").replace(/=/g,"");
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  const challenge = btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g,"-").replace(/\//g,"_").replace(/=/g,"");
  return { verifier, challenge };
}

// Launch Google OAuth popup and return the auth code
async function googleOAuthPopup(clientId) {
  const { verifier, challenge } = await pkcePair();
  const state = crypto.randomUUID();
  LS.set("oauth_pkce", { verifier, state });

  // Must match EXACTLY what is registered in Google Cloud Console → Authorized redirect URIs
  // For local dev: http://localhost:3000
  // For production: https://yourdomain.com
  const redirectUri = window.location.origin;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: GMB_SCOPES,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    access_type: "offline",
    prompt: "consent",
  });
  const authUrl = "https://accounts.google.com/o/oauth2/v2/auth?" + params;

  return new Promise((resolve, reject) => {
    const popup = window.open(authUrl, "gmb_oauth", "width=520,height=660,left=200,top=100");
    if (!popup) { reject(new Error("Popup blocked - allow popups for this site")); return; }
    const iv = setInterval(() => {
      try {
        if (!popup || popup.closed) { clearInterval(iv); reject(new Error("Popup closed")); return; }
        const url = popup.location.href;
        if (!url.includes(window.location.origin)) return;
        popup.close(); clearInterval(iv);
        const p = new URL(url).searchParams;
        const code = p.get("code"), retState = p.get("state"), err = p.get("error");
        if (err) { reject(new Error("OAuth error: " + err)); return; }
        const saved = LS.get("oauth_pkce");
        if (retState !== saved?.state) { reject(new Error("State mismatch - CSRF check failed")); return; }
        resolve({ code, verifier: saved.verifier, redirectUri });
      } catch { /* cross-origin - still loading */ }
    }, 300);
  });
}

// Exchange code for token using PKCE - no client_secret needed for public clients
// Google's token endpoint supports CORS so this works directly from the browser
async function exchangeGmbToken(code, verifier, redirectUri, clientId, clientSecret) {
  // Electron: delegate to main process
  if (window.sharkOS?.ipcRenderer) {
    return window.sharkOS.ipcRenderer.invoke("gmb-token-exchange", { code, verifier, redirectUri, clientId, clientSecret });
  }
  // Browser: hit Google token endpoint directly using PKCE (no client_secret required for web apps)
  const body = new URLSearchParams({
    code,
    client_id: clientId,
    redirect_uri: redirectUri,
    grant_type: "authorization_code",
    code_verifier: verifier,
    // Only include client_secret if provided AND we're in a secure context (not exposed publicly)
    ...(clientSecret ? { client_secret: clientSecret } : {}),
  });
  const r = await fetch("https://oauth2.googleapis.com/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!r.ok) {
    const err = await r.json().catch(()=>({}));
    throw new Error(err.error_description || err.error || `Token exchange failed (${r.status})`);
  }
  return r.json();
}

// Refresh an expired access token
async function refreshGmbToken(refreshToken, clientId, clientSecret) {
  if (window.sharkOS?.ipcRenderer) {
    return window.sharkOS.ipcRenderer.invoke("gmb-token-refresh", { refreshToken, clientId, clientSecret });
  }
  const proxyUrl = LS.get("settings")?.gmbTokenProxy;
  if (proxyUrl) {
    const r = await fetch(proxyUrl, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({grant_type:"refresh_token", refresh_token:refreshToken}) });
    if (!r.ok) throw new Error("Proxy refresh failed: "+r.status);
    return r.json();
  }
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: clientId,
    ...(clientSecret ? { client_secret: clientSecret } : {}),
  });
  const r = await fetch("https://oauth2.googleapis.com/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.error_description || "Refresh failed"); }
  return r.json();
}

// Fetch GBP listing data using the access token
async function fetchGmbListing(accessToken) {
  // Step 1: get accounts
  const acctR = await fetch("https://mybusinessaccountmanagement.googleapis.com/v1/accounts", {
    headers: { Authorization: "Bearer " + accessToken }
  });
  if (!acctR.ok) throw new Error("Failed to fetch GMB accounts: " + acctR.status);
  const acctData = await acctR.json();
  const account = acctData.accounts?.[0];
  if (!account) throw new Error("No GMB accounts found for this Google login");

  // Step 2: get locations under first account
  const locR = await fetch(
    `https://mybusinessbusinessinformation.googleapis.com/v1/${account.name}/locations?readMask=name,title,storefrontAddress,phoneNumbers,regularHours,categories,websiteUri,metadata`,
    { headers: { Authorization: "Bearer " + accessToken } }
  );
  if (!locR.ok) throw new Error("Failed to fetch GMB locations: " + locR.status);
  const locData = await locR.json();
  return { account, locations: locData.locations || [] };
}

// Full connect flow: OAuth → token → listing data
async function connectGmb(clientId, clientSecret) {
  const { code, verifier, redirectUri } = await googleOAuthPopup(clientId);
  const tokenData = await exchangeGmbToken(code, verifier, redirectUri, clientId, clientSecret);
  const accessToken = tokenData.access_token;
  LS.set("gmb_token", { accessToken, refreshToken: tokenData.refresh_token, expiry: Date.now() + (tokenData.expires_in||3600)*1000 });
  return fetchGmbListing(accessToken);
}

// ---------------------------------------------
// Browser GEO Audit Engine
// ---------------------------------------------

// Use Claude API as a reliable server-side fetch proxy - bypasses all CORS issues
// since the Anthropic API is reachable and Claude can fetch any public URL.
// Central Anthropic API caller - direct fetch with key from any available source
async function getAnthropicKey() {
  try {
    if (window.sharkOS?.invoke) {
      const s = await window.sharkOS.invoke("get-settings");
      if (s?.anthropicApiKey) return s.anthropicApiKey;
    }
  } catch {}
  return (LS.get("settings") || {}).anthropicApiKey || "";
}

async function claudeAPI(body) {
  const apiKey = await getAnthropicKey();
  if (!apiKey) throw new Error("Anthropic API key not set. Go to Settings and add your sk-ant-... key.");

  // Filter out system messages and use as top-level system param
  const systemMsg = (body.messages || []).find(m => m.role === "system");
  const messages = (body.messages || [])
    .filter(m => m.role !== "system")
    .map(m => ({
      role: m.role,
      content: typeof m.content === "string" ? m.content :
        (m.content || []).filter(b => b.type === "text").map(b => b.text).join("\n"),
    }));

  const reqBody = {
    model: "claude-sonnet-4-20250514",
    max_tokens: body.max_tokens || 1000,
    messages,
  };

  // Add system as top-level param if present
  if (systemMsg) reqBody.system = typeof systemMsg.content === "string" ? systemMsg.content : systemMsg.content;
  if (body.system) reqBody.system = body.system;

  const r = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify(reqBody),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e?.error?.message || "Anthropic API error " + r.status);
  }
  const data = await r.json();
  return { content: data.content || [{ type: "text", text: data.content?.[0]?.text || "" }] };
}

async function fetchViaClaudeAPI(url) {
  try {
    const response = await claudeAPI({
      model: "gpt-4o",
      max_tokens: 4096,
      messages: [{
        role: "user",
        content: `Fetch the raw text content of this URL and return ONLY the raw text/content, no commentary, no markdown formatting, no explanation. If the URL returns a 404 or error, respond with exactly: FETCH_ERROR\n\nURL: ${url}`
      }]
    });
    const data = response;
    const text = data?.content?.[0]?.text || null;
    if (!text || text.trim() === "FETCH_ERROR") return null;
    return text;
  } catch { return null; }
}

async function fetchProxy(url) {
  // 1. Electron main-process fetch via IPC - no CORS, always works when in Electron
  if (window.sharkOS?.invoke) {
    try {
      const r = await window.sharkOS.invoke('fetch-url', { url, timeout: 10000 });
      if (r?.success && r.body && r.body.length > 10) return r.body;
    } catch { /* fall through */ }
  }

  // 2. Try allorigins.win (fast, often works)
  try {
    const r = await fetch("https://api.allorigins.win/get?url=" + encodeURIComponent(url),
      { signal: AbortSignal.timeout(7000) });
    if (r.ok) {
      const d = await r.json();
      if (d.contents && d.contents.length > 10) return d.contents;
    }
  } catch { /* fall through */ }

  // 3. Try corsproxy.io
  try {
    const r = await fetch("https://corsproxy.io/?" + encodeURIComponent(url),
      { signal: AbortSignal.timeout(7000) });
    if (r.ok) {
      const text = await r.text();
      if (text && text.length > 10) return text;
    }
  } catch { /* fall through */ }

  // 4. Try thingproxy.freeboard.io
  try {
    const r = await fetch("https://thingproxy.freeboard.io/fetch/" + url,
      { signal: AbortSignal.timeout(7000) });
    if (r.ok) {
      const text = await r.text();
      if (text && text.length > 10) return text;
    }
  } catch { /* fall through */ }

  // 5. Final fallback: use Claude API as a server-side fetcher
  return await fetchViaClaudeAPI(url);
}

async function geoCheckRobots(domain) {
  const CRAWLERS = [
    {name:"GPTBot",tier:1,owner:"OpenAI"},{name:"OAI-SearchBot",tier:1,owner:"OpenAI"},
    {name:"ClaudeBot",tier:1,owner:"Anthropic"},{name:"PerplexityBot",tier:1,owner:"Perplexity"},
    {name:"Googlebot",tier:1,owner:"Google"},{name:"Google-Extended",tier:2,owner:"Google"},
    {name:"Applebot-Extended",tier:2,owner:"Apple"},{name:"Amazonbot",tier:2,owner:"Amazon"},
    {name:"FacebookBot",tier:2,owner:"Meta"},{name:"bingbot",tier:2,owner:"Microsoft"},
    {name:"CCBot",tier:3,owner:"Common Crawl"},{name:"Bytespider",tier:3,owner:"ByteDance"},
  ];
  if (!domain) return { findings:[], crawlerMap:[], score:0, maxScore:40 };
  const clean = domain.replace(/^https?:\/\//,"").replace(/\/$/,"");
  const text = await fetchProxy("https://" + clean + "/robots.txt");
  const crawlerMap = CRAWLERS.map(c => {
    if (!text) return {...c, allowed:null, status:"Unknown"};
    const lower = text.toLowerCase();
    const idx = lower.indexOf("user-agent: " + c.name.toLowerCase());
    if (idx === -1) return {...c, allowed:true, status:"Allowed"};
    const section = text.substring(idx, idx + 400);
    const blocked = /disallow:\s*\//i.test(section);
    return {...c, allowed:!blocked, status:blocked?"Blocked":"Allowed"};
  });
  const blocked = crawlerMap.filter(c => c.status === "Blocked");
  const t1blocked = blocked.filter(c => c.tier === 1);
  const findings = [];
  if (!text) {
    findings.push({platform:"Crawler Access",category:"robots.txt",status:"Warning",detail:"robots.txt not found",impact:"-8 pts"});
    return { findings, crawlerMap, score:15, maxScore:40 };
  }
  if (t1blocked.length > 0) {
    findings.push({platform:"Crawler Access",category:"robots.txt",status:"Fail",
      detail:"Critical AI crawlers blocked: " + t1blocked.map(c=>c.name).join(", "),impact:"-" + (t1blocked.length*8) + " pts"});
  } else if (blocked.length > 0) {
    findings.push({platform:"Crawler Access",category:"robots.txt",status:"Warning",
      detail:"Some crawlers blocked: " + blocked.map(c=>c.name).join(", "),impact:"-4 pts"});
  } else {
    findings.push({platform:"Crawler Access",category:"robots.txt",status:"Pass",
      detail:"All AI crawlers have access",impact:"+10 pts"});
  }
  const score = Math.max(0, 40 - t1blocked.length*8 - (blocked.length - t1blocked.length)*3);
  return { findings, crawlerMap, score, maxScore:40 };
}

function buildRichLlmsTxt(client, gbmData, napData, crawlData, geoData) {
  const domain = (client.domain||"").replace(/^https?:\/\//,"").replace(/\/$/,"");
  const name   = gbmData?.name || client.name || "This Business";
  const city   = gbmData?.address || client.city || "";
  const phone  = gbmData?.phone || "";
  const website= gbmData?.website || `https://${domain}`;
  const category = gbmData?.category || client.businessType || "local business";
  const rating = gbmData?.rating;
  const reviews= gbmData?.reviews;
  const claimed= gbmData?.gbpClaimed || gbmData?.claimed;
  const verified=gbmData?.verified || gbmData?.status==="verified";
  const gtm    = gbmData?.gtm || client.gtm || "";
  const now    = new Date();
  const updated= now.toISOString().split("T")[0];

  // NAP authority signals
  const napSources = {
    nap_google:"Google Business Profile", nap_bing:"Bing Places",
    nap_apple:"Apple Maps", nap_yelp:"Yelp", nap_facebook:"Facebook Business",
    nap_bbb:"Better Business Bureau", nap_infogroup:"Data Axle (Infogroup)",
    nap_localeze:"Neustar Localeze", nap_acxiom:"Acxiom", nap_foursquare:"Foursquare"
  };
  const accurateSources = Object.entries(napSources)
    .filter(([id])=> (napData||{})[id]==="accurate")
    .map(([,label])=>label);
  const missingSources = Object.entries(napSources)
    .filter(([id])=> (napData||{})[id]==="missing")
    .map(([,label])=>label);

  // Schema signals from crawl
  const hasLocalSchema = crawlData?.localBusiness?.status==="Pass";
  const hasFaqSchema   = crawlData?.faqSchema?.status==="Pass";
  const hasServiceSchema=crawlData?.serviceSchema?.status==="Pass";
  const hasReviewSchema= crawlData?.reviewSchema?.status==="Pass";
  const hasSitemap     = crawlData?.sitemap?.status==="Pass";

  // Wiki authority
  const wikiUrl = geoData?.wikiUrl || "";

  // Build authority confidence score (0-100)
  let authScore = 0;
  if (verified)         authScore += 20;
  if (claimed)          authScore += 10;
  if (rating >= 4.0)    authScore += 10;
  if (reviews >= 10)    authScore += 10;
  if (accurateSources.length >= 3) authScore += 15;
  if (hasLocalSchema)   authScore += 10;
  if (hasFaqSchema)     authScore += 5;
  if (wikiUrl)          authScore += 10;
  if (hasSitemap)       authScore += 5;
  if (phone)            authScore += 5;
  authScore = Math.min(100, authScore);

  const trustLevel = authScore >= 80 ? "HIGH" : authScore >= 50 ? "MEDIUM" : "LOW";

  let txt = `# ${name}
> This file is intended for AI language models and crawlers (GPTBot, ClaudeBot, PerplexityBot, Googlebot, etc.)
> It provides authoritative, machine-readable information about this business entity.
> Last updated: ${updated} | Authority confidence: ${authScore}/100 (${trustLevel})

## Identity

- **Legal / Operating Name:** ${name}
- **Business Type:** ${category}
- **Primary Location:** ${city}
- **Website:** ${website}
${phone ? `- **Phone:** ${phone}` : ""}
- **Google Business Profile:** ${verified ? "✓ Verified & Claimed" : claimed ? "Claimed (not yet verified)" : "Not claimed"}
${rating ? `- **Google Rating:** ${rating} stars (${reviews || "?"} reviews)` : ""}
${wikiUrl ? `- **Wikipedia Entry:** ${wikiUrl}` : ""}

## Authority Verification

This business has been verified across the following sources:
${accurateSources.length > 0
  ? accurateSources.map(s=>`- ✓ ${s}`).join("\n")
  : "- No third-party directories verified yet - NAP audit pending"}
${missingSources.length > 0 ? `\nCurrently absent from:\n${missingSources.map(s=>`- ✗ ${s}`).join("\n")}` : ""}

## Structured Data

${hasLocalSchema ? "- ✓ LocalBusiness schema (JSON-LD) implemented on website" : "- ✗ LocalBusiness schema not yet detected on website"}
${hasFaqSchema   ? "- ✓ FAQ schema (JSON-LD) implemented" : "- ✗ FAQ schema not detected"}
${hasServiceSchema? "- ✓ Service schema implemented for offerings" : "- ✗ Service schema not detected"}
${hasReviewSchema ? "- ✓ Review/rating schema implemented" : "- ✗ Review schema not detected"}
${hasSitemap      ? "- ✓ XML sitemap submitted to Google & Bing" : "- ✗ Sitemap not detected"}

## Services

${category ? `${name} is a ${category} serving ${city || "the local area"}.` : `${name} serves customers in the local area.`}
- [Complete this section with your specific service offerings]
- [Include service area cities and neighborhoods]
- [Include specialties and differentiators]

## About

${name} is a local business${city ? ` located in ${city}` : ""}.${rating ? ` Rated ${rating} stars across ${reviews||0} Google reviews.` : ""} ${claimed ? "This listing is claimed and actively managed." : ""}

[Expand this section with: year founded, ownership, certifications, awards, community involvement]

## Contact & Location

- **Website:** ${website}
${phone ? `- **Phone:** ${phone}` : "- **Phone:** [Add phone number]"}
- **Address:** ${city || "[Add full address]"}
- **Hours:** [Add business hours]

## AI Crawler Permissions

This business authorizes the following AI systems to index and reference this content:
- GPTBot (OpenAI / ChatGPT)
- ClaudeBot (Anthropic / Claude)
- PerplexityBot (Perplexity AI)
- Google-Extended (Google AI Overview)
- Bingbot (Microsoft Copilot)
- Applebot-Extended (Apple Intelligence)

See /robots.txt for full crawler configuration.

---
*Generated by SharkOS GEO Intelligence Platform - ${updated}*
*Entity Authority Score: ${authScore}/100 | Trust Level: ${trustLevel}*
`;
  return txt;
}

async function geoCheckLlms(domain, clientName, client, gbmData, napData, crawlData) {
  if (!domain) return { findings:[], llmsData:null, generatedFile:null, score:0, maxScore:20 };
  const clean = domain.replace(/^https?:\/\//,"").replace(/\/$/,"");
  const base  = "https://" + clean;

  // Check all common locations - /llms.txt first, then /llms (Wix hidden page),
  // then /.well-known/llms.txt
  const candidates = [
    base + "/llms.txt",
    base + "/llms",
    base + "/.well-known/llms.txt",
  ];

  let text = null;
  let foundUrl = null;
  for (const url of candidates) {
    try {
      const result = await fetchProxy(url);
      const isHtml = result && (
        result.trimStart().startsWith("<!") ||
        result.trimStart().startsWith("<html") ||
        result.includes("<body") ||
        result.includes("wix-")
      );
      if (result && result.length >= 50 && !isHtml) {
        text = result;
        foundUrl = url;
        break;
      }
    } catch { /* try next candidate */ }
  }

  const findings = [];
  if (!text) {
    findings.push({ platform:"llms.txt", category:"AI Readability", status:"Fail",
      detail:`No llms.txt found at ${candidates.join(", ")} - AI models cannot self-describe this business`,
      impact:"-10 pts" });
    return { findings, llmsData:null, generatedFile:null, score:0, maxScore:20 };
  }

  const hasAbout    = /##\s*about/i.test(text);
  const hasServices = /##\s*service/i.test(text);
  const hasContact  = /##\s*contact/i.test(text);
  const hasAuthority= /authority|verified|rating|review|award|credential/i.test(text);
  let score = 0;
  if (hasAbout)     score += 6;
  if (hasServices)  score += 6;
  if (hasContact)   score += 4;
  if (hasAuthority) score += 4;
  score = Math.min(20, score);

  const foundAt = foundUrl.replace(base, "") || "/llms.txt";
  if (score >= 16) {
    findings.push({ platform:"llms.txt", category:"AI Readability", status:"Pass",
      detail:`llms.txt found at ${foundAt} - About, Services, Contact and authority signals present`,
      impact:"+20 pts" });
  } else if (score >= 8) {
    findings.push({ platform:"llms.txt", category:"AI Readability", status:"Warning",
      detail:`llms.txt found at ${foundAt} but missing: ${!hasAbout?"About ":""}${!hasServices?"Services ":""}${!hasContact?"Contact ":""}${!hasAuthority?"authority signals":""}`.trim(),
      impact:`+${score} pts` });
  } else {
    findings.push({ platform:"llms.txt", category:"AI Readability", status:"Fail",
      detail:`llms.txt found at ${foundAt} but too thin - missing critical sections`,
      impact:`+${score} pts` });
  }
  return { findings, llmsData:text, llmsFoundAt:foundUrl, generatedFile:null, score, maxScore:20 };
}

async function geoCheckWikipedia(clientName) {
  const findings = [];
  if (!clientName) return { findings, wikiUrl:null, score:0, maxScore:20 };
  try {
    const query = encodeURIComponent(clientName);
    const r = await fetch(`https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${query}&format=json&origin=*`, { signal: AbortSignal.timeout(6000) });
    const d = await r.json();
    const hits = d?.query?.search || [];
    const match = hits.find(h => h.title.toLowerCase().includes(clientName.toLowerCase().split(" ")[0]));
    if (match) {
      findings.push({platform:"Brand Authority",category:"Wikipedia",status:"Pass",
        detail:"Wikipedia entry found: " + match.title,impact:"+20 pts"});
      return { findings, wikiUrl:"https://en.wikipedia.org/wiki/" + encodeURIComponent(match.title), score:20, maxScore:20 };
    }
  } catch {}
  findings.push({platform:"Brand Authority",category:"Wikipedia",status:"Fail",
    detail:"No Wikipedia entry found - low entity authority for AI models",impact:"-10 pts"});
  return { findings, wikiUrl:null, score:0, maxScore:20 };
}

async function geoCheckSitemap(domain) {
  if (!domain) return { found:false, status:"Fail" };
  const clean = domain.replace(/^https?:\/\//,"").replace(/\/$/,"");
  const text = await fetchProxy("https://" + clean + "/sitemap.xml");
  const isXml = text && (text.includes("<urlset") || text.includes("<sitemapindex"));
  return { found: !!isXml, status: isXml ? "Pass" : "Fail", detail: isXml ? "XML sitemap found" : "No sitemap.xml detected" };
}

async function geoCheckAboutFaqAreas(domain) {
  // Check for /about, /faq, /service-areas pages existing
  if (!domain) return { about:false, faq:false, areas:false };
  const clean = domain.replace(/^https?:\/\//,"").replace(/\/$/,"");
  const pages = ["about","faq","service-areas","services","locations"];
  const results = await Promise.allSettled(pages.map(p =>
    fetchProxy("https://" + clean + "/" + p).then(t => ({page:p, found: t && t.length > 200 && !t.includes("404")}))
  ));
  const found = {};
  results.forEach((r,i) => { if(r.status==="fulfilled") found[pages[i]] = r.value.found; });
  return {
    about:   !!(found.about),
    faq:     !!(found.faq),
    areas:   !!(found["service-areas"] || found.locations),
  };
}

async function runGeoAudit(client, gbmData, napData) {
  const domain = client.domain || "";
  const name = client.name || "";
  const [robots, llmsRaw, wiki, sitemapRes, pagesRes] = await Promise.allSettled([
    geoCheckRobots(domain),
    geoCheckLlms(domain, name),
    geoCheckWikipedia(name),
    geoCheckSitemap(domain),
    geoCheckAboutFaqAreas(domain),
  ]);
  const r  = robots.status==="fulfilled"    ? robots.value    : {findings:[],crawlerMap:[],score:0,maxScore:40};
  const l  = llmsRaw.status==="fulfilled"   ? llmsRaw.value   : {findings:[],llmsData:null,generatedFile:null,score:0,maxScore:20};
  const w  = wiki.status==="fulfilled"      ? wiki.value      : {findings:[],wikiUrl:null,score:0,maxScore:20};
  const sm = sitemapRes.status==="fulfilled"? sitemapRes.value: {found:false,status:"Fail"};
  const pg = pagesRes.status==="fulfilled"  ? pagesRes.value  : {about:false,faq:false,areas:false};

  // Build the rich llms.txt now that we have all scan data
  // (crawlData isn't available yet here, pass what we have)
  const generatedLlms = buildRichLlmsTxt(client, gbmData, napData, null, w);

  return {
    crawlerMap:     r.crawlerMap,
    llmsGenerated:  l.llmsData ? null : generatedLlms,
    llmsData:       l.llmsData,
    wikiUrl:        w.wikiUrl,
    geoFindings:    [...r.findings, ...l.findings, ...w.findings],
    geoScore:       Math.round(((r.score/r.maxScore)+(l.score/l.maxScore)+(w.score/w.maxScore))/3*100),
    sitemapFound:   sm.found,
    pagesFound:     pg,
  };
}

// CHECKLIST DATA - 23 tasks
// ---------------------------------------------
// CITATION SOURCES (~40+ directories)
// ---------------------------------------------
// NAP status helpers - used by audit signal logic
function getNAPStatus(clientId, sourceId){
  const statuses = LS.get("nap_"+clientId) || {};
  return statuses[sourceId] || "unchecked";
}

const CHECKLIST_CATS = [
  { name:"Entity Foundation", items:[
    {id:"entity_gbp",       task:"GBP listing complete - all fields, services, photos, and attributes",time:"45 min",price:150},
    {id:"entity_categories",task:"All relevant GBP categories added (primary + secondary)",time:"15 min",price:75},
    {id:"entity_nap_sync",  task:"Citation Sync activated - NAP pushed live to 40+ directories",time:"1 hr",price:200},
    {id:"entity_tier1",     task:"Tier 1 authority sources verified accurate (Google, Bing, Apple, Yelp, Facebook, BBB)",time:"45 min",price:150},
    {id:"entity_duplicates",task:"Duplicate listings identified and suppressed across all sources",time:"30 min",price:75},
  ]},
  { name:"Structured Data", items:[
    {id:"schema_local",  task:"LocalBusiness / Organization schema in static HTML - readable by all AI crawlers",time:"1 hr",price:200},
    {id:"schema_faq",    task:"FAQPage schema baked into static HTML page (not GTM - AI bots don't run JS)",time:"45 min",price:150},
    {id:"schema_service",task:"Service schema enumerating all offerings so AI can list what the business does",time:"1 hr",price:200},
    {id:"schema_geo",    task:"GeoCoordinates and areaServed defined - powers geo-targeted AI queries",time:"30 min",price:100},
    {id:"schema_gtm",    task:"GTM code injection active for Google AI Overviews and rich results",time:"30 min",price:150},
  ]},
  { name:"Content for AI", items:[
    {id:"content_faq",       task:"FAQ page (10–15 Q&A) in plain crawlable HTML - answers what buyers actually ask AI",time:"2 hr",price:300},
    {id:"content_areas",     task:"Service area pages for each city or neighborhood served",time:"2 hr",price:250},
    {id:"content_monthly",   task:"Monthly blog or news post published - keeps entity fresh for AI training and retrieval",time:"1 hr",price:200},
    {id:"content_spotlights",task:"Member or client spotlight pages with Person + Organization schema",time:"1.5 hr",price:200},
    {id:"content_about",     task:"About page updated with geo-specific content and industry keywords",time:"1 hr",price:150},
  ]},
  { name:"Review Authority", items:[
    {id:"review_system",   task:"Review request system activated and live",time:"1 hr",price:200},
    {id:"review_volume",   task:"Google review count at or above local authority threshold (50+ target)",time:"ongoing",price:150},
    {id:"review_respond",  task:"All reviews responded to across every platform",time:"30 min",price:100},
    {id:"review_platforms",task:"Reviews present and monitored on Google, Facebook, and BBB",time:"45 min",price:150},
  ]},
  { name:"Crawl Access", items:[
    {id:"crawl_llmstxt", task:"llms.txt created and maps all key content - tells AI crawlers exactly what to read",time:"30 min",price:150},
    {id:"crawl_sitemap", task:"Sitemap up to date and submitted to Google and Bing Search Console",time:"15 min",price:75},
    {id:"crawl_robots",  task:"robots.txt verified - GPTBot, ClaudeBot, and PerplexityBot are not blocked",time:"15 min",price:75},
    {id:"crawl_vitals",  task:"Core Web Vitals passing - page speed signals that affect crawler priority",time:"2 hr",price:300},
  ]},
];
const TOTAL_TASKS = CHECKLIST_CATS.reduce((a,c)=>a+c.items.length,0);
const TOTAL_VALUE = CHECKLIST_CATS.reduce((a,c)=>a+c.items.reduce((b,i)=>b+i.price,0),0);

// ---------------------------------------------
// HELPERS
// ---------------------------------------------
const gradeFromScore = s => s>=90?"A":s>=80?"B":s>=70?"C":s>=60?"D":"F";
const GRADE_COL = {A:T.mint,B:"#4FD1A0",C:T.gold,D:T.yellow,F:T.red};
const gcol = g => GRADE_COL[g]||T.red;

// ---------------------------------------------
// GLOBAL STYLES
// ---------------------------------------------
const GS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');

html,body,#root{background:#060C0F!important;color:#F8FAFC;margin:0;padding:0;min-height:100vh;}

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}

/* ── Root background: deep space + ambient orbs ── */
body{
  font-family:'Inter',system-ui,sans-serif;
  letter-spacing:-0.022em;
  background:${T.bg};
  background-image:
    radial-gradient(ellipse 80% 50% at 20% 0%, rgba(21,180,214,0.07) 0%, transparent 60%),
    radial-gradient(ellipse 60% 40% at 80% 10%, rgba(31,112,133,0.06) 0%, transparent 55%),
    radial-gradient(ellipse 40% 30% at 50% 100%, rgba(79,209,160,0.03) 0%, transparent 50%);
  color:${T.text};
  -webkit-font-smoothing:antialiased;
  -moz-osx-font-smoothing:grayscale;
}

/* Dot grid overlay - subtle premium texture */
body::before{
  content:'';
  position:fixed;
  inset:0;
  pointer-events:none;
  z-index:0;
  background-image: radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px);
  background-size: 28px 28px;
}

/* Noise grain overlay */
body::after{
  content:'';
  position:fixed;
  inset:0;
  pointer-events:none;
  z-index:0;
  opacity:0.018;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  background-size: 256px 256px;
}

input,textarea,button,select{
  font-family:'Inter',inherit;
  letter-spacing:-0.01em;
}

/* ── Scrollbar ── */
::-webkit-scrollbar{width:5px;height:5px;}
::-webkit-scrollbar-track{background:transparent;}
::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:99px;}
::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,0.16);}

/* ── Core animations ── */
@keyframes slideIn{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
@keyframes slideInLeft{from{opacity:0;transform:translateX(-10px);}to{opacity:1;transform:translateX(0);}}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes checkPop{0%,100%{transform:scale(1)}50%{transform:scale(1.25)}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.4;}}
@keyframes shimmer{0%{background-position:-1000px 0;}100%{background-position:1000px 0;}}
@keyframes glow{0%,100%{box-shadow:0 0 8px rgba(21,180,214,0.3);}50%{box-shadow:0 0 20px rgba(21,180,214,0.6);}}
@keyframes borderFlow{
  0%{background-position:0% 50%;}
  50%{background-position:100% 50%;}
  100%{background-position:0% 50%;}
}
@keyframes countUp{from{opacity:0;transform:translateY(4px);}to{opacity:1;transform:translateY(0);}}
@keyframes ripple{0%{transform:scale(0.8);opacity:1;}100%{transform:scale(2.4);opacity:0;}}

.slide-in{animation:slideIn 0.28s cubic-bezier(0.16,1,0.3,1) forwards;}
.slide-in-left{animation:slideInLeft 0.28s cubic-bezier(0.16,1,0.3,1) forwards;}
.fade-in{animation:fadeIn 0.3s ease forwards;}
.check-pop{animation:checkPop 0.2s ease-out;}
.spin{animation:spin 0.9s linear infinite;display:inline-block;}
.pulse-anim{animation:pulse 2s ease-in-out infinite;}

/* ── Skeleton loader ── */
.skeleton-box{
  background:linear-gradient(90deg,rgba(255,255,255,0.03) 25%,rgba(255,255,255,0.07) 50%,rgba(255,255,255,0.03) 75%);
  background-size:1000px 100%;
  animation:shimmer 1.8s infinite linear;
  border-radius:8px;
}

/* ── Glass panel (primary card) ── */
.glass-panel{
  background:${T.surface};
  backdrop-filter:blur(20px) saturate(1.4);
  -webkit-backdrop-filter:blur(20px) saturate(1.4);
  border:1px solid ${T.surfaceBorder};
  border-radius:16px;
  box-shadow:0 1px 0 0 rgba(255,255,255,0.04) inset, 0 8px 32px -4px rgba(0,0,0,0.4);
  position:relative;
  z-index:1;
}

/* ── Elevated card variant ── */
.card-elevated{
  background:linear-gradient(145deg, rgba(14,20,40,0.9) 0%, rgba(10,14,28,0.95) 100%);
  border:1px solid rgba(255,255,255,0.07);
  border-radius:16px;
  box-shadow:0 2px 0 rgba(255,255,255,0.04) inset, 0 16px 48px -8px rgba(0,0,0,0.5);
}

/* ── Gradient border card ── */
.card-glow{
  position:relative;
  border-radius:16px;
  background:linear-gradient(145deg, rgba(14,20,40,0.9), rgba(8,10,20,0.95));
}
.card-glow::before{
  content:'';
  position:absolute;
  inset:-1px;
  border-radius:17px;
  background:linear-gradient(135deg, rgba(21,180,214,0.3), rgba(31,112,133,0.2), rgba(21,180,214,0.1));
  z-index:-1;
  opacity:0;
  transition:opacity 0.3s ease;
}
.card-glow:hover::before{opacity:1;}

/* ── Gradient text ── */
.gradient-text{
  background:linear-gradient(135deg, #15B4D6 0%, #1F7085 100%);
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
  background-clip:text;
}
.gradient-text-gold{
  background:linear-gradient(135deg, #F9DB79 0%, #F7555F 100%);
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
  background-clip:text;
}

/* ── Live pulse indicator ── */
.pulse-dot{
  position:relative;
  display:inline-flex;
  align-items:center;
  justify-content:center;
}
.pulse-dot::before{
  content:'';
  position:absolute;
  width:100%;
  height:100%;
  border-radius:50%;
  background:inherit;
  animation:ripple 1.8s ease-out infinite;
}

/* ── Stat number (large metric) ── */
.stat-number{
  font-size:36px;
  font-weight:900;
  letter-spacing:-0.04em;
  line-height:1;
  animation:countUp 0.5s cubic-bezier(0.16,1,0.3,1) forwards;
}

/* ── Interactive button base ── */
.btn-primary{
  background:linear-gradient(135deg, #15B4D6, #1F7085);
  color:#060810;
  font-weight:800;
  border:none;
  box-shadow:0 4px 16px rgba(21,180,214,0.3);
  transition:all 0.2s cubic-bezier(0.16,1,0.3,1);
}
.btn-primary:hover{
  transform:translateY(-1px);
  box-shadow:0 8px 24px rgba(21,180,214,0.45);
}

/* ── Nav item active glow ── */
.nav-item-active{
  background:linear-gradient(135deg, rgba(21,180,214,0.12), rgba(31,112,133,0.08));
  box-shadow:inset 0 0 0 1px rgba(21,180,214,0.2);
}

/* ── Header ── */
.app-header{
  background:rgba(7,9,15,0.85);
  backdrop-filter:blur(24px) saturate(1.6);
  -webkit-backdrop-filter:blur(24px) saturate(1.6);
  border-bottom:1px solid rgba(255,255,255,0.06);
  position:relative;
  z-index:10;
}

/* ── Tag / badge ── */
.badge{
  display:inline-flex;
  align-items:center;
  gap:4px;
  padding:3px 8px;
  border-radius:99px;
  font-size:10px;
  font-weight:700;
  letter-spacing:0.06em;
  text-transform:uppercase;
}
.badge-blue{background:rgba(21,180,214,0.12);color:#15B4D6;border:1px solid rgba(21,180,214,0.2);}
.badge-green{background:rgba(79,209,160,0.12);color:#4FD1A0;border:1px solid rgba(79,209,160,0.2);}
.badge-red{background:rgba(248,113,113,0.12);color:#F7555F;border:1px solid rgba(248,113,113,0.2);}
.badge-gold{background:rgba(249,219,121,0.12);color:#F9DB79;border:1px solid rgba(249,219,121,0.2);}
.badge-purple{background:rgba(31,112,133,0.12);color:#1F7085;border:1px solid rgba(31,112,133,0.2);}

/* ── Table ── */
.data-table{width:100%;border-collapse:collapse;}
.data-table th{
  font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;
  color:${T.ghost};padding:10px 14px;border-bottom:1px solid ${T.surfaceBorder};text-align:left;
}
.data-table td{
  padding:12px 14px;border-bottom:1px solid rgba(255,255,255,0.04);
  font-size:13px;color:${T.textMuted};
}
.data-table tr:last-child td{border-bottom:none;}
.data-table tr:hover td{background:rgba(255,255,255,0.02);}

/* ── Input focus ring ── */
input:focus,textarea:focus,select:focus{
  outline:none;
  box-shadow:0 0 0 2px rgba(21,180,214,0.25);
  border-color:rgba(21,180,214,0.4) !important;
}

/* ── Divider ── */
.divider{height:1px;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent);margin:4px 0;}

/* ── Legacy compat ── */
.wavy-bg{background:transparent;position:relative;z-index:1;}
.dark-card{background:linear-gradient(145deg,rgba(8,12,28,0.95) 0%,rgba(6,8,18,0.98) 100%);}
`;

// ---------------------------------------------
// TOAST SYSTEM - global, no prop drilling
// ---------------------------------------------
let _toastEmit = null;
function showToast(msg, type = 'success', duration = 3500) {
  if (_toastEmit) _toastEmit(msg, type, duration);
}

function Toast({ id, msg, type, onDismiss }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t); }, []);
  const cfg = {
    success: { border: 'rgba(79,209,160,0.35)', icon: '#4FD1A0', bg: 'rgba(79,209,160,0.12)', symbol: '✓' },
    error:   { border: 'rgba(248,113,113,0.35)', icon: '#F7555F', bg: 'rgba(248,113,113,0.12)', symbol: '✕' },
    info:    { border: 'rgba(21,180,214,0.35)',   icon: '#15B4D6', bg: 'rgba(21,180,214,0.12)',   symbol: 'ℹ' },
    warn:    { border: 'rgba(249,219,121,0.35)',  icon: '#F9DB79', bg: 'rgba(249,219,121,0.12)',  symbol: '⚠' },
  }[type] || { border:'rgba(21,180,214,0.35)', icon:'#15B4D6', bg:'rgba(21,180,214,0.12)', symbol:'ℹ' };
  return (
    <div onClick={() => onDismiss(id)} style={{
      display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
      background:'rgba(8,10,20,0.96)', backdropFilter:'blur(24px)',
      border:`1px solid ${cfg.border}`, borderRadius:14,
      boxShadow:`0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${cfg.border}`,
      minWidth:280, maxWidth:380, cursor:'pointer',
      transform: visible ? 'translateX(0)' : 'translateX(24px)',
      opacity: visible ? 1 : 0,
      transition:'all 0.32s cubic-bezier(0.16,1,0.3,1)',
    }}>
      <div style={{width:26,height:26,borderRadius:99,background:cfg.bg,border:`1px solid ${cfg.border}`,
        display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:900,color:cfg.icon,flexShrink:0}}>
        {cfg.symbol}
      </div>
      <span style={{fontSize:13,fontWeight:500,color:T.text,flex:1,lineHeight:1.45}}>{msg}</span>
      <span style={{fontSize:11,color:T.ghost,fontWeight:600,flexShrink:0}}>✕</span>
    </div>
  );
}

function ToastContainer({ toasts, onDismiss }) {
  return (
    <div style={{position:'fixed',top:20,right:20,zIndex:9999,display:'flex',flexDirection:'column',gap:10,pointerEvents:'none',alignItems:'flex-end'}}>
      {toasts.map(t => (
        <div key={t.id} style={{pointerEvents:'all'}}>
          <Toast {...t} onDismiss={onDismiss}/>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------
// COUNT-UP HOOK
// ---------------------------------------------
function useCountUp(target, duration = 1100, delay = 80) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf, start = null;
    const timeout = setTimeout(() => {
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
        setValue(Math.round(eased * target));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(timeout); cancelAnimationFrame(raf); };
  }, [target, duration, delay]);
  return value;
}

// ---------------------------------------------
// PRIMITIVES
// ---------------------------------------------
function useMouseGlow() {
  const [pos, setPos] = useState({ x: -1000, y: -1000 });
  const [isHovered, setIsHovered] = useState(false);
  
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  
  return {
    onMouseMove: handleMouseMove,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    style: {
      backgroundImage: isHovered ? `radial-gradient(400px circle at ${pos.x}px ${pos.y}px, rgba(21,180,214,0.06), transparent 40%)` : "",
      transition: "background-image 0s"
    }
  };
}
const Card = ({children,style={},className="", ...rest}) => {
  const { style: glowStyle, ...glowProps } = useMouseGlow();
  return (
    <div className={`glass-panel ${className}`} style={{padding:24, ...style, ...glowStyle}} {...glowProps} {...rest}>
      {children}
    </div>
  );
};

const Inp = ({value,onChange,placeholder,style={}}) => (
  <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
    style={{width:"100%",background:T.input,border:`1px solid ${T.surfaceBorder}`,borderRadius:12,
      padding:"10px 14px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",transition:"all 0.2s ease",...style}}
    onFocus={e=>{e.target.style.borderColor=T.blue;e.target.style.boxShadow=`0 0 0 3px rgba(21,180,214,0.15)`;}}
    onBlur={e=>{e.target.style.borderColor=T.surfaceBorder;e.target.style.boxShadow="none";}} />
);

// Uncontrolled input - safe inside inline sub-components, won't lose focus on re-render
function InpFree({defaultVal,onCommit,placeholder,style={},mono=false}){
  const ref=React.useRef(null);
  React.useEffect(()=>{
    if(ref.current&&document.activeElement!==ref.current)ref.current.value=defaultVal||"";
  },[defaultVal]);
  const handleBlur=e=>{if(onCommit)onCommit(e.target.value);e.target.style.borderColor=T.surfaceBorder;e.target.style.boxShadow="none";};
  return(
    <input ref={ref} defaultValue={defaultVal||""} onBlur={handleBlur}
      onKeyDown={e=>{if(e.key==="Enter"&&onCommit)onCommit(e.target.value);}}
      placeholder={placeholder}
      style={{width:"100%",background:T.input,border:`1px solid ${T.surfaceBorder}`,borderRadius:12,
        padding:"10px 14px",fontSize:13,color:T.text,fontFamily:mono?"monospace":"inherit",
        outline:"none",transition:"all 0.2s ease",...style}}
      onFocus={e=>{e.target.style.borderColor=T.blue;e.target.style.boxShadow=`0 0 0 3px rgba(21,180,214,0.15)`;}}
    />
  );
}


function Btn({children,onClick,variant="ghost",disabled,style={},sm}){
  const base={display:"inline-flex",alignItems:"center",gap:6,padding:sm?"6px 14px":"9px 18px",
    borderRadius:12,border:"none",cursor:disabled?"not-allowed":"pointer",fontSize:sm?12:13,
    fontWeight:700,fontFamily:"inherit",opacity:disabled?0.5:1,transition:"all 0.2s ease",whiteSpace:"nowrap"};
  const v={
    primary:{background:`linear-gradient(135deg, ${T.blue}, #0E8FAA)`,color:"#060C0F",boxShadow:`0 4px 16px rgba(21, 180, 214, 0.25)`},
    ghost:{background:T.input,color:T.ghost,border:`1px solid ${T.surfaceBorder}`},
    success:{background:`rgba(79, 209, 160, 0.15)`,color:T.teal,border:`1px solid rgba(79, 209, 160, 0.3)`},
    danger:{background:`rgba(239, 68, 68, 0.15)`,color:T.red,border:`1px solid rgba(239, 68, 68, 0.3)`},
  };
  return <button 
    onClick={disabled?undefined:onClick} 
    style={{...base,...v[variant],...style}}
    onMouseEnter={e=>{if(!disabled){e.currentTarget.style.transform="translateY(-1px)"; if(variant==="primary")e.currentTarget.style.boxShadow=`0 6px 20px rgba(21, 180, 214, 0.4)`; else e.currentTarget.style.color=T.text;}}}
    onMouseLeave={e=>{if(!disabled){e.currentTarget.style.transform="translateY(0)"; if(variant==="primary")e.currentTarget.style.boxShadow=v.primary.boxShadow; else e.currentTarget.style.color=v[variant].color;}}}
  >
    {children}
  </button>;
}

function ScoreRing({score,size=100,sw=8,platform,showGrade=false}){
  const uid = useMemo(()=>Math.random().toString(36).slice(2,8),[]);
  const displayed = useCountUp(score||0, 1200, 120);
  const g = gradeFromScore(score);
  const s = score||0;
  // Gradient: red→amber (poor), amber→cyan (avg), cyan→teal (good), teal→blue (excellent)
  const [c0,c1] = s>=80?['#4FD1A0','#15B4D6']:s>=60?['#15B4D6','#1F7085']:s>=40?['#F9DB79','#15B4D6']:['#F7555F','#F9DB79'];
  const r=(size-sw)/2, circ=2*Math.PI*r;
  const offset = circ-(displayed/100)*circ;
  const finalOffset = circ-(s/100)*circ;
  return(
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
      <div style={{position:"relative",width:size,height:size}}>
        <svg width={size} height={size} style={{transform:"rotate(-90deg)",overflow:"visible"}}>
          <defs>
            <linearGradient id={`rg-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={c0}/>
              <stop offset="100%" stopColor={c1}/>
            </linearGradient>
          </defs>
          {/* Track ring */}
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={sw}/>
          {/* Glow layer */}
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`url(#rg-${uid})`} strokeWidth={sw+8}
            strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={finalOffset}
            style={{filter:`blur(${sw+2}px)`,opacity:0.25,transition:"stroke-dashoffset 1.4s cubic-bezier(0.16,1,0.3,1)"}}/>
          {/* Arc */}
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`url(#rg-${uid})`} strokeWidth={sw}
            strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
            style={{transition:"stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1)"}}/>
        </svg>
        <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
          <span style={{fontSize:size*0.27,fontWeight:900,color:T.white,lineHeight:1,letterSpacing:"-0.04em"}}>{Math.round(displayed)}</span>
          {showGrade&&<span style={{fontSize:size*0.15,fontWeight:700,color:c0,marginTop:2}}>{g}</span>}
        </div>
      </div>
      {platform&&<span style={{fontSize:10,color:T.ghost,fontWeight:700,textAlign:"center",letterSpacing:"0.05em",textTransform:"uppercase"}}>{platform}</span>}
    </div>
  );
}

const GradeBadge = ({grade}) => {
  const col=gcol(grade);
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:36,height:36,borderRadius:10,background:`${col}22`,color:col,fontWeight:800,fontSize:14}}>{grade}</span>;
};

// ---------------------------------------------
// SIDEBAR
// ---------------------------------------------
function Sidebar({activeNav,onNavChange}){
  const nav=[
    {id:"clients", label:"Clients", icon:(
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    )},
    {id:"schema",  label:"Schema",  icon:(
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
      </svg>
    )},
    {id:"podcast", label:"Podcast", icon:(
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="2"/><path d="M16.72 11a4.72 4.72 0 0 1-9.44 0"/><path d="M5 12a7 7 0 0 0 14 0"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="9" y1="22" x2="15" y2="22"/>
      </svg>
    )},
    {id:"gtm",label:"GTM", icon:(
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        <line x1="12" y1="2" x2="12" y2="22"/>
      </svg>
    )},
    {id:"settings",label:"Settings", icon:(
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    )},
  ];
  return(
    <div style={{width:240,height:"100%",display:"flex",flexDirection:"column",flexShrink:0,
      background:"rgba(7,9,15,0.92)",backdropFilter:"blur(24px)",WebkitBackdropFilter:"blur(24px)",
      borderRight:`1px solid rgba(255,255,255,0.06)`,position:"relative",zIndex:2}}>

      {/* Logo */}
      <div style={{padding:"24px 20px 20px",display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:32,height:32,borderRadius:10,background:T.gradBlue,
          display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
          boxShadow:`0 4px 16px rgba(21,180,214,0.35)`}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M3 12C3 7 7.5 3 13 3C18 3 21 7 21 12C21 14 20 16 18 17.5C16 15 14 14 12 14C10 14 8 15 6 17.5C4 16 3 14 3 12Z" fill="white" opacity="0.9"/>
            <path d="M6 17.5C8 15 10 14 12 14C14 14 16 15 18 17.5C16.5 19.5 14.5 21 12 21C9.5 21 7.5 19.5 6 17.5Z" fill="white" opacity="0.6"/>
          </svg>
        </div>
        <div>
          <div style={{fontSize:15,fontWeight:800,color:T.white,letterSpacing:"-0.03em",lineHeight:1.1}}>Shark AI</div>
          <div style={{fontSize:10,fontWeight:600,color:T.ghost,letterSpacing:"0.06em",textTransform:"uppercase"}}>GEO Intelligence</div>
        </div>
      </div>

      <div className="divider" style={{margin:"0 16px 12px"}}/>

      {/* Nav */}
      <nav style={{flex:1,padding:"4px 12px",overflowY:"auto"}}>
        {nav.map(item=>{
          const active=activeNav===item.id;
          return(
            <button key={item.id} onClick={()=>onNavChange(item.id)}
              className={active?"nav-item-active":""}
              style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:12,
                border:"none",cursor:"pointer",background:active?"":"transparent",
                marginBottom:2,transition:"all 0.18s cubic-bezier(0.16,1,0.3,1)",fontFamily:"inherit",textAlign:"left"}}>
              <div style={{width:32,height:32,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                background:active?T.gradBlue:"rgba(255,255,255,0.05)",
                color:active?"#060810":active?T.blue:T.ghost,
                boxShadow:active?`0 4px 12px rgba(21,180,214,0.35)`:"none",
                transition:"all 0.18s ease",border:active?"none":"1px solid rgba(255,255,255,0.07)"}}>
                {item.icon}
              </div>
              <span style={{fontSize:13,fontWeight:active?700:500,color:active?T.white:T.ghost,letterSpacing:"-0.01em",transition:"color 0.18s"}}>{item.label}</span>
              {active&&<div style={{marginLeft:"auto",width:4,height:4,borderRadius:99,background:T.blue,boxShadow:`0 0 8px ${T.blue}`}}/>}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{padding:"12px 16px 20px",borderTop:`1px solid rgba(255,255,255,0.05)`}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div className="pulse-dot" style={{width:6,height:6,borderRadius:99,background:T.teal}}/>
            <span style={{fontSize:11,fontWeight:600,color:T.ghost}}>Live</span>
          </div>
          <span style={{fontSize:11,fontWeight:700,color:T.blue,textShadow:`0 0 12px rgba(21,180,214,0.5)`}}>1,247 signals</span>
        </div>
        <div style={{fontSize:10,color:"rgba(255,255,255,0.2)",fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase"}}>Shark OS · v2.0</div>
      </div>
    </div>
  );
}

// ---------------------------------------------
// SIGN-IN MODAL
// ---------------------------------------------
function SignInModal({onClose,onSignIn}){
  const [email,setEmail]=useState("");
  const [pass,setPass]=useState("");
  const [err,setErr]=useState("");
  const submit=()=>{
    if(!email||!pass){setErr("Please enter email and password.");return;}
    onSignIn({email,name:email.split("@")[0]});
    onClose();
  };
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:"rgba(10,14,26,0.97)",backdropFilter:"blur(24px)",border:`1px solid rgba(255,255,255,0.1)`,borderRadius:20,padding:36,width:380,boxShadow:"0 24px 80px rgba(0,0,0,0.6)"}}>
        <div style={{fontWeight:800,fontSize:20,color:T.white,marginBottom:4,letterSpacing:"-0.03em"}}>Sign In</div>
        <div style={{fontSize:13,color:T.ghost,marginBottom:24}}>Access your Shark AI dashboard</div>
        {err&&<div style={{background:`${T.red}12`,border:`1px solid ${T.red}33`,borderRadius:8,padding:"8px 12px",fontSize:12,color:T.red,marginBottom:14}}>{err}</div>}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:6}}>EMAIL</div>
          <Inp value={email} onChange={setEmail} placeholder="you@sharkbranding.com"/>
        </div>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:6}}>PASSWORD</div>
          <Inp value={pass} onChange={setPass} placeholder="" style={{fontFamily:"inherit"}}/>
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn variant="primary" onClick={submit} style={{flex:1,justifyContent:"center"}}>Sign In</Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>
        <div style={{fontSize:11,color:T.ghost,textAlign:"center",marginTop:14}}>Demo: any email + password works</div>
      </div>
    </div>
  );
}

// ---------------------------------------------
// HEADER
// ---------------------------------------------
function Header({title,breadcrumb,user,onSignIn,onSignOut,onOpenCmdK}){
  const [showModal,setShowModal]=useState(false);
  return(
    <>
    <div className="app-header" style={{padding:"16px 28px",flexShrink:0}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{color:T.ghost,fontSize:11,fontWeight:600,letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:3}}>{breadcrumb}</div>
          <div style={{color:T.white,fontSize:20,fontWeight:800,letterSpacing:"-0.035em",lineHeight:1.2}}>{title}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button onClick={onOpenCmdK}
            style={{background:"rgba(255,255,255,0.1)",backdropFilter:"blur(8px)",border:"1px solid rgba(255,255,255,0.2)", cursor:"pointer",
              borderRadius:24,padding:"8px 16px",color:T.ghost,fontSize:13,outline:"none",width:220,fontFamily:"inherit",textAlign:"left",display:"flex",alignItems:"center",justifyContent:"space-between",transition:"all 0.2s"}}
            onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.2)";e.currentTarget.style.color=T.white;}}
            onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.1)";e.currentTarget.style.color=T.ghost;}}>
            <span>Search clients...</span>
            <span style={{fontSize:10,background:"rgba(255,255,255,0.1)",padding:"2px 6px",borderRadius:4,color:T.ghost}}>Cmd K</span>
          </button>
          {user?(
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:10,
                padding:"6px 12px",color:T.white,fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {user.name}
              </div>
              <button onClick={onSignOut} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.3)",
                borderRadius:10,padding:"6px 12px",color:T.white,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                Sign Out
              </button>
            </div>
          ):(
            <button onClick={()=>setShowModal(true)}
              style={{background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:10,
                padding:"8px 14px",color:T.white,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:6}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              Sign In
            </button>
          )}
          <button style={{background:"transparent",border:"none",color:T.white,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center"}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          </button>
        </div>
      </div>
    </div>
    {showModal&&<SignInModal onClose={()=>setShowModal(false)} onSignIn={onSignIn}/>}
    </>
  );
}

// ---------------------------------------------
// STAT CARD
// ---------------------------------------------
function StatCard({label,value,change,positive,icon}){
  const numVal = (typeof value==='number') ? value : (!isNaN(parseFloat(value))&&!/[\/\s]/.test(String(value)) ? parseFloat(value) : null);
  const counted = useCountUp(numVal||0, 1000, 200);
  const display = numVal!==null ? Math.round(counted) : value;
  const accentColor = positive!==false ? T.blue : T.red;
  const gradBg = positive!==false
    ? `linear-gradient(135deg,${T.blue},${T.tealDeep})`
    : `linear-gradient(135deg,${T.orange},${T.red})`;
  return(
    <div className="glass-panel card-glow" style={{padding:22,transition:"transform 0.2s ease, box-shadow 0.2s ease",cursor:"default"}}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=`0 12px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(21,180,214,0.15)`;}}
      onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="";}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>{label}</div>
          <div style={{fontSize:28,fontWeight:900,color:T.white,letterSpacing:"-0.04em",lineHeight:1}}>{display}</div>
          {change&&(
            <div style={{display:"flex",alignItems:"center",gap:5,marginTop:9}}>
              <span style={{fontSize:11,fontWeight:700,color:positive?T.teal:T.red,display:"flex",alignItems:"center",gap:2}}>
                {positive?"↑":"↓"} {change}
              </span>
              <span style={{fontSize:11,color:T.ghost}}>vs last month</span>
            </div>
          )}
        </div>
        <div style={{width:42,height:42,borderRadius:12,background:gradBg,flexShrink:0,
          display:"flex",alignItems:"center",justifyContent:"center",
          boxShadow:`0 4px 16px ${accentColor}33`}}>
          {icon}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------
// CLIENT LIST
// ---------------------------------------------
function ClientRow({c, i, onSelect, onDelete}) {
  const { style: glowStyle, ...glowProps } = useMouseGlow();
  return (
    <div className="slide-in glass-panel"
      style={{animationDelay:`${i*40}ms`,padding:"20px 24px",
        cursor:"pointer",display:"flex",alignItems:"center",gap:20,transition:"all 0.2s",...glowStyle}}
      {...glowProps}
      onClick={()=>onSelect(c)}
      onMouseEnter={e=>{
        glowProps.onMouseEnter?.(e);
        e.currentTarget.style.transform="translateY(-2px)"; 
        e.currentTarget.style.boxShadow=`0 8px 32px rgba(21, 180, 214, 0.15)`;
        e.currentTarget.style.borderColor=T.blue;
      }}
      onMouseLeave={e=>{
        glowProps.onMouseLeave?.(e);
        e.currentTarget.style.transform="translateY(0)"; 
        e.currentTarget.style.boxShadow=`0 4px 24px -4px rgba(0,0,0,0.3)`;
        e.currentTarget.style.borderColor=T.surfaceBorder;
      }}>
      <ScoreRing score={c.score} size={64} sw={6}/>
      <div style={{flex:1}}>
        <div style={{fontWeight:800,fontSize:18,color:T.white}}>{c.name}</div>
        <div style={{fontSize:13,color:T.ghost,marginTop:6,display:"flex",alignItems:"center",gap:6}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>  
          {c.domain} · {c.businessType}{c.city?` · ${c.city}`:""}
        </div>
      </div>
      <GradeBadge grade={c.grade}/>
      <span style={{fontSize:13,color:T.ghost,minWidth:100,textAlign:"right",fontWeight:600}}>{c.lastUpdated}</span>
      <button onClick={e=>{e.stopPropagation();onDelete(c.id);}}
        style={{background:"transparent",border:"none",cursor:"pointer",padding:8,color:T.ghost,borderRadius:10,fontSize:16,transition:"all 0.2s"}}
        onMouseEnter={e=>{e.currentTarget.style.background=`rgba(239, 68, 68, 0.15)`;e.currentTarget.style.color=T.red;}}
        onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=T.ghost;}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
        </button>
    </div>
  );
}

function SkeletonRow({i}) {
  return (
    <div className="glass-panel" style={{padding:"20px 24px",display:"flex",alignItems:"center",gap:20,opacity:1-(i*0.15)}}>
      <div className="skeleton-box" style={{width:64,height:64,borderRadius:"50%"}} />
      <div style={{flex:1,display:"flex",flexDirection:"column",gap:10}}>
        <div className="skeleton-box" style={{width:"30%",height:20}} />
        <div className="skeleton-box" style={{width:"50%",height:14}} />
      </div>
      <div className="skeleton-box" style={{width:36,height:36,borderRadius:10}} />
      <div className="skeleton-box" style={{width:80,height:14}} />
      <div className="skeleton-box" style={{width:34,height:34,borderRadius:10}} />
    </div>
  );
}

function ClientList({clients=[],onSelect,onAdd,onDelete,isLoading}){
  const [showForm,setShowForm]=useState(false);
  const [form,setForm]=useState({name:"",domain:"",businessType:"",city:"",gtm:""});
  const avg=clients.length?Math.round(clients.reduce((a,c)=>a+c.score,0)/clients.length):0;
  const aCount=clients.filter(c=>c.grade==="A").length;
  const submit=()=>{
    if(form.name&&form.domain&&form.businessType){onAdd(form);setForm({name:"",domain:"",businessType:"",gtm:"",city:""});setShowForm(false);}
  };
  return(
    <div style={{flex:1,padding:28,overflow:"auto",position:"relative"}}>
      

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:28, position:"relative", zIndex:1}}>
        <div className="glass-panel" style={{padding:24}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:1,color:T.ghost}}>TOTAL CLIENTS</div>
            <div style={{width:32,height:32,borderRadius:8,background:`rgba(21, 180, 214, 0.1)`,display:"flex",alignItems:"center",justifyContent:"center",color:T.blue}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
          </div>
          <div style={{fontSize:32,fontWeight:800,color:T.white}}>{clients.length}</div>
          <div style={{fontSize:12,color:T.teal,marginTop:8,fontWeight:700}}>+2 <span style={{color:T.ghost,fontWeight:500}}>this month</span></div>
        </div>
        
        <div className="glass-panel" style={{padding:24}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:1,color:T.ghost}}>AVG. GEO SCORE</div>
            <div style={{width:32,height:32,borderRadius:8,background:`rgba(79, 209, 160, 0.1)`,display:"flex",alignItems:"center",justifyContent:"center",color:T.teal}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            </div>
          </div>
          <div style={{fontSize:32,fontWeight:800,color:T.white}}>{avg}</div>
          <div style={{fontSize:12,color:T.teal,marginTop:8,fontWeight:700}}>+3.5% <span style={{color:T.ghost,fontWeight:500}}>this month</span></div>
        </div>

        <div className="glass-panel" style={{padding:24}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:1,color:T.ghost}}>A-GRADE CLIENTS</div>
            <div style={{width:32,height:32,borderRadius:8,background:`rgba(124, 58, 237, 0.1)`,display:"flex",alignItems:"center",justifyContent:"center",color:T.blue}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </div>
          </div>
          <div style={{fontSize:32,fontWeight:800,color:T.white}}>{aCount}</div>
          <div style={{fontSize:12,color:T.teal,marginTop:8,fontWeight:700}}>+1 <span style={{color:T.ghost,fontWeight:500}}>this month</span></div>
        </div>
      </div>

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16, position:"relative", zIndex:1}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20,fontWeight:800,color:T.white}}>All Clients</span>
          <span style={{background:`rgba(21, 180, 214, 0.1)`,border:`1px solid rgba(21, 180, 214, 0.2)`,color:T.blue,fontSize:12,padding:"4px 12px",borderRadius:20,fontWeight:700}}>{clients.length} total</span>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <Btn sm variant="ghost" onClick={()=>{
            const data = JSON.stringify(LS.get("clients")||[], null, 2);
            const blob = new Blob([data], {type:"application/json"});
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = "sharkai-clients-"+new Date().toISOString().slice(0,10)+".json";
            a.click(); URL.revokeObjectURL(url);
          }}>⬇ Export</Btn>
          <label style={{cursor:"pointer"}}>
            <input type="file" accept=".json" style={{display:"none"}} onChange={e=>{
              const file = e.target.files[0]; if(!file) return;
              const reader = new FileReader();
              reader.onload = ev => {
                try {
                  const imported = JSON.parse(ev.target.result);
                  if (!Array.isArray(imported)) { alert("Invalid file — expected a JSON array of clients."); return; }
                  const existing = LS.get("clients")||[];
                  const merged = [...existing];
                  let added = 0;
                  imported.forEach(c => {
                    if (!merged.find(x => x.id === c.id)) { merged.unshift(c); added++; }
                  });
                  LS.set("clients", merged);
                  alert(`✓ Imported ${added} new client${added!==1?"s":""}. Refresh to see them.`);
                  window.location.reload();
                } catch(err) { alert("Failed to parse file: " + err.message); }
              };
              reader.readAsText(file);
            }}/>
            <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 14px",
              borderRadius:12,fontSize:12,fontWeight:700,cursor:"pointer",
              background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.5)",
              border:"1px solid rgba(255,255,255,0.1)",transition:"all 0.2s",fontFamily:"inherit"}}
              onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.1)";e.currentTarget.style.color="#fff";}}
              onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.06)";e.currentTarget.style.color="rgba(255,255,255,0.5)";}}>
              ⬆ Import
            </span>
          </label>
          <Btn variant="primary" onClick={()=>setShowForm(!showForm)}>+ Add Client</Btn>
        </div>
      </div>

      {showForm&&(
        <div style={{marginBottom:16, position:"relative", zIndex:1}}>
          <Card>
            <div style={{fontSize:14,fontWeight:800,color:T.text,marginBottom:16}}>Add New Client</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
              {[["Business Name *","name"],["City","city"],["Domain *","domain"],["Business Type *","businessType"],["GTM Container ID","gtm"]].map(([lbl,key])=>(
                <div key={key}>
                  <div style={{fontSize:11,fontWeight:700,color:T.ghost,marginBottom:6,letterSpacing:0.8}}>{lbl.toUpperCase()}</div>
                  <InpFree
                    defaultVal={form[key]}
                    onCommit={v=>setForm(f=>({...f,[key]:v}))}
                    placeholder={lbl.replace(" *","")}
                  />
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <Btn variant="ghost" onClick={()=>{setShowForm(false);setForm({name:"",domain:"",businessType:"",gtm:"",city:""});}}>Cancel</Btn>
              <Btn variant="primary" onClick={submit}>Save Client</Btn>
            </div>
          </Card>
        </div>
      )}
      <div style={{display:"flex",flexDirection:"column",gap:12, position:"relative", zIndex:1}}>
        {isLoading ? (
          [0,1,2,3].map(i => <SkeletonRow key={`skel-${i}`} i={i} />)
        ) : clients.length === 0 ? (
          <div style={{textAlign:"center",padding:40,color:T.ghost}}>No clients found. Add one to get started.</div>
        ) : (
          clients.map((c,i)=>(
            <ClientRow key={c.id} c={c} i={i} onSelect={onSelect} onDelete={onDelete} />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------
// OVERVIEW TAB
// ---------------------------------------------

// Real audit history lookback - reads from saved audit snapshots in localStorage
// Each time an audit runs, a snapshot is saved with score + timestamp
function saveAuditSnapshot(clientId, score){
  const key = "audit_history_"+clientId;
  const history = LS.get(key) || [];
  const now = new Date();
  const monthKey = now.toLocaleString("default",{month:"short",year:"numeric"});
  // Only save one snapshot per month (latest wins)
  const filtered = history.filter(h=>h.m!==monthKey);
  filtered.push({m:monthKey,v:score,ts:now.toISOString()});
  // Keep last 12 months
  filtered.sort((a,b)=>new Date(a.ts)-new Date(b.ts));
  LS.set(key, filtered.slice(-12));
}

function getAuditHistory(clientId){
  return LS.get("audit_history_"+clientId) || [];
}

const LAST_6=()=>{
  const months=[];
  for(let i=5;i>=0;i--){
    const d=new Date();d.setMonth(d.getMonth()-i);
    months.push(d.toLocaleString("default",{month:"short"}));
  }
  return months;
};

const ACTIONS=[
  {n:"01",title:"Add LocalBusiness schema markup",detail:"Missing structured data for local search",effort:"Low"},
  {n:"02",title:"Optimize Google Business Profile",detail:"Update hours and add more photos",effort:"Medium"},
  {n:"03",title:"Fix NAP consistency issues",detail:"Address mismatch in 3 citation sources",effort:"High"},
  {n:"04",title:"Respond to pending reviews",detail:"5 reviews awaiting response on Google",effort:"Low"},
];
const EFFORT_COL={Low:`${T.teal}22|${T.teal}`,Medium:`${T.gold}22|${T.gold}`,High:`${T.orange}22|${T.orange}`};

// SVG line chart helper
function buildPath(data,W=740,H=160,ox=30,oy=10){
  const max=Math.max(...data.map(d=>d.v),1);
  const pts=data.map((d,i)=>{
    const x=(i/(data.length-1))*W+ox;
    const y=H+oy-(d.v/max)*H;
    return[x,y];
  });
  const line=pts.map((p,i)=>`${i===0?"M":"L"}${p[0]},${p[1]}`).join(" ");
  const area=`${line} L${pts[pts.length-1][0]},${H+oy} L${ox},${H+oy} Z`;
  return{pts,line,area,max};
}

function CitationTrendChart({clientId,score}){
  const history = getAuditHistory(clientId);
  const [hover,setHover]=useState(null);
  const W=740,H=150,ox=30,oy=10;

  // No history yet - show empty state
  if(history.length < 2){
    return(
      <div className="dark-card" style={{borderRadius:20,padding:28}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <div style={{color:"rgba(255,255,255,0.75)",fontSize:14,fontWeight:700}}>GEO Score History</div>
            <div style={{color:"rgba(255,255,255,0.4)",fontSize:11,marginTop:2}}>Audit history builds over time</div>
          </div>
        </div>
        <div style={{textAlign:"center",padding:"32px 0",color:"rgba(255,255,255,0.25)",fontSize:13}}>
          <div style={{fontSize:32,marginBottom:12}}>📊</div>
          <div style={{fontWeight:600,marginBottom:6}}>No audit history yet</div>
          <div style={{fontSize:12}}>Run the first audit to start tracking GEO score over time.<br/>Each audit saves a monthly snapshot automatically.</div>
          {score>0&&<div style={{marginTop:16,fontSize:12,color:"rgba(255,255,255,0.4)"}}>Current score: <span style={{color:"#4318FF",fontWeight:700}}>{score}</span></div>}
        </div>
      </div>
    );
  }

  // Real data - build single-series chart from audit snapshots
  const data = history.slice(-6); // last 6 snapshots
  const globalMax = Math.max(...data.map(d=>d.v),100);
  const cur = data[data.length-1].v;
  const prev = data.length>1 ? data[data.length-2].v : cur;
  const delta = cur - prev;

  return(
    <div className="dark-card" style={{borderRadius:20,padding:28}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <div style={{color:"rgba(255,255,255,0.75)",fontSize:14,fontWeight:700}}>GEO Score History</div>
          <div style={{color:"rgba(255,255,255,0.4)",fontSize:11,marginTop:2}}>{data.length} audit{data.length!==1?"s":""} recorded</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{display:"flex",alignItems:"center",gap:5,justifyContent:"flex-end",marginBottom:2}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:"#4318FF"}}/>
            <span style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontWeight:600}}>GEO Score</span>
          </div>
          <div style={{fontSize:18,fontWeight:800,color:"white"}}>{cur}</div>
          <div style={{fontSize:11,fontWeight:700,color:delta>=0?T.teal:T.red}}>
            {delta>=0?"▲":"▼"}{Math.abs(delta)} pts
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{position:"relative"}}>
        <svg width="100%" viewBox={`0 0 ${W+ox+20} ${H+oy+20}`} preserveAspectRatio="none" style={{display:"block",overflow:"visible"}}>
          <defs>
            <linearGradient id="ag_geo" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#4318FF" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#4318FF" stopOpacity="0"/>
            </linearGradient>
          </defs>
          {[0,25,50,75,100].map(v=>(
            <line key={v} x1={ox} x2={W+ox}
              y1={H+oy-(v/100)*H} y2={H+oy-(v/100)*H}
              stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="4,4"/>
          ))}
          {[0,25,50,75,100].map(v=>(
            <text key={v} x={ox-6} y={H+oy-(v/100)*H+4} textAnchor="end"
              fill="rgba(255,255,255,0.25)" fontSize="9">{v}</text>
          ))}
          {(()=>{
            const pts=data.map((d,i)=>[(i/(data.length-1||1))*W+ox, H+oy-(d.v/globalMax)*H]);
            const line=pts.map((p,i)=>`${i===0?"M":"L"}${p[0]},${p[1]}`).join(" ");
            const area=`${line} L${pts[pts.length-1][0]},${H+oy} L${ox},${H+oy} Z`;
            return(<>
              <path d={area} fill="url(#ag_geo)"/>
              <path d={line} fill="none" stroke="#4318FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              {pts.map((p,i)=>(
                <circle key={i} cx={p[0]} cy={p[1]} r={hover?.i===i?6:4}
                  fill={T.white} stroke="#4318FF" strokeWidth="2"
                  style={{cursor:"pointer",transition:"r 0.1s"}}
                  onMouseEnter={()=>setHover({i,m:data[i].m,v:data[i].v})}
                  onMouseLeave={()=>setHover(null)}/>
              ))}
            </>);
          })()}
          {hover&&<line x1={(hover.i/(data.length-1||1))*W+ox} x2={(hover.i/(data.length-1||1))*W+ox}
            y1={oy} y2={H+oy} stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="3,3"/>}
        </svg>
        {hover&&(
          <div style={{position:"absolute",top:0,left:`${(hover.i/(data.length-1||1))*92+4}%`,
            transform:"translateX(-50%)",background:"rgba(13,21,40,0.95)",
            border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,padding:"8px 12px",
            pointerEvents:"none",minWidth:100,zIndex:10}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontWeight:700,marginBottom:4}}>{hover.m}</div>
            <div style={{display:"flex",justifyContent:"space-between",gap:12,fontSize:12}}>
              <span style={{color:"#4318FF",fontWeight:600}}>GEO Score</span>
              <span style={{color:T.white,fontWeight:700}}>{hover.v}</span>
            </div>
          </div>
        )}
      </div>

      <div style={{display:"flex",paddingLeft:30,paddingRight:20,marginTop:6}}>
        {data.map((d,i)=>(
          <span key={i} style={{flex:1,textAlign:"center",fontSize:10,
            color:hover?.i===i?"rgba(255,255,255,0.7)":"rgba(255,255,255,0.3)",
            fontWeight:hover?.i===i?700:400,transition:"color 0.1s"}}>{m}</span>
        ))}
      </div>
    </div>
  );
}

function GBPCard({client, gbmData, setGbmData, syncing, onSync}){
  const [editing, setEditing] = useState(false);
  const [syncState, setSyncState] = useState("idle"); // idle | searching | error
  const [syncError, setSyncError] = useState("");
  const [candidates, setCandidates] = useState([]); // multiple results to pick from
  const [form, setForm] = useState({
    name: client.name, address: client.city||"", phone: "",
    claimed: "", verified: "", rating: "", reviews: "", gtm: client.gtm||"",
  });

  useEffect(()=>{
    if(gbmData && gbmData.status==="verified"){
      setForm({
        name: gbmData.name||client.name, address: gbmData.address||"",
        phone: gbmData.phone||"", claimed:"yes", verified:"yes",
        rating: gbmData.rating||"", reviews: gbmData.reviews||"", gtm: client.gtm||"",
      });
    }
  },[gbmData]);

  // Google Places API search - works for ANY business, no OAuth, no approval needed
  const syncWithPlaces = async () => {
    const apiKey = (LS.get("settings")||{}).placesApiKey || "";
    if (!apiKey) {
      setSyncError("Add your Google Places API Key in Settings to enable live sync.");
      setSyncState("error"); return;
    }
    setSyncState("searching"); setSyncError(""); setCandidates([]);
    try {
      // Text Search - find place by business name + city
      const query = encodeURIComponent(`${client.name} ${client.city||client.domain||""}`);
      const fields = "place_id,name,formatted_address,formatted_phone_number,rating,user_ratings_total,website,business_status,opening_hours,types,geometry";
      const searchUrl = `https://maps.googleapis.com/maps/api/place/textsearch/json?query=${query}&key=${apiKey}`;
      // Use allorigins proxy to avoid CORS
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(searchUrl)}`;
      const r = await fetch(proxyUrl, { signal: AbortSignal.timeout(10000) });
      if (!r.ok) throw new Error("Search request failed: " + r.status);
      const raw = await r.json();
      const data = JSON.parse(raw.contents);
      if (data.status === "REQUEST_DENIED") throw new Error("API key invalid or Places API not enabled: " + (data.error_message||""));
      if (data.status === "ZERO_RESULTS" || !data.results?.length) throw new Error(`No results found for "${client.name}" - try editing the business name or city.`);
      if (data.results.length === 1) {
        await applyPlace(data.results[0], apiKey);
      } else {
        // Show top 5 for user to pick
        setCandidates(data.results.slice(0,5));
        setSyncState("picking");
      }
    } catch(e) {
      setSyncError(e.message || "Search failed");
      setSyncState("error");
    }
  };

  // Fetch full place details and apply to gbmData
  const applyPlace = async (result, apiKey) => {
    setSyncState("searching");
    try {
      const placeId = result.place_id;
      const fields = "place_id,name,formatted_address,formatted_phone_number,rating,user_ratings_total,website,business_status,opening_hours,types,geometry,url";
      const detailUrl = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&fields=${fields}&key=${apiKey}`;
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(detailUrl)}`;
      const r = await fetch(proxyUrl, { signal: AbortSignal.timeout(10000) });
      if (!r.ok) throw new Error("Details fetch failed");
      const raw = await r.json();
      const data = JSON.parse(raw.contents);
      const p = data.result || result;
      const isOpen = p.business_status === "OPERATIONAL";
      setGbmData({
        name:     p.name || client.name,
        address:  p.formatted_address || "",
        phone:    p.formatted_phone_number || "",
        claimed:  true,
        verified: isOpen,
        gbpClaimed: true,
        rating:   p.rating || null,
        reviews:  p.user_ratings_total || null,
        category: client.businessType || (p.types?.[0]?.replace(/_/g," ") || ""),
        website:  p.website || null,
        mapsUrl:  p.url || `https://www.google.com/maps/place/?q=place_id:${placeId}`,
        placeId,
        status:   isOpen ? "verified" : "unverified_listing",
        source:   "places",
        gtm:      client.gtm || null,
      });
      setCandidates([]);
      setSyncState("idle");
    } catch(e) {
      setSyncError(e.message || "Failed to load place details");
      setSyncState("error");
    }
  };

  const handlePickCandidate = async (result) => {
    const apiKey = (LS.get("settings")||{}).placesApiKey || "";
    await applyPlace(result, apiKey);
  };

  const save = () => {
    const claimed = form.claimed==="yes";
    const verified = form.verified==="yes";
    if (form.gtm !== undefined && form.gtm !== client.gtm) {
      ipc("save-client", {...client, gtm: form.gtm});
    }
    setGbmData({
      name: form.name||client.name, address: form.address, phone: form.phone,
      claimed, verified, gbpClaimed: claimed,
      rating: form.rating||null, reviews: form.reviews||null,
      category: client.businessType,
      gtm: form.gtm||client.gtm||null,
      status: verified?"verified": claimed?"unverified_listing":"no_gbp",
      source: "manual",
    });
    setEditing(false);
  };

  const Field=({label,k,placeholder})=>(
    <div>
      <div style={{fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:4}}>{label}</div>
      <InpFree defaultVal={form[k]||""} onCommit={v=>setForm(f=>({...f,[k]:v}))} placeholder={placeholder}/>
    </div>
  );
  const SelectField=({label,k,options})=>(
    <div>
      <div style={{fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:4}}>{label}</div>
      <select value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})}
        style={{width:"100%",background:T.input,border:`1px solid ${T.border}`,borderRadius:12,
          padding:"10px 14px",fontSize:13,color:form[k]?T.text:T.ghost,fontFamily:"inherit",outline:"none"}}>
        <option value="">Select</option>
        {options.map(([v,l])=><option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  // Multiple results - let user pick the right one
  if (syncState === "picking") {
    return (
      <Card>
        <div style={{fontWeight:800,color:T.text,marginBottom:4}}>Google Business Profile</div>
        <div style={{fontSize:12,color:T.ghost,marginBottom:14}}>
          Multiple listings found for <strong style={{color:T.text}}>{client.name}</strong> - pick the correct one:
        </div>
        {candidates.map((c,i) => (
          <div key={c.place_id||i} onClick={()=>handlePickCandidate(c)}
            style={{padding:"12px 14px",marginBottom:8,borderRadius:10,border:`1px solid ${T.border}`,
              cursor:"pointer",transition:"all 0.15s"}}
            onMouseEnter={e=>{e.currentTarget.style.background=`${T.blue}12`;e.currentTarget.style.borderColor=T.blue;}}
            onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor=T.border;}}>
            <div style={{fontWeight:700,color:T.text,fontSize:13}}>{c.name}</div>
            <div style={{fontSize:11,color:T.ghost,marginTop:2}}>{c.formatted_address}</div>
            {c.rating&&<div style={{fontSize:11,color:T.gold,marginTop:3}}>★ {c.rating} ({c.user_ratings_total} reviews)</div>}
          </div>
        ))}
        <div style={{display:"flex",gap:8,marginTop:4}}>
          <Btn sm variant="ghost" onClick={()=>{setSyncState("idle");setCandidates([]);}}>Cancel</Btn>
          <Btn sm variant="ghost" onClick={()=>setSyncState("idle")}>Enter Manually Instead</Btn>
        </div>
      </Card>
    );
  }

  // Entry form (first time or editing)
  if (!gbmData || editing) {
    const hasPlacesKey = !!(LS.get("settings")||{}).placesApiKey;
    return (
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <span style={{fontWeight:800,color:T.text}}>Google Business Profile</span>
          {editing&&<Btn sm variant="ghost" onClick={()=>setEditing(false)}>Cancel</Btn>}
        </div>

        {/* Places API Sync button */}
        <div style={{marginBottom:14}}>
          <button onClick={syncWithPlaces} disabled={syncState==="searching"}
            style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,
              padding:"11px 0",borderRadius:12,border:`1px solid ${T.border}`,
              background: hasPlacesKey ? "#fff" : T.input,
              cursor:syncState==="searching"?"wait":"pointer",
              fontFamily:"inherit",fontSize:13,fontWeight:700,
              color: hasPlacesKey ? "#3c4043" : T.ghost,
              transition:"box-shadow 0.15s"}}
            onMouseEnter={e=>{if(hasPlacesKey)e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.18)";}}
            onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
            {syncState==="searching"
              ? <><span className="spin" style={{width:16,height:16,border:"2px solid #ddd",borderTopColor:"#4285F4",borderRadius:"50%",display:"inline-block"}}/> Searching Google Maps...</>
              : <><svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.5 1.2 8.9 3.2l6.6-6.6C35.4 2.4 30 0 24 0 14.6 0 6.5 5.4 2.5 13.4l7.7 6C12.2 13 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17z"/><path fill="#FBBC05" d="M10.2 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.7-4.6l-7.7-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.8l7.7-6.2z"/><path fill="#34A853" d="M24 48c6 0 11.1-2 14.8-5.4l-7.5-5.8c-2 1.4-4.6 2.2-7.3 2.2-6.4 0-11.8-4.3-13.8-10.1l-7.7 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                {hasPlacesKey ? "Sync Live GBP Data from Google Maps" : "Add Places API Key in Settings to Sync"}</>
            }
          </button>
          {syncState==="error" && (
            <div style={{marginTop:8,fontSize:11,color:T.red,background:`${T.red}10`,border:`1px solid ${T.red}33`,
              borderRadius:8,padding:"8px 10px",lineHeight:1.5}}>⚠ {syncError}</div>
          )}
          <div style={{display:"flex",alignItems:"center",gap:8,margin:"12px 0"}}>
            <div style={{flex:1,height:1,background:T.border}}/>
            <span style={{fontSize:11,color:T.ghost}}>or enter manually</span>
            <div style={{flex:1,height:1,background:T.border}}/>
          </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="BUSINESS NAME" k="name" placeholder={client.name}/>
          <Field label="GTM CONTAINER ID" k="gtm" placeholder="GTM-XXXXXXX"/>
          <Field label="FULL ADDRESS (as it appears on GBP)" k="address" placeholder="123 Main St, Wesley Chapel, FL 33543"/>
          <Field label="PHONE" k="phone" placeholder="(813) 555-0000"/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <SelectField label="GBP CLAIMED?" k="claimed" options={[["yes","✓ Yes - claimed"],["no","✗ No - unclaimed"]]}/>
            <SelectField label="VERIFIED BY GOOGLE?" k="verified" options={[["yes","✓ Yes - verified"],["no","✗ No - not verified"]]}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="STAR RATING (optional)" k="rating" placeholder="4.8"/>
            <Field label="REVIEW COUNT (optional)" k="reviews" placeholder="47"/>
          </div>
          <Btn variant="primary" onClick={save}>Save GBP Data</Btn>
        </div>
      </Card>
    );
  }

  // Verified display card
  const st = gbmData.status;
  const isClaimed = gbmData.claimed||gbmData.gbpClaimed;
  const isVerified = gbmData.verified||st==="verified";
  const isPlaces = gbmData.source==="places";

  return (
    <Card>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontWeight:800,color:T.text}}>Google Business Profile</span>
          {isPlaces&&<span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:6,
            background:`${T.teal}18`,color:T.teal}}>LIVE via Places API</span>}
        </div>
        <div style={{display:"flex",gap:6}}>
          <Btn sm variant="ghost" onClick={syncWithPlaces} disabled={syncState==="searching"}>
            {syncState==="searching"?<><span className="spin" style={{width:10,height:10,border:`2px solid ${T.ghost}33`,borderTopColor:T.ghost,borderRadius:"50%",display:"inline-block"}}/> Syncing...</>:"↻ Re-sync"}
          </Btn>
          <Btn sm variant="ghost" onClick={()=>setEditing(true)}>✎ Edit</Btn>
          {gbmData.mapsUrl&&<a href={gbmData.mapsUrl} target="_blank" rel="noreferrer"
            style={{display:"inline-flex",alignItems:"center",gap:4,padding:"6px 12px",borderRadius:10,
              fontSize:12,fontWeight:700,color:T.blue,background:`${T.blue}10`,border:`1px solid ${T.blue}22`,
              textDecoration:"none",transition:"all 0.2s"}}
            onMouseEnter={e=>e.currentTarget.style.background=`${T.blue}20`}
            onMouseLeave={e=>e.currentTarget.style.background=`${T.blue}10`}>
            ↗ Maps
          </a>}
        </div>
      </div>

      {syncState==="error"&&(
        <div style={{marginBottom:12,fontSize:11,color:T.red,background:`${T.red}10`,border:`1px solid ${T.red}33`,
          borderRadius:8,padding:"8px 10px"}}>⚠ {syncError}</div>
      )}

      {isVerified?(
        <div style={{background:`${T.teal}12`,border:`1px solid ${T.teal}44`,borderRadius:10,
          padding:"10px 14px",marginBottom:14,fontSize:12,color:T.teal,fontWeight:600}}>
          ✓ Active listing found on Google Maps{isPlaces?" · Data pulled live from Places API":""}
        </div>
      ):isClaimed?(
        <div style={{background:`${T.gold}12`,border:`1px solid ${T.gold}44`,borderRadius:10,
          padding:"10px 14px",marginBottom:14,fontSize:12,color:T.gold,fontWeight:600}}>
          ⚠ Listing found but may be unclaimed or inactive
        </div>
      ):(
        <div style={{background:`${T.red}10`,border:`1px solid ${T.red}33`,borderRadius:10,
          padding:"10px 14px",marginBottom:14,fontSize:12,color:T.red,fontWeight:600}}>
          ✗ Not claimed - listing may show wrong info in AI and Maps
        </div>
      )}

      {[
        ["Business Name", gbmData.name||client.name],
        ["Address",       gbmData.address||"-"],
        ["Phone",         gbmData.phone||"-"],
        ["Category",      gbmData.category||client.businessType||"-"],
        ["GTM Container", {val: gbmData.gtm||client.gtm||"Not set", col:(gbmData.gtm||client.gtm)?T.blue:T.ghost}],
        ...(gbmData.website?[["Website",{val:gbmData.website,col:T.blue}]]:[]),
        ["Claimed",  {val:isClaimed?"✓ Yes":"✗ No",  col:isClaimed?T.teal:T.red}],
        ["Verified", {val:isVerified?"✓ Yes":"✗ No", col:isVerified?T.teal:T.red}],
        ...(gbmData.rating?[["Rating",{val:`★ ${gbmData.rating} (${gbmData.reviews||"?"} reviews)`,col:T.gold}]]:[]),
      ].map(([k,v])=>(
        <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
          <span style={{fontSize:12,color:T.ghost}}>{k}</span>
          <span style={{fontSize:12,fontWeight:600,color:v?.col||T.text,wordBreak:"break-all",textAlign:"right",maxWidth:"65%"}}>{v?.val||v}</span>
        </div>
      ))}
    </Card>
  );
}

function OverviewTab({client, gbmData, setGbmData, syncing, onSync, onTabSwitch}){
  // Platform scores derived from real NAP audit data - not seeded
  const napData = LS.get("nap_"+client.id)||{};
  const auditHist = getAuditHistory(client.id);
  const latestScore = auditHist.length>0 ? auditHist[auditHist.length-1].v : (client.score||0);
  const googleStatus = napData["nap_google"]||"unchecked";
  const bingStatus   = napData["nap_bing"]||"unchecked";
  const appleStatus  = napData["nap_apple"]||"unchecked";
  const statusToScore = (st, base) => st==="accurate"?Math.min(99,base):st==="inaccurate"?Math.min(60,base-15):st==="missing"?Math.max(10,base-30):null;
  const googleScore = statusToScore(googleStatus, latestScore);
  const bingScore   = statusToScore(bingStatus,   latestScore-8);
  const appleScore  = statusToScore(appleStatus,  latestScore-14);
  const plats=[
    {label:"Google AI", score:googleScore},
    {label:"Bing",      score:bingScore},
    {label:"Apple",     score:appleScore},
  ];
  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      <CitationTrendChart clientId={client.id} score={client.score}/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
        <GBPCard client={client} gbmData={gbmData} setGbmData={setGbmData} syncing={syncing} onSync={onSync}/>
        <Card>
          <div style={{fontWeight:800,color:T.text,marginBottom:20}}>Platform Scores</div>
          {plats.every(p=>p.score===null)
            ?<div style={{textAlign:"center",color:T.ghost,fontSize:12,padding:"20px 0"}}>Run audit to calculate platform scores</div>
            :<div style={{display:"flex",justifyContent:"space-around",alignItems:"center",paddingTop:8}}>
              {plats.map(p=>p.score!==null
                ?<ScoreRing key={p.label} score={p.score} size={80} sw={7} platform={p.label}/>
                :<div key={p.label} style={{textAlign:"center"}}>
                  <div style={{width:80,height:80,borderRadius:"50%",border:`3px dashed ${T.border}`,display:"flex",alignItems:"center",justifyContent:"center",color:T.ghost,fontSize:11}}>N/A</div>
                  <div style={{fontSize:11,color:T.ghost,marginTop:6,fontWeight:700}}>{p.label}</div>
                </div>
              )}
            </div>
          }
        </Card>
      </div>
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontWeight:800,color:T.text}}>Top Action Items</span>
          <Btn sm variant="primary" onClick={()=>onTabSwitch?.("CHECKLIST")}>View Checklist ?</Btn>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {ACTIONS.map(a=>{const[bg,col]=EFFORT_COL[a.effort].split("|");return(
            <div key={a.n} style={{display:"flex",alignItems:"flex-start",gap:12}}>
              <span style={{fontWeight:800,color:T.blue,fontSize:13,width:24,flexShrink:0}}>{a.n}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:T.text}}>{a.title}</div>
                <div style={{fontSize:11,color:T.ghost,marginTop:2}}>{a.detail}</div>
              </div>
              <span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:bg,color:col,fontWeight:700,whiteSpace:"nowrap"}}>{a.effort}</span>
            </div>
          );})}
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------
// AUDIT TAB
// ---------------------------------------------
// Each signal has a finding for each possible status so they always match
const AUDIT_DATA=[
  {name:"Google AI Overview",signals:[
    {signal:"LocalBusiness Schema",impact:"High",findings:{
      Pass:"LocalBusiness schema present and valid",
      Warning:"Schema found but missing recommended fields (hours, priceRange)",
      Fail:"No LocalBusiness schema markup detected",
    }},
    {signal:"NAP Consistency",impact:"Medium",findings:{
      Pass:"Name, address, and phone match across all sources",
      Warning:"Minor formatting differences detected across listings",
      Fail:"NAP mismatch found  address or phone conflicts with Google",
    }},
    {signal:"Review Signals",impact:"Low",findings:{
      Pass:"Strong review presence with recent activity",
      Warning:"Reviews present but no owner responses found",
      Fail:"Fewer than 10 reviews or no recent review activity",
    }},
    {signal:"Content Freshness",impact:"Medium",findings:{
      Pass:"GBP posts and website updated within last 30 days",
      Warning:"Last update was 3090 days ago",
      Fail:"No content updates detected in 90+ days",
    }},
    {signal:"FAQ Schema",impact:"High",findings:{
      Pass:"FAQ schema present and indexed by Google",
      Warning:"FAQ content exists but schema markup is missing",
      Fail:"No FAQ schema or FAQ content found",
    }},
    {signal:"Service Categories",impact:"Medium",findings:{
      Pass:"Primary and secondary categories fully populated",
      Warning:"Primary category set but missing secondary categories",
      Fail:"Business category not set or incorrect",
    }},
  ]},
  {name:"Bing Copilot",signals:[
    {signal:"Bing Places Listing",impact:"Medium",findings:{
      Pass:"Verified and active listing on Bing Places",
      Warning:"Listing exists but not claimed or verified",
      Fail:"No Bing Places listing found",
    }},
    {signal:"Category Alignment",impact:"Low",findings:{
      Pass:"Categories match Google listing exactly",
      Warning:"Category mismatch between Bing and Google",
      Fail:"Incorrect or missing category on Bing",
    }},
    {signal:"Photo Quality",impact:"Medium",findings:{
      Pass:"High-quality photos present on listing",
      Warning:"Photos present but low resolution or outdated",
      Fail:"No photos on Bing listing",
    }},
    {signal:"Business Description",impact:"Low",findings:{
      Pass:"Description is complete and keyword-rich (250+ chars)",
      Warning:"Description present but under 250 characters",
      Fail:"No business description on Bing listing",
    }},
  ]},
  {name:"Apple Siri / Spotlight",signals:[
    {signal:"Apple Maps Listing",impact:"High",findings:{
      Pass:"Listing verified and accurate on Apple Maps",
      Warning:"Listing found but unverified or unclaimed",
      Fail:"Business not found on Apple Maps",
    }},
    {signal:"Business Hours",impact:"Medium",findings:{
      Pass:"Hours are accurate and synced with Apple Maps",
      Warning:"Hours present but may be outdated",
      Fail:"No business hours set on Apple Maps",
    }},
    {signal:"Payment Methods",impact:"Low",findings:{
      Pass:"Payment methods fully listed",
      Warning:"Some payment methods missing",
      Fail:"No payment information on listing",
    }},
    {signal:"Website Link",impact:"Low",findings:{
      Pass:"Website correctly linked and resolving",
      Warning:"Website linked but redirects or loads slowly",
      Fail:"No website link on Apple Maps listing",
    }},
  ]},
  {name:"AI Search Visibility",isCitations:true,signals:[
    // signals here are generated dynamically per-client in AuditTab
  ]},
  {name:"AI Mentions & Entity Coverage",signals:[
    {signal:"Brand Entity Recognition",impact:"High",findings:{
      Pass:"Business recognized as a named entity in AI results",
      Warning:"Business appears in AI results but not as a confirmed entity",
      Fail:"Business not recognized as a named entity in AI results",
    }},
    {signal:"Knowledge Panel",impact:"High",findings:{
      Pass:"Google Knowledge Panel exists for this business",
      Warning:"Knowledge Panel present but showing incomplete data",
      Fail:"No Google Knowledge Panel found  GBP likely unclaimed",
    }},
    {signal:"Wikipedia / Wikidata",impact:"Medium",findings:{
      Pass:"Wikidata entity exists and linked to Google",
      Warning:"Partial Wikidata entry found  needs completion",
      Fail:"No Wikidata entity found for this business",
    }},
    {signal:"llms.txt Present",impact:"Medium",findings:{
      Pass:"llms.txt found at domain root and properly formatted",
      Warning:"llms.txt exists but missing key sections",
      Fail:"No llms.txt file found at domain root",
    }},
    {signal:"AI-Cited Content",impact:"High",findings:{
      Pass:"Pages from this domain appear in AI overview citations",
      Warning:"Limited AI citation presence  12 pages only",
      Fail:"No pages from this domain found in AI overview citations",
    }},
    {signal:"Structured Q&A Coverage",impact:"High",findings:{
      Pass:"FAQ and Q&A content indexed and appearing in AI answers",
      Warning:"Q&A content exists but not structured for AI parsing",
      Fail:"No FAQ or Q&A content found on site",
    }},
  ]},
];
const SSTYLE={Pass:{dot:T.teal,text:T.teal,bg:`${T.teal}18`},Fail:{dot:T.red,text:T.red,bg:`${T.red}18`},Warning:{dot:T.gold,text:T.gold,bg:`${T.gold}18`}};

const CSTYLE={
  accurate:{dot:T.teal,text:T.teal,bg:`${T.teal}18`,label:"Accurate"},
  inaccurate:{dot:T.gold,text:T.gold,bg:`${T.gold}18`,label:"Inaccurate"},
  missing:{dot:T.red,text:T.red,bg:`${T.red}18`,label:"Missing"},
};

// NAP CONSISTENCY TRACKER - replaces seeded citation grid
// Users enter actual NAP data; statuses are manually set and persisted
const NAP_SOURCES = [
  {id:"nap_google",   name:"Google Business Profile", tier:1, url:"business.google.com"},
  {id:"nap_bing",     name:"Bing Places",             tier:1, url:"bingplaces.com"},
  {id:"nap_apple",    name:"Apple Maps",              tier:1, url:"mapsconnect.apple.com"},
  {id:"nap_yelp",     name:"Yelp",                    tier:1, url:"biz.yelp.com"},
  {id:"nap_facebook", name:"Facebook Business",       tier:1, url:"facebook.com/business"},
  {id:"nap_bbb",      name:"Better Business Bureau",  tier:1, url:"bbb.org"},
  {id:"nap_infogroup",name:"Data Axle / Infogroup",   tier:2, url:"dataaxle.com"},
  {id:"nap_localeze", name:"Neustar Localeze",         tier:2, url:"neustarlocaleze.biz"},
  {id:"nap_acxiom",   name:"Acxiom",                  tier:2, url:"acxiom.com"},
  {id:"nap_foursquare",name:"Foursquare",             tier:2, url:"foursquare.com"},
];
const NAP_TIER_LABELS={1:"Authority",2:"Aggregator"};
const NAP_TIER_COL={1:T.blue,2:T.teal};
const NAP_STATUS_CYCLE={unchecked:"accurate",accurate:"inaccurate",inaccurate:"missing",missing:"unchecked"};
const NAP_CSTYLE={
  accurate: {dot:T.teal, text:T.teal, bg:`${T.teal}18`, label:"Accurate"},
  inaccurate:{dot:T.gold, text:T.gold, bg:`${T.gold}18`, label:"Inaccurate"},
  missing:  {dot:T.red,  text:T.red,  bg:`${T.red}18`,  label:"Missing"},
  unchecked:{dot:T.ghost,text:T.ghost,bg:`${T.ghost}18`,label:"Not Checked"},
};

function NAPTracker({clientId}){
  const [statuses,setStatuses]=useState(()=>LS.get("nap_"+clientId)||{});
  const [notes,setNotes]=useState(()=>LS.get("nap_notes_"+clientId)||{});
  const [editing,setEditing]=useState(null);

  const cycleStatus=(id)=>{
    const cur=statuses[id]||"unchecked";
    const next=NAP_STATUS_CYCLE[cur];
    const updated={...statuses,[id]:next};
    setStatuses(updated);
    LS.set("nap_"+clientId,updated);
  };

  const saveNote=(id,val)=>{
    const updated={...notes,[id]:val};
    setNotes(updated);
    LS.set("nap_notes_"+clientId,updated);
    setEditing(null);
  };

  const checked=NAP_SOURCES.filter(s=>(statuses[s.id]||"unchecked")!=="unchecked");
  const accurate=checked.filter(s=>statuses[s.id]==="accurate").length;
  const inaccurate=checked.filter(s=>statuses[s.id]==="inaccurate").length;
  const missing=checked.filter(s=>statuses[s.id]==="missing").length;
  const pct=checked.length?Math.round((accurate/checked.length)*100):0;

  return(
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,padding:"16px 24px",borderBottom:`1px solid ${T.border}`}}>
        {[
          {label:"Checked",val:checked.length,col:T.text},
          {label:"Accurate",val:accurate,col:T.teal},
          {label:"Inaccurate",val:inaccurate,col:T.gold},
          {label:"Missing",val:missing,col:T.red},
        ].map(({label,val,col})=>(
          <div key={label} style={{textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:col}}>{val}</div>
            <div style={{fontSize:11,color:T.ghost,fontWeight:600}}>{label}</div>
          </div>
        ))}
      </div>
      {checked.length>0&&(
        <div style={{padding:"10px 24px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,fontWeight:600,marginBottom:5}}>
            <span style={{color:T.ghost}}>NAP Accuracy</span>
            <span style={{color:pct>=70?T.teal:pct>=40?T.gold:T.red}}>{pct}%</span>
          </div>
          <div style={{height:6,background:T.border,borderRadius:4,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${pct}%`,borderRadius:4,transition:"width 0.5s",
              background:pct>=70?`linear-gradient(90deg,${T.mint},#15B4D6)`:pct>=40?`linear-gradient(90deg,${T.gold},${T.yellow})`:`linear-gradient(90deg,${T.red},${T.coral})`}}/>
          </div>
        </div>
      )}
      <div style={{padding:"8px 24px",fontSize:11,color:T.ghost,background:T.input,borderBottom:`1px solid ${T.border}`}}>
        Click the status badge to mark each source. Add notes for any issues found. All data is saved per client.
      </div>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
          {["SOURCE","TIER","STATUS","NOTES"].map(h=>(
            <th key={h} style={{textAlign:"left",padding:"8px 24px",fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:1}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {NAP_SOURCES.map(s=>{
            const st=NAP_CSTYLE[statuses[s.id]||"unchecked"];
            const note=notes[s.id]||"";
            return(
              <tr key={s.id} style={{borderBottom:`1px solid ${T.border}`}}
                onMouseEnter={e=>e.currentTarget.style.background=T.input}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <td style={{padding:"10px 24px",fontWeight:600,color:T.text}}>
                  <a href={`https://${s.url}`} target="_blank" rel="noreferrer"
                    style={{color:T.text,textDecoration:"none"}}
                    onMouseEnter={e=>e.target.style.color=T.blue}
                    onMouseLeave={e=>e.target.style.color=T.text}>
                    {s.name}
                  </a>
                </td>
                <td style={{padding:"10px 24px"}}>
                  <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:6,
                    background:`${NAP_TIER_COL[s.tier]}18`,color:NAP_TIER_COL[s.tier]}}>
                    T{s.tier} {NAP_TIER_LABELS[s.tier]}
                  </span>
                </td>
                <td style={{padding:"10px 24px"}}>
                  <div onClick={()=>cycleStatus(s.id)}
                    style={{display:"inline-flex",alignItems:"center",gap:6,cursor:"pointer",
                      padding:"3px 8px",borderRadius:8,background:st.bg,
                      border:`1px solid ${st.dot}33`,transition:"opacity 0.15s",userSelect:"none"}}
                    title="Click to change status"
                    onMouseEnter={e=>e.currentTarget.style.opacity="0.75"}
                    onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:st.dot}}/>
                    <span style={{color:st.text,fontWeight:700,fontSize:11}}>{st.label}</span>
                    <span style={{fontSize:9,color:st.text,opacity:0.6}}>▼</span>
                  </div>
                </td>
                <td style={{padding:"10px 24px"}}>
                  {editing===s.id
                    ?<input autoFocus defaultValue={note}
                        onBlur={e=>saveNote(s.id,e.target.value)}
                        onKeyDown={e=>{if(e.key==="Enter")saveNote(s.id,e.target.value);if(e.key==="Escape")setEditing(null);}}
                        style={{width:"100%",border:`1px solid ${T.blue}`,borderRadius:6,padding:"4px 8px",
                          fontSize:11,fontFamily:"inherit",background:T.input,color:T.text,outline:"none"}}/>
                    :<div onClick={()=>setEditing(s.id)}
                        style={{fontSize:11,color:note?T.text:T.ghost,cursor:"text",padding:"4px 0",
                          minHeight:22,borderBottom:`1px dashed ${note?T.border:"transparent"}`}}>
                        {note||<span style={{opacity:0.5}}>Add note...</span>}
                      </div>
                  }
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AISnippetMonitor({ client }) {
  const [snippetPhase, setSnippetPhase] = useState("idle");
  const [snippetProgress, setSnippetProgress] = useState(0);
  const [snippetLabel, setSnippetLabel] = useState("");

  // Current scan - stored as {results, date, mentionCount, total}
  const [scan, setScan] = useState(() => LS.get("ai_snippets_" + client?.id) || null);
  const snippets = scan?.results || [];

  // Baseline - set on first scan, or manually via button
  const [baseline, setBaseline] = useState(() => LS.get("ai_snippets_baseline_" + client?.id) || null);

  const runSnippetSearch = async () => {
    if (!client?.name) return;
    setSnippetPhase("running");
    setScan(null);
    setSnippetProgress(0);

    const businessName = client.name;
    const domain = (client.domain || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
    const city = client.city || "";
    const btype = client.businessType || "business";

    const queries = [
      `best ${btype} in ${city || "my area"}`,
      `who is ${businessName}`,
      `${businessName} reviews`,
      `top ${btype} companies ${city || ""}`.trim(),
      `alternatives to ${businessName}`,
      `${btype} near me ${city || ""}`.trim(),
    ].filter(Boolean);

    const results = [];

    for (let i = 0; i < queries.length; i++) {
      const q = queries[i];
      setSnippetProgress(Math.round(((i) / queries.length) * 90));
      setSnippetLabel(`Querying AI: "${q}"…`);

      try {
        const resp = await claudeAPI({
            model: "gpt-4o",
            max_tokens: 600,
            
            messages: [{
              role: "user",
              content: `You are an AI assistant with broad knowledge of businesses, brands, and local markets. Answer the following query as if you are an AI search engine giving a direct, confident answer. Do NOT say you cannot browse the web. Do NOT suggest the user search elsewhere. Just answer directly from your knowledge.

The business we are tracking is "${businessName}"${domain ? ` (${domain})` : ""}${city ? ` in ${city}` : ""}.

Query: "${q}"

Give a 2-3 sentence answer. If you have no knowledge of this specific business, say so briefly and describe what kinds of businesses typically appear for this query instead.

Then output:
MENTIONED: yes or no (did ${businessName} appear in your answer?)
COMPETITOR_NAMES: comma-separated competitor names you mentioned, or "none"
SOURCE_URLS: relevant websites you know of for this topic, or "none"`
            }]
          });

        const data = resp;

        // Extract text from response (handles tool use + text blocks)
        const fullText = (data.content || [])
          .filter(b => b.type === "text")
          .map(b => b.text)
          .join("\n");

        // Parse the structured output
        const mentionedMatch = fullText.match(/MENTIONED:\s*(yes|no)/i);
        const competitorMatch = fullText.match(/COMPETITOR_NAMES:\s*([^\n]+)/i);
        const sourceMatch = fullText.match(/SOURCE_URLS:\s*([^\n]+)/i);

        const mentioned = mentionedMatch ? mentionedMatch[1].toLowerCase() === "yes" : false;
        const competitors = competitorMatch
          ? competitorMatch[1].split(",").map(s => s.trim()).filter(s => s && s.toLowerCase() !== "none")
          : [];
        const sources = sourceMatch
          ? sourceMatch[1].split(",").map(s => s.trim()).filter(s => s && s.toLowerCase() !== "none")
          : [];

        // Clean the answer (strip the metadata lines)
        const answer = fullText
          .replace(/MENTIONED:.*$/im, "")
          .replace(/COMPETITOR_NAMES:.*$/im, "")
          .replace(/SOURCE_URLS:.*$/im, "")
          .trim();

        results.push({ query: q, answer, mentioned, competitors, sources });
      } catch {
        results.push({ query: q, answer: "Search unavailable - API error.", mentioned: false, competitors: [], sources: [] });
      }

      await new Promise(r => setTimeout(r, 300));
    }

    setSnippetProgress(100);
    setSnippetLabel("Done");
    const mentionCount = results.filter(r => r.mentioned).length;
    const newScan = { results, date: new Date().toISOString(), mentionCount, total: results.length };
    setScan(newScan);
    LS.set("ai_snippets_" + client.id, newScan);
    // Auto-set baseline if none exists
    const existingBaseline = LS.get("ai_snippets_baseline_" + client.id);
    if (!existingBaseline) {
      setBaseline(newScan);
      LS.set("ai_snippets_baseline_" + client.id, newScan);
    }
    setSnippetPhase("done");
  };

  const setAsBaseline = () => {
    if (!scan) return;
    setBaseline(scan);
    LS.set("ai_snippets_baseline_" + client.id, scan);
  };

  const mentionedCount = snippets.filter(s => s.mentioned).length;
  const allCompetitors = [...new Set(snippets.flatMap(s => s.competitors))].filter(Boolean);
  const baselineMentions = baseline?.mentionCount ?? null;
  const delta = baselineMentions !== null ? mentionedCount - baselineMentions : null;

  // Look up whether this query was mentioned in baseline for before/after badge
  const baselineMap = {};
  (baseline?.results || []).forEach(b => { baselineMap[b.query] = b.mentioned; });

  return (
    <div style={{ padding: "0 0 4px" }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontWeight: 800, color: T.text }}>AI Search Visibility</span>
          {snippets.length > 0 && (
            <span style={{ fontSize: 12, color: T.ghost }}>
              {mentionedCount}/{snippets.length} queries - business appeared
            </span>
          )}
          {/* Delta vs baseline */}
          {delta !== null && snippets.length > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 800, padding: "2px 9px", borderRadius: 6,
              background: delta > 0 ? `${T.teal}18` : delta < 0 ? `${T.red}18` : `${T.ghost}18`,
              color: delta > 0 ? T.teal : delta < 0 ? T.red : T.ghost,
            }}>
              {delta > 0 ? `▲ +${delta}` : delta < 0 ? `▼ ${delta}` : "→ No change"} vs baseline
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {scan && (
            <button onClick={setAsBaseline}
              style={{ fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 8,
                border: `1px solid ${T.border}`, background: "transparent", color: T.ghost, cursor: "pointer", fontFamily: "inherit" }}
              title="Save current results as the new baseline for future comparisons">
              📌 Set as Baseline
            </button>
          )}
          <button onClick={runSnippetSearch} disabled={snippetPhase === "running"}
            style={{
              background: snippetPhase === "running" ? T.input : `linear-gradient(135deg,${T.blue},#0E8FAA)`,
              border: "none", borderRadius: 10, padding: "7px 16px", fontSize: 12, fontWeight: 700,
              color: snippetPhase === "running" ? T.ghost : "#060C0F", cursor: snippetPhase === "running" ? "not-allowed" : "pointer",
              fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6,
            }}>
            {snippetPhase === "running"
              ? <><span className="spin" style={{ width: 11, height: 11, border: `2px solid ${T.ghost}44`, borderTopColor: T.ghost, borderRadius: "50%", display: "inline-block" }} /> Scanning…</>
              : snippets.length > 0 ? "↺ Re-scan" : "Scan AI Engines"}
          </button>
        </div>
      </div>

      {/* Baseline info strip */}
      {baseline && (
        <div style={{ padding: "8px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", gap: 24, fontSize: 11, color: T.ghost }}>
          <span>📍 Baseline: <strong style={{ color: T.text }}>{baseline.mentionCount}/{baseline.total}</strong> mentions - {new Date(baseline.date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</span>
          {scan && scan.date !== baseline.date && (
            <span>🔄 Latest: <strong style={{ color: T.text }}>{mentionedCount}/{snippets.length}</strong> mentions - {new Date(scan.date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</span>
          )}
        </div>
      )}

      {/* Progress */}
      {snippetPhase === "running" && (
        <div style={{ padding: "12px 24px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.ghost, marginBottom: 6 }}>
            <span>{snippetLabel}</span>
            <span style={{ color: T.blue, fontWeight: 700 }}>{snippetProgress}%</span>
          </div>
          <div style={{ height: 4, background: T.border, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${snippetProgress}%`, background: `linear-gradient(90deg,${T.blue},${T.blue})`, transition: "width 0.4s ease", borderRadius: 3 }} />
          </div>
        </div>
      )}

      {/* Idle state */}
      {snippetPhase === "idle" && snippets.length === 0 && (
        <div style={{ padding: "20px 24px", fontSize: 12, color: T.ghost, lineHeight: 1.7 }}>
          Runs 6 live AI queries - "<em>{client?.name}</em> reviews", "best {client?.businessType || "business"} in {client?.city || "area"}", "alternatives to {client?.name}", and more. Shows exactly what AI engines say, whether you appear, and which competitors rank instead. First scan sets the baseline - every re-scan shows your progress.
        </div>
      )}

      {/* Competitor summary banner */}
      {snippets.length > 0 && allCompetitors.length > 0 && (
        <div style={{ margin: "12px 24px 0", background: `${T.red}08`, border: `1px solid ${T.red}22`, borderRadius: 10, padding: "10px 14px", fontSize: 12 }}>
          <span style={{ color: T.red, fontWeight: 700 }}>⚠ Competitors appearing instead: </span>
          <span style={{ color: T.text }}>{allCompetitors.join(", ")}</span>
        </div>
      )}

      {/* Snippets */}
      {snippets.length > 0 && (
        <div style={{ padding: "12px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
          {snippets.map((s, i) => {
            const wasInBaseline = baselineMap[s.query];
            const improved   = !wasInBaseline && s.mentioned;
            const regressed  = wasInBaseline && !s.mentioned;
            const unchanged  = wasInBaseline === s.mentioned;
            return (
              <div key={i} style={{
                background: s.mentioned ? `${T.teal}07` : `${T.red}05`,
                border: `1px solid ${s.mentioned ? T.teal + "33" : T.red + "22"}`,
                borderRadius: 10, padding: "12px 16px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.ghost} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.ghost, fontStyle: "italic" }}>"{s.query}"</span>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                    {/* Before/after badge (only if baseline exists for this query) */}
                    {baseline && wasInBaseline !== undefined && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                        background: improved ? `${T.teal}25` : regressed ? `${T.red}25` : `${T.ghost}15`,
                        color: improved ? T.teal : regressed ? T.red : T.ghost }}>
                        {improved ? "▲ NEW WIN" : regressed ? "▼ LOST" : "→ SAME"}
                      </span>
                    )}
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 5,
                      background: s.mentioned ? `${T.teal}20` : `${T.red}18`,
                      color: s.mentioned ? T.teal : T.red,
                      border: `1px solid ${s.mentioned ? T.teal + "44" : T.red + "33"}`,
                    }}>
                      {s.mentioned ? "✓ MENTIONED" : "✗ NOT FOUND"}
                    </span>
                  </div>
                </div>
                <p style={{ fontSize: 12, color: T.text, lineHeight: 1.65, margin: "0 0 8px", whiteSpace: "pre-wrap" }}>
                  {s.answer.slice(0, 420)}{s.answer.length > 420 ? "…" : ""}
                </p>
                {s.competitors.length > 0 && (
                  <div style={{ fontSize: 11, marginBottom: 6 }}>
                    <span style={{ color: T.gold, fontWeight: 700 }}>Competitors listed: </span>
                    {s.competitors.map((c, ci) => (
                      <span key={ci} style={{ background: `${T.gold}18`, color: T.gold, borderRadius: 5, padding: "1px 7px", marginRight: 5, fontSize: 11, fontWeight: 600 }}>{c}</span>
                    ))}
                  </div>
                )}
                {s.sources.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {s.sources.map((url, ui) => {
                      let displayUrl = url;
                      try { displayUrl = new URL(url).hostname.replace("www.", ""); } catch {}
                      return (
                        <a key={ui} href={url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 10, color: T.blue, textDecoration: "none", background: `${T.blue}12`, padding: "2px 8px", borderRadius: 5 }}>
                          ↗ {displayUrl}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------
// FAQ CODE INJECTOR
// Generates business-specific FAQs via OpenAI and injects FAQPage schema via GTM
// ---------------------------------------------
function FAQSchemaInjector({ client }) {
  const [faqs, setFaqs] = useState(() => LS.get("faqs_" + client?.id) || []);
  const [generating, setGenerating] = useState(false);
  const [injecting, setInjecting]   = useState(false);
  const [injectStatus, setInjectStatus] = useState("idle"); // idle | done | error
  const [injectMsg, setInjectMsg]   = useState("");
  const [editIdx, setEditIdx]       = useState(null);
  const [editQ, setEditQ]           = useState("");
  const [editA, setEditA]           = useState("");

  const generateFAQs = async () => {
    if (!client?.name) return;
    setGenerating(true);
    try {
      const prompt = `You are an SEO expert generating FAQ schema for a local business.

Business: ${client.name}
Type: ${client.businessType || "local business"}
City: ${client.city || "unknown"}
Domain: ${client.domain || ""}
Phone: ${client.phone || ""}

Generate exactly 8 FAQs real customers ask this type of business. Focus on:
- Pricing ("How much does X cost?")
- Services offered ("Do you offer X?")
- Location/hours ("Where are you located?", "What are your hours?")
- Experience/qualifications ("How long have you been in business?")
- Process ("What happens during a consultation?")
- Local relevance ("Do you serve [city/area]?")

Rules:
- Answers must be 1-3 sentences, specific to this business type
- Do NOT hallucinate specific prices or guarantees
- Use placeholders like "[call for pricing]" for unknown details
- Output ONLY a JSON array, no markdown, no explanation:
[{"q":"question","a":"answer"}, ...]`;

      const resp = await claudeAPI({
        max_tokens: 1200,
        messages: [{ role: "user", content: prompt }],
      });
      const text = (resp.content?.[0]?.text || "").trim();
      const clean = text.replace(/^```(?:json)?\s*/i, "").replace(/\s*```\s*$/, "");
      const parsed = JSON.parse(clean);
      if (Array.isArray(parsed) && parsed.length) {
        setFaqs(parsed);
        LS.set("faqs_" + client.id, parsed);
      }
    } catch (e) {
      alert("FAQ generation failed: " + e.message);
    }
    setGenerating(false);
  };

  const startEdit = (i) => { setEditIdx(i); setEditQ(faqs[i].q); setEditA(faqs[i].a); };
  const saveEdit  = () => {
    const updated = faqs.map((f, i) => i === editIdx ? { q: editQ, a: editA } : f);
    setFaqs(updated);
    LS.set("faqs_" + client.id, updated);
    setEditIdx(null);
  };
  const removeFAQ = (i) => {
    const updated = faqs.filter((_, fi) => fi !== i);
    setFaqs(updated);
    LS.set("faqs_" + client.id, updated);
  };
  const addFAQ = () => {
    const updated = [...faqs, { q: "New question?", a: "Answer here." }];
    setFaqs(updated);
    LS.set("faqs_" + client.id, updated);
    setEditIdx(updated.length - 1);
    setEditQ("New question?"); setEditA("Answer here.");
  };

  const injectFAQs = async () => {
    if (!faqs.length) return;
    setInjecting(true); setInjectStatus("idle"); setInjectMsg("");

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": `${client.name} - Frequently Asked Questions`,
      "mainEntity": faqs.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    };

    // Record in localStorage for reports regardless of injection method
    const logEntry = { date: new Date().toISOString(), count: faqs.length, status: "Injected" };
    const existingLog = LS.get("faq_injections_" + client.id) || [];
    LS.set("faq_injections_" + client.id, [logEntry, ...existingLog].slice(0, 20));

    // Try Electron IPC first (full GTM push), fall back to clipboard copy
    if (window.sharkOS?.invoke) {
      try {
        const result = await window.sharkOS.invoke("inject-podcast-schema", {
          clientId: client.id,
          clientData: { ...client },
          schemaBlocks: [faqSchema],
          episodeTitle: `FAQ Schema - ${client.name}`,
        });
        if (result.success && result.injected > 0) {
          setInjectStatus("done");
          setInjectMsg(`✓ FAQ schema live in GTM - ${result.injected} block published.`);
        } else {
          setInjectStatus("error");
          setInjectMsg(result.error || (result.results?.find(r => r.error)?.error) || "Injection failed - check GTM auth.");
        }
      } catch (e) {
        setInjectStatus("error"); setInjectMsg(e.message);
      }
    } else {
      // No bridge - copy the script tag to clipboard so it can be pasted into GTM manually
      const scriptTag = `<script type="application/ld+json">\n${JSON.stringify(faqSchema, null, 2)}\n</script>`;
      try { await navigator.clipboard.writeText(scriptTag); } catch {}
      setInjectStatus("done");
      setInjectMsg("✓ Schema copied to clipboard - paste into a GTM Custom HTML tag and publish.");
    }
    setInjecting(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header card */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 800, color: T.text, fontSize: 14 }}>FAQ Code Injector</div>
            <div style={{ fontSize: 11, color: T.ghost, marginTop: 2 }}>
              Generates FAQPage schema.org markup and injects it into GTM - no website access needed.
              AI engines pull FAQ answers directly from structured data.
            </div>
            {injectMsg && (
              <div style={{ marginTop: 5, fontSize: 12, fontWeight: 600,
                color: injectStatus === "done" ? T.teal : T.red }}>
                {injectMsg}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={addFAQ}
              style={{ fontSize: 12, fontWeight: 700, padding: "7px 14px", borderRadius: 9, border: `1px solid ${T.border}`,
                background: "transparent", color: T.ghost, cursor: "pointer", fontFamily: "inherit" }}>
              + Add FAQ
            </button>
            <button onClick={generateFAQs} disabled={generating}
              style={{ fontSize: 12, fontWeight: 700, padding: "7px 16px", borderRadius: 9, border: "none",
                background: generating ? T.input : `linear-gradient(135deg,${T.blue},#a855f7)`,
                color: generating ? T.ghost : "#fff", cursor: generating ? "not-allowed" : "pointer", fontFamily: "inherit",
                display: "flex", alignItems: "center", gap: 6 }}>
              {generating
                ? <><span className="spin" style={{ width: 11, height: 11, border: `2px solid rgba(255,255,255,0.3)`, borderTopColor: "#fff", borderRadius: "50%" }} /> Generating…</>
                : faqs.length > 0 ? "↺ Regenerate" : "✦ Generate FAQs"}
            </button>
            <button onClick={injectFAQs} disabled={injecting || faqs.length === 0}
              style={{ fontSize: 12, fontWeight: 800, padding: "7px 18px", borderRadius: 9, border: "none",
                background: injecting || faqs.length === 0 ? T.input
                  : injectStatus === "done" ? `linear-gradient(135deg,${T.teal},#4FD1A0)`
                  : `linear-gradient(135deg,${T.blue},#0E8FAA)`,
                color: injecting || faqs.length === 0 ? T.ghost : "#060C0F",
                cursor: injecting || faqs.length === 0 ? "not-allowed" : "pointer",
                fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
              {injecting
                ? <><span className="spin" style={{ width: 11, height: 11, border: `2px solid rgba(255,255,255,0.3)`, borderTopColor: T.white, borderRadius: "50%" }} /> Injecting…</>
                : injectStatus === "done" ? "✓ Re-inject" : "⚡ Inject to GTM"}
            </button>
          </div>
        </div>

        {/* Empty state */}
        {faqs.length === 0 && !generating && (
          <div style={{ padding: "32px 24px", textAlign: "center", color: T.ghost, fontSize: 13, lineHeight: 1.7 }}>
            No FAQs yet. Click <strong style={{ color: T.text }}>Generate FAQs</strong> to create 8 AI-optimized questions
            based on {client.name}'s business profile, or add them manually.
          </div>
        )}

        {/* FAQ list */}
        {faqs.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {faqs.map((f, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${T.border}`, padding: "14px 24px" }}>
                {editIdx === i ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <input value={editQ} onChange={e => setEditQ(e.target.value)}
                      style={{ background: T.input, border: `1px solid ${T.blue}`, borderRadius: 8, padding: "8px 12px",
                        fontSize: 13, fontWeight: 600, color: T.text, fontFamily: "inherit", outline: "none" }} />
                    <textarea value={editA} onChange={e => setEditA(e.target.value)} rows={3}
                      style={{ background: T.input, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px",
                        fontSize: 12, color: T.text, fontFamily: "inherit", outline: "none", resize: "vertical" }} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={saveEdit}
                        style={{ fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 7, border: "none",
                          background: T.teal, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                      <button onClick={() => setEditIdx(null)}
                        style={{ fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 7,
                          border: `1px solid ${T.border}`, background: "transparent", color: T.ghost, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>Q: {f.q}</div>
                      <div style={{ fontSize: 12, color: T.ghost, lineHeight: 1.6 }}>A: {f.a}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => startEdit(i)}
                        style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: `1px solid ${T.border}`,
                          background: "transparent", color: T.ghost, cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                      <button onClick={() => removeFAQ(i)}
                        style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: `1px solid ${T.red}33`,
                          background: "transparent", color: T.red, cursor: "pointer", fontFamily: "inherit" }}>✕</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

    </div>
  );
}

// ---------------------------------------------
// GTM ACCESS PANEL
// Seamless 2-step flow: copy access request → connect Google account → verified
// ---------------------------------------------
function GtmAccessPanel({ client, clientGtm }) {
  const settings = LS.get("settings") || {};
  const myEmail = settings.userGoogleEmail || "";

  const [status, setStatus] = useState(() => {
    if (!clientGtm) return "no_container";
    return LS.get("gtm_connected_" + client.id) || "no_access";
  });
  const [verifying, setVerifying] = useState(false);
  const [error, setError]   = useState("");
  const [copied, setCopied] = useState(false);

  // Auto-verify the moment the OAuth browser tab completes
  useEffect(() => {
    if (!window.sharkOS?.on) return;
    const unsub = window.sharkOS.on("gtm-auth-complete", async (data) => {
      if (data.clientId !== client.id || data.scope !== "gtm") return;
      setStatus("verifying");
      setError("");
      try {
        const r = await window.sharkOS.invoke("verify-gtm-access", { clientId: client.id, clientData: client });
        if (r.success) { setStatus("connected"); LS.set("gtm_connected_" + client.id, "connected"); }
        else           { setStatus("no_access"); setError(r.error || "Access check failed."); }
      } catch (e) { setStatus("no_access"); setError(e.message); }
    });
    return unsub;
  }, [client]);

  const msgText = [
    `Subject: GTM Publisher Access Request - ${client.name}`,
    ``,
    `Hi,`,
    ``,
    `I manage digital visibility for ${client.name}. To deploy structured schema markup through Google Tag Manager (which improves rankings in AI search - ChatGPT, Perplexity, Google AI Overviews), I need Publisher access to their GTM container: ${clientGtm || "[GTM-XXXXXXX]"}.`,
    ``,
    `Please add this Google account as Publisher:`,
    `  ${myEmail || "[Add your Google email in SharkOS Settings → Google API Setup]"}`,
    ``,
    `Steps:`,
    `  1. Sign in at tagmanager.google.com`,
    `  2. Select the ${client.name} account`,
    `  3. Admin tab → User Management → Add User`,
    `  4. Email: ${myEmail || "[your email]"} - Permission: Publish`,
    `  5. Click Add`,
    ``,
    `Once added, I can deploy the markup immediately.`,
    ``,
    `Thank you`,
  ].join("\n");

  const copyMsg = () => {
    navigator.clipboard.writeText(msgText);
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  };

  const connect = async () => {
    if (!window.sharkOS?.invoke) { setError("Run from Electron (npm run dev from geo-optimizer/)."); return; }
    setStatus("waiting_auth"); setError("");
    try {
      await window.sharkOS.invoke("trigger-gtm-oauth", { clientId: client.id, scope: "gtm" });
      // gtm-auth-complete listener above fires when the browser tab finishes
    } catch (e) { setStatus("no_access"); setError(e.message); }
  };

  const checkAccess = async () => {
    if (!window.sharkOS?.invoke) { setError("Run from Electron."); return; }
    setVerifying(true); setError("");
    try {
      const r = await window.sharkOS.invoke("verify-gtm-access", { clientId: client.id, clientData: client });
      if (r.success) { setStatus("connected"); LS.set("gtm_connected_" + client.id, "connected"); }
      else           { setStatus("no_access"); setError(r.error || "Access not verified."); }
    } catch (e) { setStatus("no_access"); setError(e.message); }
    setVerifying(false);
  };

  const isConnected = status === "connected";
  const isWaiting   = status === "waiting_auth";
  const isBusy      = status === "verifying" || verifying;

  return (
    <div style={{background:T.white,borderRadius:16,border:`1px solid ${isConnected?T.teal:T.border}`,overflow:"hidden",
      boxShadow:isConnected?`0 0 0 1px ${T.teal}33`:"none"}}>
      {/* Header */}
      <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontWeight:800,color:T.text}}>Code Injection - GTM</span>
        {!clientGtm
          ? <span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:`${T.gold}18`,color:T.gold,fontWeight:700}}>NO CONTAINER SET</span>
          : isConnected
            ? <span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:`${T.teal}18`,color:T.teal,fontWeight:700}}>✓ CONNECTED - {clientGtm}</span>
            : <span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:`${T.blue}15`,color:T.blue,fontWeight:700}}>{clientGtm}</span>}
      </div>

      {!clientGtm && (
        <div style={{padding:"20px 24px",fontSize:13,color:T.ghost}}>
          Edit this client and enter the GTM Container ID (GTM-XXXXXXX) to enable seamless code injection.
        </div>
      )}

      {clientGtm && (
        <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:20}}>

          {/* Connected banner */}
          {isConnected && (
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderRadius:10,
              background:`${T.teal}10`,border:`1px solid ${T.teal}33`}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:T.teal,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:T.teal}}>Publisher access verified</div>
                <div style={{fontSize:11,color:T.ghost,marginTop:2}}>Schema tags will inject into {clientGtm} on next run.</div>
              </div>
              <button onClick={checkAccess} disabled={isBusy}
                style={{fontSize:11,padding:"5px 12px",borderRadius:7,border:`1px solid ${T.border}`,
                  background:"transparent",color:T.ghost,cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>
                {isBusy?"Checking...":"Re-verify"}
              </button>
            </div>
          )}

          {/* Step 1 - request access */}
          {!isConnected && (
            <div>
              <div style={{fontSize:11,fontWeight:800,color:T.ghost,letterSpacing:1,marginBottom:8}}>STEP 1 - SEND ACCESS REQUEST TO GTM OWNER</div>
              <div style={{fontSize:12,color:T.ghost,marginBottom:12,lineHeight:1.5}}>
                Copy this message and send it to whoever owns the GTM account for <strong style={{color:T.text}}>{client.name}</strong>. They need to add your Google account as a <strong style={{color:T.text}}>Publisher</strong>.
              </div>
              <div style={{background:T.input,borderRadius:10,border:`1px solid ${T.border}`,padding:14,marginBottom:10}}>
                <pre style={{fontSize:11,color:T.text,margin:0,lineHeight:1.65,whiteSpace:"pre-wrap",fontFamily:"inherit"}}>{msgText}</pre>
              </div>
              {!myEmail && (
                <div style={{fontSize:11,color:T.gold,marginBottom:8}}>
                  ⚠ Add your Google account email in Settings → Google API Setup so it appears in the message above.
                </div>
              )}
              <button onClick={copyMsg}
                style={{fontSize:12,padding:"7px 18px",borderRadius:9,border:`1px solid ${copied?T.teal:T.blue}`,
                  background:copied?`${T.teal}15`:`${T.blue}12`,color:copied?T.teal:T.blue,
                  cursor:"pointer",fontWeight:700,transition:"all 0.2s"}}>
                {copied?"✓ Copied to Clipboard":"Copy Access Request"}
              </button>
            </div>
          )}

          {/* Step 2 - connect */}
          {!isConnected && (
            <div>
              <div style={{fontSize:11,fontWeight:800,color:T.ghost,letterSpacing:1,marginBottom:8}}>STEP 2 - CONNECT YOUR GOOGLE ACCOUNT</div>
              <div style={{fontSize:12,color:T.ghost,marginBottom:12,lineHeight:1.5}}>
                Once the GTM owner has granted access, click Connect. Sign in with the Google account they added as Publisher. The app verifies access automatically.
              </div>
              <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                <button onClick={connect} disabled={isWaiting||isBusy}
                  style={{fontSize:13,padding:"9px 24px",borderRadius:10,border:"none",
                    background:isWaiting?T.border:`linear-gradient(135deg,${T.blue},#0E8FAA)`,
                    color:isWaiting?T.ghost:"#060C0F",cursor:isWaiting?"not-allowed":"pointer",
                    fontWeight:800,transition:"all 0.2s",boxShadow:isWaiting?"none":"0 2px 12px rgba(21,180,214,0.3)"}}>
                  {isBusy?"Verifying...":(isWaiting?"Waiting for browser auth...":"Connect GTM")}
                </button>
                {isWaiting && (
                  <button onClick={checkAccess}
                    style={{fontSize:12,padding:"8px 18px",borderRadius:9,border:`1px solid ${T.teal}`,
                      background:`${T.teal}12`,color:T.teal,cursor:"pointer",fontWeight:700}}>
                    {verifying?"Verifying...":"I authorized - Check Access"}
                  </button>
                )}
              </div>
            </div>
          )}

          {error && (
            <div style={{fontSize:12,color:T.red,padding:"10px 14px",borderRadius:9,
              background:`${T.red}10`,border:`1px solid ${T.red}33`,lineHeight:1.5}}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function AuditTab({auditResults,auditing,clientId,client,geoData,crawlData,clientGtm}){
  const data = auditResults || AUDIT_DATA.map(plat=>({
    ...plat,
    signals:plat.isCitations?[]:plat.signals.map(s=>({
      signal:s.signal,
      impact:s.impact,
      status:"Fail",
      finding:s.findings.Fail,
    }))
  }));
  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>

{auditing&&(
        <div style={{background:`${T.blue}10`,border:`1px solid ${T.blue}33`,borderRadius:12,padding:"12px 18px",
          display:"flex",alignItems:"center",gap:10,fontSize:13,color:T.blue,fontWeight:600}}>
          <span className="spin" style={{width:14,height:14,border:`2px solid ${T.blue}33`,borderTopColor:T.blue,borderRadius:"50%",flexShrink:0}}/>
          Crawling site, scanning citations, AI mentions, and GBP signals
        </div>
      )}
      {auditResults&&!auditing&&(
        <div style={{background:`${T.teal}12`,border:`1px solid ${T.teal}44`,borderRadius:12,padding:"10px 18px",
          fontSize:13,color:T.teal,fontWeight:600,display:"flex",alignItems:"center",gap:8}}>
          ? Audit complete  {new Date().toLocaleTimeString()}
        </div>
      )}

      {/* -- Site Crawl Summary -- */}
      {crawlData&&(
        <Card style={{padding:0,overflow:"hidden"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 24px",borderBottom:`1px solid ${T.border}`}}>
            <span style={{fontWeight:800,color:T.text}}>Site Crawl</span>
            <span style={{fontSize:11,color:crawlData.success?T.teal:T.gold,fontWeight:700,
              padding:"3px 10px",borderRadius:8,background:crawlData.success?`${T.teal}18`:`${T.gold}18`}}>
              {crawlData.success
                ? `✓ ${crawlData.jsonLdBlockCount} JSON-LD block${crawlData.jsonLdBlockCount!==1?"s":""} found`
                : `⚠ Could not reach ${crawlData.domain} - run via Electron (npm run dev) for full crawl access.`}
            </span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:0}}>
            {[
              {label:"LocalBusiness Schema", r:crawlData.localBusiness},
              {label:"Service Schema",       r:crawlData.serviceSchema},
              {label:"FAQ Schema",           r:crawlData.faqSchema},
              {label:"Review Schema",        r:crawlData.reviewSchema},
              {label:"Content Freshness",    r:crawlData.contentFreshness},
              {label:"Sitemap",              r:crawlData.sitemap},
              {label:"Q&A Content",          r:crawlData.qaContent},
              {label:"Geo Coordinates",      r:crawlData.geoCoordinates},
            ].map(({label,r})=>{
              if(!r) return null;
              const st=SSTYLE[r.status]||SSTYLE.Warning;
              return(
                <div key={label} style={{padding:"12px 24px",borderBottom:`1px solid ${T.border}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <div style={{width:7,height:7,borderRadius:"50%",background:st.dot,flexShrink:0}}/>
                    <span style={{fontSize:12,fontWeight:700,color:st.text}}>{r.status}</span>
                  </div>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:2}}>{label}</div>
                  <div style={{fontSize:11,color:T.ghost,lineHeight:1.4}}>{r.detail}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {data.map(plat=>(
        <Card key={plat.name} style={{padding:0,overflow:"hidden"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 24px",borderBottom:`1px solid ${T.border}`}}>
            <span style={{fontWeight:800,color:T.text}}>{plat.name}</span>
          </div>
          {plat.isCitations
            ? <AISnippetMonitor client={client}/>
            : (
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
                  {["SIGNAL","STATUS","FINDING","IMPACT"].map(h=><th key={h} style={{textAlign:"left",padding:"10px 24px",fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1.1}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {plat.signals.map((s,i)=>{const st=SSTYLE[s.status];return(
                    <tr key={i} style={{borderBottom:`1px solid ${T.border}`}}
                      onMouseEnter={e=>e.currentTarget.style.background=T.input}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{padding:"12px 24px",fontWeight:600,color:T.text}}>{s.signal}</td>
                      <td style={{padding:"12px 24px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:7}}>
                          <div style={{width:7,height:7,borderRadius:"50%",background:st.dot}}/>
                          <span style={{color:st.text,fontWeight:700}}>{s.status}</span>
                        </div>
                      </td>
                      <td style={{padding:"12px 24px",color:T.ghost}}>{s.finding}</td>
                      <td style={{padding:"12px 24px"}}>
                        <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:8,background:st.bg,color:st.text}}>{s.impact}</span>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            )
          }
        </Card>
      ))}

      {/* ── Crawler Access Section ── */}
      {(geoData?.crawlerMap?.length>0)&&(
        <div style={{background:T.white,borderRadius:16,border:`1px solid ${T.border}`,overflow:"hidden"}}>
          <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontWeight:800,color:T.text}}>AI Crawler Access</span>
            <span style={{fontSize:11,color:T.ghost}}>robots.txt analysis</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10,padding:20}}>
            {geoData.crawlerMap.map(c=>(
              <div key={c.name} style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${T.border}`,
                background:c.status==="Blocked"?`${T.red}08`:c.status==="Allowed"?`${T.teal}08`:`${T.gold}08`}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                  <div style={{width:7,height:7,borderRadius:"50%",flexShrink:0,
                    background:c.status==="Blocked"?T.red:c.status==="Allowed"?T.teal:T.gold}}/>
                  <span style={{fontSize:12,fontWeight:700,color:T.text}}>{c.name}</span>
                </div>
                <div style={{fontSize:10,color:T.ghost}}>{c.owner}</div>
                <div style={{fontSize:10,fontWeight:600,marginTop:3,
                  color:c.status==="Blocked"?T.red:c.status==="Allowed"?T.teal:T.gold}}>{c.status}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── llms.txt Section ── */}
      {geoData&&(
        <div style={{background:T.white,borderRadius:16,border:`1px solid ${T.border}`,overflow:"hidden"}}>
          <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
            <span style={{fontWeight:800,color:T.text}}>llms.txt - AI Readability File</span>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {geoData.llmsData
                ?<span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:`${T.teal}18`,color:T.teal,fontWeight:700}}>✓ FOUND ON SITE</span>
                :<span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:`${T.red}18`,color:T.red,fontWeight:700}}>✗ MISSING</span>}
              <button onClick={()=>{
                const napData = LS.get("nap_"+(geoData._clientId||""))||{};
                const txt = geoData.llmsGenerated || geoData.llmsData || "";
                const blob = new Blob([txt],{type:"text/plain"});
                const a = document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="llms.txt"; a.click(); URL.revokeObjectURL(a.href);
              }} style={{fontSize:11,padding:"5px 14px",borderRadius:8,border:`1px solid ${T.blue}`,
                background:`${T.blue}15`,color:T.blue,cursor:"pointer",fontWeight:700}}>
                ↓ {geoData.llmsData?"Download Existing":"Download Generated"} llms.txt
              </button>
            </div>
          </div>
          <div style={{padding:20}}>
            {geoData.llmsData?(
              <>
                <div style={{fontSize:11,color:T.teal,fontWeight:600,marginBottom:8}}>✓ llms.txt is live on this site. Preview:</div>
                <pre style={{fontSize:11,color:T.text,background:T.input,padding:14,borderRadius:10,overflow:"auto",maxHeight:200,margin:0,lineHeight:1.6}}>{geoData.llmsData.substring(0,800)}{geoData.llmsData.length>800?"...":""}</pre>
              </>
            ):(
              <>
                <div style={{fontSize:12,color:T.gold,fontWeight:600,marginBottom:4}}>
                  ✗ No llms.txt found on this site. SharkOS generated an authority-rich file using live GBP, NAP, and schema data:
                </div>
                <div style={{fontSize:11,color:T.ghost,marginBottom:10}}>
                  Upload this file to the root of {geoData._domain||"the site"} so AI models (ChatGPT, Claude, Perplexity, Google AI) can read and cite this business accurately.
                </div>
                <pre style={{fontSize:11,color:T.text,background:T.input,padding:14,borderRadius:10,overflow:"auto",maxHeight:280,margin:0,lineHeight:1.6}}>{geoData.llmsGenerated||"Run audit to generate..."}</pre>
                <div style={{display:"flex",gap:8,marginTop:10}}>
                  <button onClick={()=>{if(geoData.llmsGenerated){const b=new Blob([geoData.llmsGenerated],{type:"text/plain"});const a=document.createElement("a");a.href=URL.createObjectURL(b);a.download="llms.txt";a.click();URL.revokeObjectURL(a.href);}}}
                    style={{fontSize:12,padding:"6px 16px",borderRadius:8,border:"none",
                      background:`linear-gradient(135deg,${T.blue},${T.blue})`,color:"#fff",cursor:"pointer",fontWeight:700}}>
                    ↓ Download llms.txt
                  </button>
                  <button onClick={()=>navigator.clipboard.writeText(geoData.llmsGenerated||"")}
                    style={{fontSize:12,padding:"6px 16px",borderRadius:8,border:`1px solid ${T.border}`,
                      background:"transparent",color:T.ghost,cursor:"pointer",fontWeight:600}}>
                    Copy to clipboard
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── GTM Access Panel ── */}
      <GtmAccessPanel client={client} clientGtm={clientGtm} />
    </div>
  );
}

// ---------------------------------------------
// CHECKLIST TAB
// ---------------------------------------------
function deriveAutoChecked(clientId, auditResults, gbmData, crawlData, geoData){
  const auto={};
  const getSignal=(name)=>{
    for(const plat of auditResults||[]){
      const s=plat.signals?.find(s=>s.signal===name);
      if(s) return s;
    }
    return null;
  };

  // ── GBP tasks ──
  const gbpVerified = gbmData?.status==="verified" || gbmData?.verified;
  const gbpClaimed  = gbmData?.gbpClaimed || gbpVerified;
  const gbpRating   = parseFloat(gbmData?.rating||0);
  const gbpReviews  = parseInt(gbmData?.reviews||0,10);
  const gbpCategory = !!(gbmData?.category || gbmData?.name);
  if (gbpVerified)           auto.gbp_complete   = true;
  if (gbpVerified)           auto.gbp_categories = true;
  if (gbpReviews >= 1)       auto.gbp_qa         = true; // has activity = Q&A likely started
  if (gbpVerified && gbpReviews >= 4) auto.gbp_post = true; // has engagement = posting likely

  // ── Schema Markup (from crawlData) ──
  if (crawlData?.localBusiness?.status==="Pass") auto.schema_local   = true;
  if (crawlData?.faqSchema?.status==="Pass")     auto.schema_faq     = true;
  if (crawlData?.serviceSchema?.status==="Pass") auto.schema_service  = true;
  if (crawlData?.reviewSchema?.status==="Pass")  auto.schema_review   = true;

  // ── On-Page Content (from crawlData + geoData page checks) ──
  if (crawlData?.qaContent?.status==="Pass")     auto.content_faq    = true;
  if (geoData?.pagesFound?.areas)                auto.content_areas  = true;
  if (crawlData?.h1Optimized?.status==="Pass")   auto.content_h1     = true;
  if (geoData?.pagesFound?.about)                auto.content_about  = true;

  // ── Citation Consistency (from NAP tracker data) ──
  const napData = LS.get("nap_"+clientId)||{};
  const napSources     = ["nap_google","nap_bing","nap_apple","nap_yelp","nap_facebook","nap_bbb"];
  const napAggregators = ["nap_infogroup","nap_localeze","nap_acxiom","nap_foursquare"];
  const napAll         = [...napSources,...napAggregators];
  const napAllChecked  = napAll.filter(id=>napData[id]&&napData[id]!=="unchecked");
  const napTier1Acc    = napSources.filter(id=>napData[id]==="accurate").length;
  const napAggAcc      = napAggregators.filter(id=>napData[id]==="accurate").length;
  const napIssues      = napAllChecked.filter(id=>napData[id]==="inaccurate"||napData[id]==="missing").length;
  const napAccurate    = napAllChecked.filter(id=>napData[id]==="accurate").length;

  if (napAllChecked.length >= 4 && napIssues < 3)                                    auto.cite_audit       = true;
  if (napAccurate >= 8)                                                               auto.cite_sync        = true;
  if (napTier1Acc === 6)                                                              auto.cite_tier1       = true;
  if (napAggAcc   === 4)                                                              auto.cite_aggregators = true;
  const dupes = getSignal("Duplicate Listings");
  if (dupes?.status==="Pass")                                                         auto.cite_duplicates  = true;

  // ── Reviews ──
  if (gbpReviews >= 10 && gbpRating >= 4.0)  auto.review_respond  = true;
  if (gbpReviews >= 1)                        auto.review_monthly  = true; // monitoring active if reviews exist
  if (gbpVerified && gbpReviews >= 5)         auto.review_system   = true; // system in place if accumulating

  // ── Technical GEO ──
  if (geoData?.llmsData)                      auto.tech_llmstxt    = true;
  if (geoData?.sitemapFound || crawlData?.sitemap?.status==="Pass") auto.tech_sitemap = true;
  if (crawlData?.coreWebVitals?.status==="Pass") auto.tech_speed   = true;

  return auto;
}

function ChecklistTab({clientId, auditResults, gbmData, crawlData, geoData, onChecklistChange}){
  const autoChecked = deriveAutoChecked(clientId, auditResults, gbmData, crawlData, geoData);
  const [manualChecked,setManualChecked]=useState(()=>LS.get("checklist_"+clientId)||{});

  // Merge: auto wins for items it can confirm, manual handles the rest
  const checked={...manualChecked,...autoChecked};

  const toggle=async(id)=>{
    // Don't let user uncheck auto-confirmed items
    if(autoChecked[id]) return;
    const next={...manualChecked,[id]:!manualChecked[id]};
    setManualChecked(next);
    await ipc("save-checklist",{clientId,state:next});
    onChecklistChange?.({...next,...autoChecked});
  };

  const done=CHECKLIST_CATS.reduce((a,c)=>a+c.items.filter(i=>checked[i.id]).length,0);
  const delivered=CHECKLIST_CATS.reduce((a,c)=>a+c.items.filter(i=>checked[i.id]).reduce((b,i)=>b+i.price,0),0);
  const pct=Math.round((done/TOTAL_TASKS)*100);
  const autoCount=Object.keys(autoChecked).length;

  const downloadLlms = () => {
    const napData = LS.get("nap_"+clientId)||{};
    const txt = geoData?.llmsGenerated || buildRichLlmsTxt(
      {id:clientId}, gbmData, napData, crawlData, geoData
    );
    const blob = new Blob([txt], {type:"text/plain"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "llms.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Scan source labels for auto-detected items
  const autoSources = {
    gbp_complete:"GBP sync", gbp_categories:"GBP sync", gbp_post:"GBP data", gbp_qa:"GBP data",
    schema_local:"Site crawl", schema_faq:"Site crawl", schema_service:"Site crawl", schema_review:"Site crawl",
    content_faq:"Site crawl", content_areas:"Page scan", content_h1:"Site crawl", content_about:"Page scan",
    cite_audit:"NAP tracker", cite_sync:"NAP tracker", cite_tier1:"NAP tracker",
    cite_aggregators:"NAP tracker", cite_duplicates:"Audit signal",
    review_system:"GBP data", review_respond:"GBP data", review_monthly:"GBP data",
    tech_llmstxt:"Site crawl", tech_sitemap:"Site crawl", tech_speed:"Site crawl",
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {/* Status banner */}
      {autoCount>0?(
        <div style={{background:`${T.teal}10`,border:`1px solid ${T.teal}33`,borderRadius:12,
          padding:"12px 18px",fontSize:12,color:T.teal,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
          <span>🔍 {autoCount}/{TOTAL_TASKS} tasks auto-detected from live scan - items with <span style={{background:`${T.teal}20`,padding:"1px 6px",borderRadius:4,fontSize:10}}>SCAN</span> badge were verified automatically</span>
          <button onClick={downloadLlms}
            style={{background:`linear-gradient(135deg,${T.blue},${T.blue})`,border:"none",borderRadius:8,
              padding:"6px 14px",fontSize:11,fontWeight:800,color:"#fff",cursor:"pointer",whiteSpace:"nowrap",letterSpacing:0.3}}>
            ↓ Download llms.txt
          </button>
        </div>
      ):(
        <div style={{background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:12,
          padding:"10px 18px",fontSize:12,color:T.gold,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
          <span>⚡ Run an audit to auto-detect completed tasks from live site scan, GBP data, and NAP tracker</span>
          <button onClick={downloadLlms}
            style={{background:`${T.gold}20`,border:`1px solid ${T.gold}44`,borderRadius:8,
              padding:"6px 14px",fontSize:11,fontWeight:800,color:T.gold,cursor:"pointer",whiteSpace:"nowrap"}}>
            ↓ Generate llms.txt
          </button>
        </div>
      )}

      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div>
            <span style={{fontSize:28,fontWeight:800,color:T.text}}>{done}/{TOTAL_TASKS}</span>
            <span style={{fontSize:14,color:T.ghost,marginLeft:8}}>{pct}% complete</span>
          </div>
          {autoCount>0&&<div style={{fontSize:12,color:T.ghost}}>
            <span style={{color:T.teal,fontWeight:700}}>{autoCount}</span> auto-detected &nbsp;·&nbsp;
            <span style={{color:T.blue,fontWeight:700}}>{done-autoCount}</span> manual
          </div>}
        </div>
        <div style={{height:10,background:T.border,borderRadius:6,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,${T.blue},${T.blue})`,transition:"width 0.5s ease",borderRadius:6}}/>
        </div>
      </Card>
      {CHECKLIST_CATS.map(cat=>{
        const catDone=cat.items.filter(i=>checked[i.id]).length;
        const catAuto=cat.items.filter(i=>autoChecked[i.id]).length;
        return(
          <Card key={cat.name} style={{padding:0,overflow:"hidden"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 24px",borderBottom:`1px solid ${T.border}`}}>
              <span style={{fontWeight:800,color:T.text}}>{cat.name}</span>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {catAuto>0&&<span style={{fontSize:10,color:T.teal,fontWeight:700,background:`${T.teal}15`,padding:"2px 8px",borderRadius:6}}>
                  🔍 {catAuto} scanned
                </span>}
                <span style={{fontSize:12,color:catDone===cat.items.length?T.teal:T.ghost,fontWeight:catDone===cat.items.length?700:400,
                  background:catDone===cat.items.length?`${T.teal}18`:T.input,padding:"3px 10px",borderRadius:20,transition:"all 0.2s"}}>
                  {catDone}/{cat.items.length}
                </span>
              </div>
            </div>
            {cat.items.map(item=>{
              const ck=!!checked[item.id];
              const isAuto=!!autoChecked[item.id];
              const srcLabel = autoSources[item.id] || "scan";
              return(
                <div key={item.id} onClick={()=>toggle(item.id)}
                  style={{display:"flex",alignItems:"center",gap:14,padding:"12px 24px",
                    cursor:isAuto?"default":"pointer",
                    background:ck?`${T.teal}08`:"transparent",borderBottom:`1px solid ${T.border}`,transition:"background 0.12s"}}
                  onMouseEnter={e=>{if(!ck&&!isAuto)e.currentTarget.style.background=T.input;}}
                  onMouseLeave={e=>e.currentTarget.style.background=ck?`${T.teal}08`:"transparent"}>
                  <div className={ck&&!isAuto?"check-pop":""}
                    style={{width:22,height:22,borderRadius:6,
                      border:`2px solid ${ck?"transparent":T.border}`,
                      background:ck?isAuto?`linear-gradient(135deg,${T.teal},#4FD1A0)`:`linear-gradient(135deg,${T.blue},${T.blue})`:"transparent",
                      display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
                    {ck&&<span style={{color:T.white,fontSize:11,fontWeight:800}}>✓</span>}
                  </div>
                  <span style={{flex:1,fontSize:13,fontWeight:600,color:ck?T.ghost:T.text,textDecoration:ck?"line-through":"none"}}>{item.task}</span>
                  {isAuto&&ck&&<span style={{fontSize:10,color:T.teal,fontWeight:700,background:`${T.teal}15`,padding:"2px 7px",borderRadius:6,whiteSpace:"nowrap"}}>🔍 {srcLabel}</span>}
                  <span style={{fontSize:11,color:T.ghost,background:T.input,padding:"3px 10px",borderRadius:8,whiteSpace:"nowrap"}}>{item.time}</span>
                </div>
              );
            })}
          </Card>
        );
      })}
    </div>
  );
}

// ---------------------------------------------
// IN-APP REPORT VIEWER  (full proof version)
// ---------------------------------------------
function buildReportHTML(r, client){
  const completed   = r.completedTasks || [];
  const grade       = r.score>=90?"A":r.score>=80?"B":r.score>=70?"C":r.score>=60?"D":"F";
  const gradeCol    = r.score>=90?"#00C853":r.score>=75?"#69D025":r.score>=60?"#FFB547":r.score>=45?"#FF6D00":"#E31A1A";
  const month       = `${r.month} ${r.year}`;
  const circumference = 2 * Math.PI * 54;
  const dash        = circumference - (r.score / 100) * circumference;

  // Pull live data snapshots from localStorage at report-generation time
  const snippetScan  = LS.get("ai_snippets_" + client.id);
  const snippets     = snippetScan?.results || [];
  const baseline     = LS.get("ai_snippets_baseline_" + client.id);
  const baselineMap  = {};
  (baseline?.results || []).forEach(b => { baselineMap[b.query] = b.mentioned; });
  const mentionedNow = snippets.filter(s => s.mentioned).length;
  const mentionedBase= baseline?.mentionCount ?? null;
  const delta        = mentionedBase !== null ? mentionedNow - mentionedBase : null;
  const allCompetitors = [...new Set(snippets.flatMap(s => s.competitors))].filter(Boolean);

  const faqInjections  = LS.get("faq_injections_" + client.id) || [];
  const eventInj       = LS.get("event_injections_" + client.id) || [];
  const podcastInj     = LS.get("podcast_injections_" + client.id) || [];
  const allInjections  = [
    ...faqInjections.map(x => ({ ...x, schemaType: "FAQPage", name: "FAQ Schema" })),
    ...eventInj.map(x  => ({ date: x.ts || x.date, name: x.event_name, schemaType: x.schema_type || "Event", status: x.status })),
    ...podcastInj.map(x=> ({ date: x.ts, name: x.name, schemaType: x.schema_type || "Podcast", status: "Injected" })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 20);

  const faqs = LS.get("faqs_" + client.id) || [];

  // Previous score from report history
  const allReports = LS.get("reports_" + client.id) || [];
  const prevReport = allReports.find(rr => rr.id !== r.id);
  const prevScore  = prevReport?.score ?? null;
  const scoreDelta = prevScore !== null ? r.score - prevScore : null;

  // Helpers
  const pct = n => `${Math.round(n*100)}%`;
  const fmtDate = iso => { try { return new Date(iso).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}); } catch { return iso || "-"; } };
  const pill = (text, col, bg) => `<span style="font-size:10px;font-weight:800;padding:2px 9px;border-radius:5px;background:${bg};color:${col}">${text}</span>`;

  const snippetRows = snippets.map(s => {
    const wasInBase = baselineMap[s.query];
    const changed = wasInBase !== undefined;
    const improved = !wasInBase && s.mentioned;
    const regressed = wasInBase && !s.mentioned;
    const changeBadge = changed
      ? improved  ? pill("▲ NEW WIN","#00C853","rgba(0,200,83,0.15)")
      : regressed ? pill("▼ LOST","#E31A1A","rgba(227,26,26,0.12)")
                  : pill("→ SAME","#8898BB","rgba(136,152,187,0.15)")
      : "";
    return `<tr>
      <td style="padding:9px 12px;font-size:12px;color:#2A3344;font-style:italic">"${s.query}"</td>
      <td style="padding:9px 12px;text-align:center">${s.mentioned
        ? pill("✓ MENTIONED","#00C853","rgba(0,200,83,0.12)")
        : pill("✗ NOT FOUND","#E31A1A","rgba(227,26,26,0.1)")}</td>
      <td style="padding:9px 12px;text-align:center">${mentionedBase!==null
        ? (wasInBase!==undefined ? pill(wasInBase?"Yes":"No", wasInBase?"#00C853":"#cc4444", wasInBase?"rgba(0,200,83,0.1)":"rgba(200,0,0,0.08)") : "-")
        : "-"}</td>
      <td style="padding:9px 12px;text-align:center">${changeBadge}</td>
      <td style="padding:9px 12px;font-size:11px;color:#667788">${s.competitors.slice(0,3).join(", ")||"None detected"}</td>
    </tr>`;
  }).join("");

  const injectionRows = allInjections.length ? allInjections.map(inj => `<tr>
    <td style="padding:9px 12px;font-size:12px;font-weight:700;color:#2A3344">${inj.name || "-"}</td>
    <td style="padding:9px 12px;font-size:12px;color:#667788">${inj.schemaType || "-"}</td>
    <td style="padding:9px 12px;font-size:12px;color:#667788">${fmtDate(inj.date)}</td>
    <td style="padding:9px 12px;text-align:center">${pill("● Injected","#00C853","rgba(0,200,83,0.1)")}</td>
  </tr>`).join("") : `<tr><td colspan="4" style="padding:16px;text-align:center;color:#889;font-size:12px">No code injections recorded yet.</td></tr>`;

  const competitorRows = allCompetitors.length ? allCompetitors.map(c => {
    const queries = snippets.filter(s => s.competitors.includes(c)).map(s => `"${s.query}"`).join(", ");
    return `<tr>
      <td style="padding:9px 12px;font-size:12px;font-weight:700;color:#cc3333">${c}</td>
      <td style="padding:9px 12px;font-size:11px;color:#667788">${queries}</td>
      <td style="padding:9px 12px;text-align:center;font-size:12px;color:#cc3333;font-weight:700">${snippets.filter(s=>s.competitors.includes(c)).length}</td>
    </tr>`;
  }).join("") : `<tr><td colspan="3" style="padding:16px;text-align:center;color:#889;font-size:12px">No competitors detected in AI answers - excellent signal.</td></tr>`;

  const taskRows = completed.length ? completed.map(t => `<li style="margin-bottom:5px;font-size:12px">✓ ${t.replace(/_/g," ")}</li>`).join("") : `<li style="color:#889;font-style:italic;font-size:12px">No tasks marked complete for this period.</li>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Plus Jakarta Sans',sans-serif;background:#F7F9FD;color:#1A2340;padding:0;}
  /* Cover */
  .cover{background:linear-gradient(135deg,#0D1528 0%,#1A2856 100%);color:#fff;padding:52px 48px 44px;position:relative;overflow:hidden;}
  .cover::before{content:'';position:absolute;right:-80px;top:-80px;width:320px;height:320px;border-radius:50%;background:rgba(21,180,214,0.05);}
  .cover::after{content:'';position:absolute;left:40%;bottom:-60px;width:240px;height:240px;border-radius:50%;background:rgba(26,76,255,0.07);}
  .brand-tag{font-size:10px;font-weight:700;letter-spacing:3px;color:rgba(255,255,255,0.45);margin-bottom:12px;}
  .client-name{font-size:34px;font-weight:800;margin-bottom:4px;position:relative;}
  .client-sub{font-size:14px;color:rgba(255,255,255,0.55);margin-bottom:32px;}
  .score-flex{display:flex;align-items:center;gap:36px;flex-wrap:wrap;}
  .score-ring{position:relative;width:130px;height:130px;flex-shrink:0;}
  .score-ring svg{transform:rotate(-90deg);}
  .score-val{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;}
  .score-val .num{font-size:30px;font-weight:800;}
  .score-val .lbl{font-size:9px;color:rgba(255,255,255,0.5);letter-spacing:1.5px;margin-top:1px;}
  .grade-big{font-size:56px;font-weight:800;color:${gradeCol};line-height:1;}
  .delta-line{font-size:12px;color:rgba(255,255,255,0.6);margin-top:6px;}
  /* Platform mini-cards */
  .plat-cards{display:flex;gap:14px;margin-top:28px;flex-wrap:wrap;}
  .plat-card{background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.1);border-radius:12px;padding:14px 18px;min-width:130px;}
  .plat-card .pc-label{font-size:9px;font-weight:700;letter-spacing:1.5px;color:rgba(255,255,255,0.45);margin-bottom:6px;}
  .plat-card .pc-val{font-size:22px;font-weight:800;}
  .plat-card .pc-sub{font-size:9px;color:rgba(255,255,255,0.4);margin-top:2px;}
  /* Content */
  .body{padding:36px 48px;}
  .section{background:#fff;border-radius:14px;padding:0;margin-bottom:24px;box-shadow:0 1px 8px rgba(26,35,64,0.06);overflow:hidden;}
  .sec-hdr{padding:16px 22px;border-bottom:1px solid #EEF2FA;display:flex;align-items:center;gap:10px;}
  .sec-title{font-size:11px;font-weight:800;letter-spacing:1.8px;color:#1A4CFF;text-transform:uppercase;}
  .sec-sub{font-size:11px;color:#8898BB;margin-left:auto;}
  table{width:100%;border-collapse:collapse;}
  thead th{background:#0D1528;color:#fff;font-size:10px;font-weight:700;letter-spacing:1px;padding:9px 12px;text-align:left;}
  tbody tr:nth-child(even){background:#F7F9FD;}
  tbody tr:hover{background:#EEF5FF;}
  .badge-green{font-size:10px;font-weight:800;padding:2px 9px;border-radius:5px;background:rgba(0,200,83,0.12);color:#00C853;}
  .badge-red{font-size:10px;font-weight:800;padding:2px 9px;border-radius:5px;background:rgba(227,26,26,0.1);color:#E31A1A;}
  .badge-gold{font-size:10px;font-weight:800;padding:2px 9px;border-radius:5px;background:rgba(240,180,41,0.12);color:#CC8800;}
  /* AI Rank banner */
  .rank-banner{display:flex;gap:0;margin:0;}
  .rank-box{flex:1;padding:20px 24px;text-align:center;border-right:1px solid #EEF2FA;}
  .rank-box:last-child{border-right:none;}
  .rank-box .rb-label{font-size:9px;font-weight:700;letter-spacing:1.5px;color:#8898BB;margin-bottom:8px;}
  .rank-box .rb-val{font-size:28px;font-weight:800;color:#1A2340;}
  .rank-box .rb-sub{font-size:11px;color:#8898BB;margin-top:3px;}
  /* FAQ list */
  .faq-item{padding:12px 22px;border-bottom:1px solid #EEF2FA;}
  .faq-item:last-child{border-bottom:none;}
  .faq-q{font-size:12px;font-weight:700;color:#1A2340;margin-bottom:3px;}
  .faq-a{font-size:11px;color:#556677;line-height:1.6;}
  /* task list */
  .task-list{list-style:none;column-count:2;column-gap:24px;padding:16px 22px;font-size:12px;color:#2A3344;line-height:2;}
  .footer{text-align:center;font-size:10px;color:#8898BB;padding:24px;letter-spacing:1px;border-top:1px solid #EEF2FA;margin-top:8px;}
  .cover-footer{display:flex;justify-content:space-between;align-items:center;margin-top:36px;padding-top:20px;border-top:1px solid rgba(255,255,255,0.1);font-size:10px;color:rgba(255,255,255,0.4);}
</style>
</head>
<body>

<!-- COVER -->
<div class="cover">
  <div class="brand-tag">SHARK AI · AI VISIBILITY INTELLIGENCE PLATFORM</div>
  <div class="client-name">${client.name}</div>
  <div class="client-sub">${client.domain||""}${client.city?" · "+client.city:""}${client.businessType?" · "+client.businessType:""}</div>
  <div class="score-flex">
    <div class="score-ring">
      <svg width="130" height="130" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r="56" fill="none" stroke="rgba(255,255,255,0.12)" stroke-width="9"/>
        <circle cx="65" cy="65" r="56" fill="none" stroke="${gradeCol}" stroke-width="9"
          stroke-dasharray="${2*Math.PI*56}" stroke-dashoffset="${2*Math.PI*56 - (r.score/100)*2*Math.PI*56}"
          stroke-linecap="round"/>
      </svg>
      <div class="score-val"><span class="num">${r.score||"-"}</span><span class="lbl">GEO SCORE</span></div>
    </div>
    <div>
      <div class="grade-big">${grade}</div>
      <div class="delta-line">
        ${scoreDelta!==null ? (scoreDelta>=0?"▲ +":"▼ ")+scoreDelta+" pts vs last report · " : ""}Generated ${r.date}
      </div>
      <div class="delta-line" style="margin-top:4px">Report period: ${month}</div>
    </div>
  </div>
  <!-- 6-section mini-stats -->
  <div class="plat-cards">
    <div class="plat-card">
      <div class="pc-label">ENTITY CONSISTENCY</div>
      <div class="pc-val" style="color:${completed.filter(t=>t.startsWith("entity_")).length>=4?"#00C853":completed.filter(t=>t.startsWith("entity_")).length>=2?"#FFB547":"#E31A1A"}">${completed.filter(t=>t.startsWith("entity_")).length}/5</div>
      <div class="pc-sub">foundation tasks complete</div>
    </div>
    <div class="plat-card">
      <div class="pc-label">REVIEW AUTHORITY</div>
      <div class="pc-val" style="color:${completed.filter(t=>t.startsWith("review_")).length>=3?"#00C853":completed.filter(t=>t.startsWith("review_")).length>=1?"#FFB547":"#E31A1A"}">${completed.filter(t=>t.startsWith("review_")).length}/4</div>
      <div class="pc-sub">review tasks active</div>
    </div>
    <div class="plat-card">
      <div class="pc-label">SCHEMA BLOCKS LIVE</div>
      <div class="pc-val" style="color:${allInjections.length>0?"#00C853":"#8898BB"}">${allInjections.length}</div>
      <div class="pc-sub">injected via GTM + static</div>
    </div>
    <div class="plat-card">
      <div class="pc-label">AI CITATION CHECK</div>
      <div class="pc-val" style="color:${mentionedNow>0?"#00E5FF":"#8898BB"}">${snippets.length>0?mentionedNow+"/"+snippets.length:"-"}</div>
      <div class="pc-sub">queries where you appear</div>
    </div>
    <div class="plat-card">
      <div class="pc-label">CONTENT FRESHNESS</div>
      <div class="pc-val" style="color:${faqs.length>0?"#00C853":"#8898BB"}">${faqs.length} FAQ${faqs.length!==1?"s":""}</div>
      <div class="pc-sub">${podcastInj.length>0?podcastInj.length+" spotlight"+(podcastInj.length!==1?"s":"")+" · ":""} ${completed.filter(t=>t==="content_monthly").length>0?"✓ Monthly post":"No post yet"}</div>
    </div>
    <div class="plat-card">
      <div class="pc-label">CRAWL ACCESS</div>
      <div class="pc-val" style="color:${completed.filter(t=>t.startsWith("crawl_")).length>=3?"#00C853":completed.filter(t=>t.startsWith("crawl_")).length>=1?"#FFB547":"#E31A1A"}">${completed.filter(t=>t.startsWith("crawl_")).length}/4</div>
      <div class="pc-sub">crawl tasks complete</div>
    </div>
  </div>
  <div class="cover-footer">
    <span>Prepared by Shark Branding Solutions · sharkbrandingsolutions.com · St. Petersburg, FL</span>
    <span>CONFIDENTIAL · SharkOS v6.0</span>
  </div>
</div>

<!-- BODY -->
<div class="body">

  <!-- SECTION 1: ENTITY CONSISTENCY SCORE -->
  <div class="section">
    <div class="sec-hdr">
      <div class="sec-title">1 · Entity Consistency Score</div>
      <div class="sec-sub">NAP match rate across directories</div>
    </div>
    <div class="rank-banner">
      <div class="rank-box">
        <div class="rb-label">FOUNDATION TASKS DONE</div>
        <div class="rb-val" style="color:${completed.filter(t=>t.startsWith("entity_")).length>=4?"#00C853":completed.filter(t=>t.startsWith("entity_")).length>=2?"#FFB547":"#E31A1A"}">${completed.filter(t=>t.startsWith("entity_")).length}/5</div>
        <div class="rb-sub">entity tasks complete</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">CITATION SYNC</div>
        <div class="rb-val" style="color:${completed.includes("entity_nap_sync")?"#00C853":"#E31A1A"}">${completed.includes("entity_nap_sync")?"Active":"Not Set"}</div>
        <div class="rb-sub">NAP pushed to 40+ directories</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">TIER 1 SOURCES</div>
        <div class="rb-val" style="color:${completed.includes("entity_tier1")?"#00C853":"#E31A1A"}">${completed.includes("entity_tier1")?"Verified":"Needs Check"}</div>
        <div class="rb-sub">Google · Bing · Apple · Yelp · BBB</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">DUPLICATES</div>
        <div class="rb-val" style="color:${completed.includes("entity_duplicates")?"#00C853":"#FFB547"}">${completed.includes("entity_duplicates")?"Suppressed":"Unresolved"}</div>
        <div class="rb-sub">conflicting listings</div>
      </div>
    </div>
    <table>
      <thead><tr><th>TASK</th><th style="text-align:center">STATUS</th></tr></thead>
      <tbody>
        ${[
          ["entity_gbp",       "GBP listing complete (all fields, services, photos)"],
          ["entity_categories","All GBP categories added (primary + secondary)"],
          ["entity_nap_sync",  "Citation Sync - NAP live on 40+ directories"],
          ["entity_tier1",     "Tier 1 sources verified (Google, Bing, Apple, Yelp, Facebook, BBB)"],
          ["entity_duplicates","Duplicate listings suppressed"],
        ].map(([id,label])=>`<tr>
          <td style="padding:9px 12px;font-size:12px;color:#2A3344">${label}</td>
          <td style="padding:9px 12px;text-align:center">${completed.includes(id)?pill("✓ DONE","#00C853","rgba(0,200,83,0.12)"):pill("PENDING","#8898BB","rgba(136,152,187,0.1)")}</td>
        </tr>`).join("")}
      </tbody>
    </table>
  </div>

  <!-- SECTION 2: REVIEW AUTHORITY -->
  <div class="section">
    <div class="sec-hdr">
      <div class="sec-title">2 · Review Authority</div>
      <div class="sec-sub">Volume · Recency · Platform coverage</div>
    </div>
    <div class="rank-banner">
      <div class="rank-box">
        <div class="rb-label">REVIEW SYSTEM</div>
        <div class="rb-val" style="color:${completed.includes("review_system")?"#00C853":"#E31A1A"}">${completed.includes("review_system")?"Active":"Inactive"}</div>
        <div class="rb-sub">Review Request System</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">REVIEW VOLUME</div>
        <div class="rb-val" style="color:${completed.includes("review_volume")?"#00C853":"#FFB547"}">${completed.includes("review_volume")?"On Target":"Below Target"}</div>
        <div class="rb-sub">50+ reviews = authority threshold</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">RESPONSES</div>
        <div class="rb-val" style="color:${completed.includes("review_respond")?"#00C853":"#FFB547"}">${completed.includes("review_respond")?"All Done":"Pending"}</div>
        <div class="rb-sub">across all platforms</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">PLATFORMS</div>
        <div class="rb-val" style="color:${completed.includes("review_platforms")?"#00C853":"#FFB547"}">${completed.includes("review_platforms")?"✓ 3 Platforms":"Incomplete"}</div>
        <div class="rb-sub">Google · Facebook · BBB</div>
      </div>
    </div>
  </div>

  <!-- SECTION 3: STRUCTURED DATA -->
  <div class="section">
    <div class="sec-hdr">
      <div class="sec-title">3 · Structured Data</div>
      <div class="sec-sub">${allInjections.length} block${allInjections.length!==1?"s":""} live · ${completed.filter(t=>t.startsWith("schema_")).length}/5 schema tasks complete</div>
    </div>
    <div class="rank-banner">
      <div class="rank-box">
        <div class="rb-label">STRUCTURED DATA</div>
        <div class="rb-val" style="color:${completed.includes("schema_local")?"#00C853":"#E31A1A"}">${completed.includes("schema_local")?"Live":"Missing"}</div>
        <div class="rb-sub">LocalBusiness - readable by all AI bots</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">FAQ STRUCTURED DATA</div>
        <div class="rb-val" style="color:${completed.includes("schema_faq")?"#00C853":"#E31A1A"}">${completed.includes("schema_faq")?"Live":"Missing"}</div>
        <div class="rb-sub">Static HTML (not GTM)</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">TAG INJECTIONS</div>
        <div class="rb-val" style="color:${allInjections.length>0?"#00C853":"#8898BB"}">${allInjections.length}</div>
        <div class="rb-sub">blocks via GTM for Google AI</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">GEO COORDINATES</div>
        <div class="rb-val" style="color:${completed.includes("schema_geo")?"#00C853":"#E31A1A"}">${completed.includes("schema_geo")?"Defined":"Missing"}</div>
        <div class="rb-sub">areaServed + lat/lng</div>
      </div>
    </div>
    ${allInjections.length > 0 ? `
    <table>
      <thead><tr><th>SCHEMA NAME</th><th>TYPE</th><th>DATE INJECTED</th><th style="text-align:center">STATUS</th></tr></thead>
      <tbody>${injectionRows}</tbody>
    </table>` : `<div style="padding:16px 22px;color:#8898BB;font-size:12px">No GTM injections recorded yet - use the FAQ, Podcast, or Event tools to inject schema.</div>`}
  </div>

  <!-- SECTION 4: AI CITATION CHECK -->
  <div class="section">
    <div class="sec-hdr">
      <div class="sec-title">4 · AI Citation Check</div>
      <div class="sec-sub">${snippets.length > 0 ? `Scanned ${fmtDate(snippetScan?.date)} · ${mentionedNow}/${snippets.length} queries where ${client.name} appears` : "No scan run yet"}</div>
    </div>
    ${snippets.length > 0 ? `
    <div class="rank-banner">
      <div class="rank-box">
        <div class="rb-label">CITED NOW</div>
        <div class="rb-val" style="color:${mentionedNow>0?"#00C853":"#E31A1A"}">${mentionedNow}/${snippets.length}</div>
        <div class="rb-sub">${Math.round((mentionedNow/snippets.length)*100)}% of target queries</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">BASELINE</div>
        <div class="rb-val">${mentionedBase!==null?mentionedBase+"/"+(baseline?.total||"?"):"-"}</div>
        <div class="rb-sub">${baseline ? fmtDate(baseline.date) : "First scan = baseline"}</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">CHANGE</div>
        <div class="rb-val" style="color:${delta>0?"#00C853":delta<0?"#E31A1A":"#1A2340"}">${delta!==null?(delta>0?"▲ +"+delta:delta<0?"▼ "+Math.abs(delta):"→ 0"):"-"}</div>
        <div class="rb-sub">${delta!==null?(delta>0?"queries gained":delta<0?"queries lost":"no change"):"vs baseline"}</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">COMPETITORS IN AI</div>
        <div class="rb-val" style="color:${allCompetitors.length>0?"#FF6D00":"#00C853"}">${allCompetitors.length}</div>
        <div class="rb-sub">${allCompetitors.length>0?"appearing instead of you":"clean - no competitors seen"}</div>
      </div>
    </div>
    <table>
      <thead><tr>
        <th>QUERY</th><th style="text-align:center">THIS MONTH</th><th style="text-align:center">BASELINE</th>
        <th style="text-align:center">CHANGE</th><th>COMPETITORS IN ANSWER</th>
      </tr></thead>
      <tbody>${snippetRows}</tbody>
    </table>` : `<div style="padding:24px;text-align:center;color:#8898BB;font-size:13px">
      No AI citation data yet. Run an AI Scan from the Audit tab - results will appear in the next report.</div>`}
  </div>

  <!-- SECTION 5: CONTENT FRESHNESS -->
  <div class="section">
    <div class="sec-hdr">
      <div class="sec-title">5 · Content Freshness</div>
      <div class="sec-sub">Pages updated · FAQs · Spotlights · Monthly post</div>
    </div>
    <div class="rank-banner">
      <div class="rank-box">
        <div class="rb-label">FAQ ENTRIES</div>
        <div class="rb-val" style="color:${faqs.length>=10?"#00C853":faqs.length>0?"#FFB547":"#E31A1A"}">${faqs.length}</div>
        <div class="rb-sub">${faqs.length>=10?"above AI threshold":"target: 10+ questions"}</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">SPOTLIGHTS</div>
        <div class="rb-val" style="color:${podcastInj.length>0?"#00C853":"#8898BB"}">${podcastInj.length}</div>
        <div class="rb-sub">member / guest pages with schema</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">MONTHLY POST</div>
        <div class="rb-val" style="color:${completed.includes("content_monthly")?"#00C853":"#FFB547"}">${completed.includes("content_monthly")?"Published":"Missing"}</div>
        <div class="rb-sub">keeps entity fresh for AI</div>
      </div>
      <div class="rank-box">
        <div class="rb-label">SERVICE AREAS</div>
        <div class="rb-val" style="color:${completed.includes("content_areas")?"#00C853":"#FFB547"}">${completed.includes("content_areas")?"Live":"Needed"}</div>
        <div class="rb-sub">city/neighborhood pages</div>
      </div>
    </div>
    ${faqs.length > 0 ? `
    <div style="padding:0 0 8px">
      ${faqs.slice(0,5).map(f => `<div class="faq-item"><div class="faq-q">Q: ${f.q}</div><div class="faq-a">A: ${f.a}</div></div>`).join("")}
      ${faqs.length > 5 ? `<div style="padding:10px 22px;font-size:11px;color:#8898BB">+ ${faqs.length-5} more FAQ entries live</div>` : ""}
    </div>` : `<div style="padding:16px 22px;color:#8898BB;font-size:12px">No FAQ content yet - add FAQs from the FAQ tab to give AI crawlers content to cite.</div>`}
  </div>

  <!-- SECTION 6: CRAWL ACCESS -->
  <div class="section">
    <div class="sec-hdr">
      <div class="sec-title">6 · Crawl Access</div>
      <div class="sec-sub">AI crawlers unblocked · llms.txt · Sitemap · Core Web Vitals</div>
    </div>
    <table>
      <thead><tr><th>SIGNAL</th><th>DETAIL</th><th style="text-align:center">STATUS</th></tr></thead>
      <tbody>
        <tr>
          <td style="padding:9px 12px;font-size:12px;font-weight:700;color:#2A3344">llms.txt</td>
          <td style="padding:9px 12px;font-size:12px;color:#667788">Maps site content for GPTBot, ClaudeBot, PerplexityBot</td>
          <td style="padding:9px 12px;text-align:center">${completed.includes("crawl_llmstxt")?pill("✓ LIVE","#00C853","rgba(0,200,83,0.12)"):pill("MISSING","#E31A1A","rgba(227,26,26,0.1)")}</td>
        </tr>
        <tr>
          <td style="padding:9px 12px;font-size:12px;font-weight:700;color:#2A3344">Sitemap</td>
          <td style="padding:9px 12px;font-size:12px;color:#667788">Submitted to Google and Bing Search Console</td>
          <td style="padding:9px 12px;text-align:center">${completed.includes("crawl_sitemap")?pill("✓ SUBMITTED","#00C853","rgba(0,200,83,0.12)"):pill("PENDING","#FFB547","rgba(240,180,41,0.12)")}</td>
        </tr>
        <tr>
          <td style="padding:9px 12px;font-size:12px;font-weight:700;color:#2A3344">robots.txt</td>
          <td style="padding:9px 12px;font-size:12px;color:#667788">GPTBot, ClaudeBot, PerplexityBot not blocked</td>
          <td style="padding:9px 12px;text-align:center">${completed.includes("crawl_robots")?pill("✓ OPEN","#00C853","rgba(0,200,83,0.12)"):pill("CHECK NEEDED","#FFB547","rgba(240,180,41,0.12)")}</td>
        </tr>
        <tr>
          <td style="padding:9px 12px;font-size:12px;font-weight:700;color:#2A3344">Core Web Vitals</td>
          <td style="padding:9px 12px;font-size:12px;color:#667788">Page speed signals that affect crawler priority</td>
          <td style="padding:9px 12px;text-align:center">${completed.includes("crawl_vitals")?pill("✓ PASSING","#00C853","rgba(0,200,83,0.12)"):pill("AUDIT NEEDED","#FFB547","rgba(240,180,41,0.12)")}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- REPORT DETAILS -->
  <div class="section">
    <div class="sec-hdr"><div class="sec-title">Report Details</div></div>
    <table>
      <tbody>
        <tr><td style="padding:9px 12px;color:#8898BB;font-size:12px;width:40%">Client</td><td style="padding:9px 12px;font-weight:700;font-size:12px">${client.name}</td></tr>
        <tr><td style="padding:9px 12px;color:#8898BB;font-size:12px">Domain</td><td style="padding:9px 12px;font-size:12px">${client.domain||"-"}</td></tr>
        <tr><td style="padding:9px 12px;color:#8898BB;font-size:12px">City</td><td style="padding:9px 12px;font-size:12px">${client.city||"-"}</td></tr>
        <tr><td style="padding:9px 12px;color:#8898BB;font-size:12px">Business Type</td><td style="padding:9px 12px;font-size:12px">${client.businessType||"-"}</td></tr>
        <tr><td style="padding:9px 12px;color:#8898BB;font-size:12px">Report Period</td><td style="padding:9px 12px;font-size:12px">${month}</td></tr>
        <tr><td style="padding:9px 12px;color:#8898BB;font-size:12px">Report Type</td><td style="padding:9px 12px;font-size:12px">${r.type||"Monthly GEO Performance Report"}</td></tr>
        <tr><td style="padding:9px 12px;color:#8898BB;font-size:12px">Generated</td><td style="padding:9px 12px;font-size:12px">${r.date}</td></tr>
        <tr><td style="padding:9px 12px;color:#8898BB;font-size:12px">GTM Container</td><td style="padding:9px 12px;font-size:12px">${client.gtm||"Not connected"}</td></tr>
      </tbody>
    </table>
  </div>

</div>

<div class="footer">
  SHARK AI · AI VISIBILITY INTELLIGENCE PLATFORM · Prepared by Shark Branding Solutions · sharkbrandingsolutions.com · St. Petersburg, FL · CONFIDENTIAL
</div>
</body>
</html>`;
}

// ---------------------------------------------
// REPORTS TAB
// ---------------------------------------------
function ReportsTab({client,checkedState}){
  const [reports,setReports]=useState(()=>LS.get("reports_"+client.id)||[]);
  const [gen,setGen]=useState(false);
  const [rtype,setRtype]=useState("Monthly GEO Performance Report");
  const [viewing,setViewing]=useState(null);

  const generate=async()=>{
    setGen(true);
    const completedTasks=Object.entries(checkedState||{}).filter(([,v])=>v).map(([k])=>k);
    const result=await ipc("generate-report",{clientId:client.id,client:client.name,domain:client.domain,
      month:new Date().toLocaleString("default",{month:"long",year:"numeric"}),
      score:client.score,completed:JSON.stringify(completedTasks)});
    const report={
      id:Date.now().toString(),
      month:new Date().toLocaleString("default",{month:"long"}),
      year:new Date().getFullYear(),
      score:client.score,
      date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),
      size:"2.1 MB",
      path:result?.path||null,
      type:rtype,
      completedTasks,
    };
    await ipc("save-report",{clientId:client.id,report});
    setReports(prev=>[report,...prev]);
    setViewing(report);
    setGen(false);
  };

  if(viewing){
    const html=buildReportHTML(viewing,client);
    return(
      <div style={{display:"flex",flexDirection:"column",height:"100%",gap:0}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",flexShrink:0}}>
          <Btn variant="ghost" onClick={()=>setViewing(null)}>? Back to Reports</Btn>
          <span style={{fontSize:13,fontWeight:700,color:T.text}}>{viewing.month} {viewing.year}  {viewing.type||"Monthly GEO Report"}</span>
          <div style={{flex:1}}/>
          <Btn sm variant="primary" onClick={()=>{
            const blob=new Blob([html],{type:"text/html"});
            const a=document.createElement("a");
            a.href=URL.createObjectURL(blob);
            a.download=`${client.name.replace(/\s+/g,"-")}-${viewing.month}-${viewing.year}-GEO-Report.html`;
            a.click();
          }}>? Save as File</Btn>
        </div>
        <div style={{flex:1,borderRadius:16,overflow:"hidden",border:`1px solid ${T.border}`}}>
          <iframe
            srcDoc={html}
            style={{width:"100%",height:"100%",border:"none"}}
            title="Report Preview"
          />
        </div>
      </div>
    );
  }

  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      <Card>
        <div style={{fontWeight:800,color:T.text,marginBottom:16}}>Generate New Report</div>
        <div style={{display:"flex",gap:12}}>
          <select value={rtype} onChange={e=>setRtype(e.target.value)}
            style={{flex:1,background:T.input,border:`1px solid ${T.border}`,borderRadius:12,padding:"10px 14px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none"}}>
            <option>Monthly GEO Performance Report</option>
            <option>Platform-Specific Audit</option>
            <option>Citation Analysis</option>
            <option>Structured Data Coverage Report</option>
          </select>
          <Btn variant="primary" onClick={generate} disabled={gen}>
            {gen?<span className="spin" style={{width:14,height:14,border:`2px solid rgba(255,255,255,0.3)`,borderTopColor:T.white,borderRadius:"50%"}}/>:"??"}
            {gen?" Generating...":" Generate"}
          </Btn>
        </div>
      </Card>
      <Card style={{padding:0,overflow:"hidden"}}>
        <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`}}>
          <span style={{fontWeight:800,color:T.text}}>Recent Reports</span>
        </div>
        {reports.length===0&&(
          <div style={{padding:40,textAlign:"center",color:T.ghost,fontSize:13}}>
            No reports yet  generate your first one above.
          </div>
        )}
        {reports.map(r=>(
          <div key={r.id} style={{display:"flex",alignItems:"center",gap:16,padding:"14px 24px",
            borderBottom:`1px solid ${T.border}`,transition:"background 0.12s",cursor:"pointer"}}
            onClick={()=>setViewing(r)}
            onMouseEnter={e=>e.currentTarget.style.background=T.input}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <ScoreRing score={r.score} size={48} sw={4}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:13,color:T.text}}>{r.month} {r.year}  {r.type||"Monthly GEO Report"}</div>
              <div style={{fontSize:11,color:T.ghost,marginTop:2}}>?? {r.date}  {r.completedTasks?.length||0} tasks completed</div>
            </div>
            <Btn sm variant="primary" onClick={e=>{e.stopPropagation();setViewing(r);}}>?? View Report</Btn>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ---------------------------------------------
// SHARKRANK TAB - Competitive Matrix + Perplexity Live Check
// Based on: Sun et al. (2023) "Is ChatGPT Good at Search?" - Stanford NLP
// ---------------------------------------------
function SharkRankTab({client}){
  const [mode,setMode]=useState("matrix");
  const [matrixRunning,setMatrixRunning]=useState(false);
  const [matrixData,setMatrixData]=useState(()=>LS.get(`sharkrank_${client.id}`)||null);
  const [matrixError,setMatrixError]=useState(null);
  const [query,setQuery]=useState(`best ${client.businessType||"local business"} in ${client.city||"Tampa Bay"}`);
  const [running,setRunning]=useState(false);
  const [results,setResults]=useState(null);
  const [error,setError]=useState(null);
  const [plxRunning,setPlxRunning]=useState(false);
  const [plxResults,setPlxResults]=useState(()=>LS.get(`plx_${client.id}`)||null);
  const [scanStatus,setScanStatus]=useState(null);
  const [isLiveData,setIsLiveData]=useState(false);

  const settings=LS.get("settings")||{};
  const anthropicKey=settings.anthropicApiKey||null;
  const plxKey=settings.perplexityApiKey||null;

  const autoQueries=useMemo(()=>{
    const t=client.businessType||"local business";
    const c=client.city||"Tampa Bay";
    const n=client.name||"";
    return[
      `best ${t} in ${c}`,
      `top rated ${t} near ${c}`,
      `${t} near me ${c}`,
      `affordable ${t} ${c}`,
      `emergency ${t} ${c}`,
      `${t} reviews ${c}`,
      `who is ${n}`,
      `${n} ${c}`,
    ].filter(q=>q.trim().length>3);
  },[client]);

  const runMatrix=async()=>{
    setMatrixRunning(true);setMatrixError(null);setScanStatus(null);
    try{
      const {passages,isLive}=await buildLivePassages(client,{perplexityKey:plxKey,anthropicKey,onStatus:setScanStatus});
      setIsLiveData(isLive);
      setScanStatus(isLive?"Running matrix analysis on live competitors…":"Running matrix analysis…");
      const allRankings=await Promise.all(
        autoQueries.map(q=>rankPassages(q,passages,{anthropicKey}))
      );
      const businesses=allRankings[0].map(p=>({key:p.id||p.name,name:p.name,isClient:p.isClient,domain:p.domain}));
      const grid=allRankings.map(qr=>{
        const m={};
        qr.forEach((p,i)=>{m[p.id||p.name]=i+1;});
        return m;
      });
      const avgRanks={};
      businesses.forEach(b=>{
        const ranks=grid.map(row=>row[b.key]||businesses.length);
        avgRanks[b.key]=ranks.reduce((a,v)=>a+v,0)/ranks.length;
      });
      const maxAvg=Math.max(...Object.values(avgRanks));
      const overallScores={};
      businesses.forEach(b=>{
        overallScores[b.key]=Math.round(100-((avgRanks[b.key]-1)/Math.max(maxAvg-1,1))*80);
      });
      const matrix={queries:autoQueries,businesses,grid,overallScores,avgRanks,
        ts:new Date().toISOString(),prevTs:matrixData?.ts||null,prevScores:matrixData?.overallScores||null};
      setMatrixData(matrix);
      LS.set(`sharkrank_${client.id}`,matrix);
    }catch(e){
      setMatrixError(e.message||"Error running matrix");
    }finally{
      setMatrixRunning(false);
      setScanStatus(null);
    }
  };

  const runSingle=async()=>{
    setRunning(true);setError(null);setResults(null);setScanStatus(null);
    try{
      const {passages,isLive}=await buildLivePassages(client,{perplexityKey:plxKey,anthropicKey,onStatus:setScanStatus});
      setIsLiveData(isLive);
      setScanStatus("Re-ranking…");
      const ranked=await rankPassages(query,passages,{anthropicKey});
      setResults(ranked);
    }catch(e){setError(e.message||"Unknown error");}
    finally{setRunning(false);setScanStatus(null);}
  };

  const runPerplexity=async()=>{
    if(!plxKey)return;
    setPlxRunning(true);
    try{
      const checks=await Promise.all(
        autoQueries.slice(0,6).map(async q=>{
          const res=await fetch("https://api.perplexity.ai/chat/completions",{
            method:"POST",
            headers:{"Authorization":`Bearer ${plxKey}`,"Content-Type":"application/json"},
            body:JSON.stringify({model:"llama-3.1-sonar-small-128k-online",
              messages:[{role:"user",content:q}],max_tokens:300}),
          });
          const data=await res.json();
          const answer=data.choices?.[0]?.message?.content||"";
          const mentioned=answer.toLowerCase().includes((client.name||"").toLowerCase());
          const sources=data.citations||[];
          return{query:q,answer:answer.slice(0,220),mentioned,sources};
        })
      );
      const result={checks,ts:new Date().toISOString()};
      setPlxResults(result);
      LS.set(`plx_${client.id}`,result);
    }catch(e){console.error("Perplexity error:",e);}
    finally{setPlxRunning(false);}
  };

  const rankColor=r=>r===1?"#4FD1A0":r===2?"#15B4D6":r<=4?"#F9DB79":T.red;
  const rankBg=r=>r===1?"rgba(79,209,160,0.18)":r===2?"rgba(21,180,214,0.12)":r<=4?"rgba(249,219,121,0.12)":"rgba(247,85,95,0.1)";
  const scoreColor=s=>s>=75?T.mint:s>=50?T.yellow:T.red;
  const badge=(label,on,color)=>(
    <span style={{fontSize:9,fontWeight:700,letterSpacing:0.6,padding:"2px 6px",borderRadius:4,
      background:on?`${color}22`:"rgba(255,255,255,0.04)",
      color:on?color:T.ghost,border:`1px solid ${on?color+"44":"rgba(255,255,255,0.06)"}`,
      whiteSpace:"nowrap"}}>{label}</span>
  );

  return(
    <div>
      {/* Header */}
      <div style={{marginBottom:16}}>
        <div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:4}}>SharkRank™ - AI Citation Intelligence</div>
        <div style={{fontSize:12,color:T.ghost,lineHeight:1.6}}>
          Listwise re-ranking shows where your client stands in AI search vs. competitors - powered by Stanford NLP research.
          <span style={{marginLeft:8,fontSize:10,color:T.blue,fontStyle:"italic"}}>Sun et al. 2023 · arxiv.org/abs/2304.09542</span>
        </div>
      </div>

      {/* Mode toggle */}
      <div style={{display:"flex",gap:8,marginBottom:20}}>
        {[["matrix","COMPETITIVE MATRIX"],["single","SINGLE QUERY"]].map(([m,label])=>(
          <button key={m} onClick={()=>setMode(m)} style={{
            padding:"7px 18px",borderRadius:8,fontSize:11,fontWeight:700,letterSpacing:0.8,
            border:"none",cursor:"pointer",
            background:mode===m?T.blue:"rgba(255,255,255,0.06)",
            color:mode===m?"#060C0F":T.ghost,
          }}>{label}</button>
        ))}
      </div>

      {/* ── MATRIX MODE ── */}
      {mode==="matrix"&&(
        <div>
          <Card style={{marginBottom:16,padding:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:3}}>Competitive Rank Matrix</div>
                <div style={{fontSize:11,color:T.ghost}}>
                  Auto-runs {autoQueries.length} queries · shows {client.name||"your client"} vs top competitors across all of them
                </div>
              </div>
              <Btn variant="primary" onClick={runMatrix} disabled={matrixRunning} style={{flexShrink:0}}>
                {matrixRunning?"Analyzing all queries...":matrixData?"Re-Run Matrix":"Run Matrix Analysis"}
              </Btn>
            </div>
            {/* Live / Demo mode badge */}
            <div style={{marginTop:10,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              {plxKey?(
                <span style={{fontSize:10,fontWeight:700,letterSpacing:1,padding:"3px 8px",borderRadius:20,
                  background:"rgba(79,209,160,0.15)",border:"1px solid rgba(79,209,160,0.4)",color:"#4FD1A0"}}>
                  ● LIVE - real competitor data via Perplexity
                </span>
              ):(
                <span style={{fontSize:10,fontWeight:700,letterSpacing:1,padding:"3px 8px",borderRadius:20,
                  background:"rgba(249,219,121,0.1)",border:"1px solid rgba(249,219,121,0.3)",color:"#F9DB79"}}>
                  ◌ DEMO - add Perplexity key in Settings for real competitors
                </span>
              )}

            </div>
            {/* Scan progress */}
            {matrixRunning&&scanStatus&&(
              <div style={{marginTop:8,fontSize:11,color:T.blue,display:"flex",alignItems:"center",gap:6}}>
                <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:T.blue,
                  animation:"pulse 1.2s ease-in-out infinite"}}/>
                {scanStatus}
              </div>
            )}
          </Card>

          {matrixError&&(
            <div style={{marginBottom:16,padding:12,background:"rgba(239,68,68,0.08)",
              border:`1px solid rgba(239,68,68,0.25)`,borderRadius:8,color:T.red,fontSize:12}}>
              {matrixError}
            </div>
          )}

          {matrixData&&(()=>{
            const{queries,businesses,grid,overallScores,prevScores,ts,prevTs}=matrixData;
            const clientBiz=businesses.find(b=>b.isClient);
            const clientKey=clientBiz?.key;
            const clientScore=overallScores[clientKey]||0;
            const prevClientScore=prevScores?.[clientKey]||null;
            const competitors=businesses.filter(b=>!b.isClient);
            const topComp=competitors.sort((a,b)=>(overallScores[b.key]||0)-(overallScores[a.key]||0))[0];
            const winsCount=queries.filter((_,qi)=>grid[qi][clientKey]===1).length;
            const scoreDelta=prevClientScore!==null?clientScore-prevClientScore:null;

            return(
              <div>
                {/* Score cards */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
                  <Card style={{padding:16,textAlign:"center",background:"rgba(21,180,214,0.04)",border:`1px solid ${T.blue}33`}}>
                    <div style={{fontSize:30,fontWeight:800,color:T.blue,lineHeight:1}}>{clientScore}</div>
                    <div style={{fontSize:10,color:T.ghost,letterSpacing:0.8,marginTop:4}}>AI VISIBILITY SCORE</div>
                    {scoreDelta!==null&&(
                      <div style={{fontSize:11,marginTop:5,fontWeight:700,color:scoreDelta>=0?T.mint:T.red}}>
                        {scoreDelta>=0?"↑":"↓"} {Math.abs(scoreDelta)} pts vs last scan
                      </div>
                    )}
                  </Card>
                  <Card style={{padding:16,textAlign:"center"}}>
                    <div style={{fontSize:30,fontWeight:800,lineHeight:1,
                      color:winsCount>=queries.length*0.6?T.mint:winsCount>=queries.length*0.3?"#F9DB79":T.red}}>
                      {winsCount}<span style={{fontSize:16,color:T.ghost}}>/{queries.length}</span>
                    </div>
                    <div style={{fontSize:10,color:T.ghost,letterSpacing:0.8,marginTop:4}}>QUERIES RANKED #1</div>
                  </Card>
                  <Card style={{padding:16,textAlign:"center",background:"rgba(239,68,68,0.03)",border:"1px solid rgba(239,68,68,0.15)"}}>
                    <div style={{fontSize:15,fontWeight:800,color:T.text,lineHeight:1.3}}>{topComp?.name||"N/A"}</div>
                    <div style={{fontSize:10,color:T.ghost,letterSpacing:0.8,marginTop:4}}>TOP COMPETITOR</div>
                    <div style={{fontSize:18,fontWeight:800,color:scoreColor(overallScores[topComp?.key]||0),marginTop:2}}>
                      {overallScores[topComp?.key]||0}
                    </div>
                  </Card>
                </div>

                {/* The grid */}
                <Card style={{padding:0,overflow:"hidden",marginBottom:16}}>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead>
                        <tr style={{borderBottom:`1px solid ${T.border}`}}>
                          <th style={{padding:"10px 14px",textAlign:"left",color:T.ghost,fontWeight:700,
                            letterSpacing:0.8,fontSize:10,minWidth:200}}>QUERY</th>
                          {businesses.map(b=>(
                            <th key={b.key} style={{padding:"10px 12px",textAlign:"center",fontWeight:700,
                              fontSize:10,color:b.isClient?T.blue:T.ghost,letterSpacing:0.6,minWidth:110,
                              background:b.isClient?"rgba(21,180,214,0.04)":"transparent"}}>
                              {b.isClient?"★ ":""}{b.name.split(" ").slice(0,2).join(" ")}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {queries.map((q,qi)=>(
                          <tr key={qi} style={{borderBottom:`1px solid ${T.border}22`}}>
                            <td style={{padding:"8px 14px",color:T.ghost,fontSize:11,fontStyle:"italic"}}>{q}</td>
                            {businesses.map(b=>{
                              const r=grid[qi][b.key]||businesses.length;
                              return(
                                <td key={b.key} style={{padding:"6px 12px",textAlign:"center",
                                  background:b.isClient?"rgba(21,180,214,0.03)":"transparent"}}>
                                  <span style={{display:"inline-block",width:34,height:24,lineHeight:"24px",
                                    borderRadius:6,fontWeight:800,fontSize:12,
                                    background:rankBg(r),color:rankColor(r)}}>#{r}</span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{borderTop:`2px solid ${T.border}`,background:"rgba(255,255,255,0.02)"}}>
                          <td style={{padding:"10px 14px",fontSize:10,fontWeight:800,color:T.ghost,letterSpacing:0.8}}>
                            OVERALL AI SCORE
                          </td>
                          {businesses.map(b=>(
                            <td key={b.key} style={{padding:"10px 12px",textAlign:"center",
                              background:b.isClient?"rgba(21,180,214,0.06)":"transparent"}}>
                              <div style={{fontSize:20,fontWeight:800,color:scoreColor(overallScores[b.key]||0)}}>
                                {overallScores[b.key]||0}
                              </div>
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Card>

                {/* Proof of Impact */}
                {(()=>{
                  const injections=LS.get(`inject_history_${client.id}`)||[];
                  if(!injections.length)return null;
                  const lastInject=injections[injections.length-1];
                  const auditHist=LS.get(`audit_history_${client.id}`)||[];
                  const before=auditHist.filter(h=>h.ts<lastInject.ts).slice(-1)[0];
                  const after=auditHist.filter(h=>h.ts>lastInject.ts)[0];
                  if(!before||!after)return null;
                  const delta=after.score-before.score;
                  return(
                    <Card style={{padding:16,marginBottom:16,background:"rgba(79,209,160,0.04)",
                      border:"1px solid rgba(79,209,160,0.2)"}}>
                      <div style={{fontSize:11,fontWeight:800,color:T.mint,letterSpacing:1,marginBottom:10}}>
                        PROOF OF IMPACT - CODE INJECTION
                      </div>
                      <div style={{display:"flex",gap:20,alignItems:"center",flexWrap:"wrap"}}>
                        <div style={{textAlign:"center"}}>
                          <div style={{fontSize:26,fontWeight:800,color:T.red}}>{before.score}</div>
                          <div style={{fontSize:9,color:T.ghost,letterSpacing:0.6}}>BEFORE SCHEMA</div>
                          <div style={{fontSize:9,color:T.ghost}}>{new Date(before.ts).toLocaleDateString()}</div>
                        </div>
                        <div style={{fontSize:22,color:T.ghost,fontWeight:300}}>→</div>
                        <div style={{textAlign:"center"}}>
                          <div style={{fontSize:26,fontWeight:800,color:T.mint}}>{after.score}</div>
                          <div style={{fontSize:9,color:T.ghost,letterSpacing:0.6}}>AFTER SCHEMA</div>
                          <div style={{fontSize:9,color:T.ghost}}>{new Date(after.ts).toLocaleDateString()}</div>
                        </div>
                        <div style={{fontSize:28,fontWeight:800,color:delta>=0?T.mint:T.red,marginLeft:8}}>
                          {delta>=0?"+":""}{delta} pts
                        </div>
                        <div style={{flex:1,fontSize:11,color:T.ghost,lineHeight:1.7}}>
                          Schema injected via GTM on {new Date(lastInject.ts).toLocaleDateString()}.
                          Audit score improved by <strong style={{color:T.mint}}>{Math.abs(delta)} points</strong> - directly attributable to structured data injection.
                        </div>
                      </div>
                    </Card>
                  );
                })()}

                <div style={{fontSize:10,color:T.ghost,textAlign:"right",marginTop:4}}>
                  Last run: {new Date(ts).toLocaleString()}
                  {prevTs&&` · Previous: ${new Date(prevTs).toLocaleString()}`}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── SINGLE QUERY MODE ── */}
      {mode==="single"&&(
        <div>
          <Card style={{marginBottom:16,padding:16}}>
            <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1.1,marginBottom:8}}>SEARCH QUERY</div>
            <div style={{display:"flex",gap:10}}>
              <input value={query} onChange={e=>setQuery(e.target.value)}
                style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${T.border}`,
                  borderRadius:8,padding:"10px 14px",color:T.text,fontSize:13,fontFamily:"inherit",outline:"none"}}
                placeholder="e.g. best HVAC repair in Tampa Bay"/>
              <Btn variant="primary" onClick={runSingle} disabled={running||!query.trim()}>
                {running?"Analyzing...":"Run SharkRank"}
              </Btn>
            </div>
            {/* Live / Demo mode badge */}
            <div style={{marginTop:10,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              {plxKey?(
                <span style={{fontSize:10,fontWeight:700,letterSpacing:1,padding:"3px 8px",borderRadius:20,
                  background:"rgba(79,209,160,0.15)",border:"1px solid rgba(79,209,160,0.4)",color:"#4FD1A0"}}>
                  ● LIVE - real competitors via Perplexity
                </span>
              ):(
                <span style={{fontSize:10,fontWeight:700,letterSpacing:1,padding:"3px 8px",borderRadius:20,
                  background:"rgba(249,219,121,0.1)",border:"1px solid rgba(249,219,121,0.3)",color:"#F9DB79"}}>
                  ◌ DEMO - add Perplexity key in Settings for real data
                </span>
              )}

            </div>
            {/* Scan progress */}
            {running&&scanStatus&&(
              <div style={{marginTop:8,fontSize:11,color:T.blue,display:"flex",alignItems:"center",gap:6}}>
                <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:T.blue,
                  animation:"pulse 1.2s ease-in-out infinite"}}/>
                {scanStatus}
              </div>
            )}
          </Card>
          {error&&(
            <div style={{marginBottom:16,padding:12,background:"rgba(239,68,68,0.08)",
              border:`1px solid rgba(239,68,68,0.25)`,borderRadius:8,color:T.red,fontSize:12}}>{error}</div>
          )}
          {results&&(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1.1}}>
                  CITATION RANK RESULTS · {results.length} LISTINGS ANALYZED
                </div>
                {isLiveData?(
                  <span style={{fontSize:10,fontWeight:700,letterSpacing:1,padding:"2px 7px",borderRadius:20,
                    background:"rgba(79,209,160,0.15)",border:"1px solid rgba(79,209,160,0.4)",color:"#4FD1A0"}}>
                    ● LIVE DATA
                  </span>
                ):(
                  <span style={{fontSize:10,fontWeight:600,letterSpacing:0.5,padding:"2px 7px",borderRadius:20,
                    background:"rgba(148,163,184,0.08)",border:"1px solid rgba(148,163,184,0.2)",color:T.ghost}}>
                    DEMO DATA
                  </span>
                )}
              </div>
              {results.map((p,i)=>{
                const isClient=p.isClient;
                const sc=p.citationScore||scoreFromRank(i+1,results.length);
                const col=scoreColor(sc);
                return(
                  <div key={p.id||i} style={{marginBottom:10,padding:"14px 16px",borderRadius:10,
                    background:isClient?"rgba(21,180,214,0.06)":"rgba(255,255,255,0.02)",
                    border:`1px solid ${isClient?T.blue+"44":T.border}`,
                    display:"flex",alignItems:"flex-start",gap:14}}>
                    <div style={{width:32,height:32,borderRadius:8,flexShrink:0,
                      background:i===0?"linear-gradient(135deg,#F9DB79,#F7555F)":i===1?"rgba(148,163,184,0.15)":i===2?"rgba(180,120,60,0.2)":"rgba(255,255,255,0.04)",
                      border:`1px solid ${i===0?"#F9DB7944":i===1?"#94a3b844":i===2?"#b4783c44":T.border}`,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:13,fontWeight:800,
                      color:i===0?"#F9DB79":i===1?"#94a3b8":i===2?"#b4783c":T.ghost}}>#{i+1}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                        <span style={{fontSize:14,fontWeight:700,color:isClient?T.blue:T.text,whiteSpace:"nowrap"}}>{p.name}</span>
                        {isClient&&<span style={{fontSize:9,fontWeight:800,letterSpacing:1,color:"#060C0F",background:T.blue,padding:"2px 7px",borderRadius:4}}>YOUR CLIENT</span>}
                        {p.domain&&<span style={{fontSize:11,color:T.ghost}}>{p.domain}</span>}
                        {p._isLiveData&&!isClient&&<span style={{fontSize:9,fontWeight:700,letterSpacing:0.8,color:"#4FD1A0",opacity:0.7}}>● LIVE</span>}
                      </div>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                        {badge("SCHEMA",p.signals?.hasSchema,"#10b981")}
                        {badge("GBP",p.signals?.isGBPVerified,T.blue)}
                        {badge("llms.txt",p.signals?.hasLLMSTxt,"#a78bfa")}
                        {badge("REVIEWS",p.signals?.hasReviews,"#F9DB79")}
                        {badge("FAQ",p.signals?.hasFAQ,"#06b6d4")}
                        {badge("BBB",p.signals?.hasBBB,"#3b82f6")}
                        {p.signals?.hasReviews&&p.signals?.rating&&(
                          <span style={{fontSize:9,fontWeight:700,color:"#F9DB79"}}>{p.signals.rating}★ ({p.signals.reviewCount} reviews)</span>
                        )}
                      </div>
                    </div>
                    <div style={{textAlign:"center",flexShrink:0}}>
                      <div style={{fontSize:22,fontWeight:800,color:col,lineHeight:1}}>{sc}</div>
                      <div style={{fontSize:9,color:T.ghost,letterSpacing:0.8,marginTop:2}}>CITATION SCORE</div>
                      <div style={{marginTop:6,width:60,height:4,background:T.border,borderRadius:2,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${sc}%`,background:col,borderRadius:2,transition:"width 0.6s ease"}}/>
                      </div>
                    </div>
                  </div>
                );
              })}
              <Card style={{marginTop:20,padding:16,background:"rgba(21,180,214,0.04)",border:`1px solid ${T.blue}22`}}>
                <div style={{fontSize:11,fontWeight:800,color:T.blue,letterSpacing:1,marginBottom:10}}>WHAT THIS MEANS</div>
                {(()=>{
                  const cr=results.find(p=>p.isClient);
                  const rank=cr?.rank||results.length;
                  const sc=cr?.citationScore||0;
                  const gap=results[0]?.isClient?null:results[0];
                  return(
                    <div style={{fontSize:12,color:T.ghost,lineHeight:1.8}}>
                      <div>
                        <span style={{color:T.text,fontWeight:700}}>{client.name}</span> is ranked{" "}
                        <span style={{color:rank<=2?T.mint:rank<=4?"#F9DB79":T.red,fontWeight:700}}>#{rank} of {results.length}</span>
                        {" "}with a citation score of{" "}
                        <span style={{color:scoreColor(sc),fontWeight:700}}>{sc}/100</span>.
                      </div>
                      {gap&&(
                        <div style={{marginTop:6}}>
                          Top competitor <span style={{color:T.text,fontWeight:700}}>{gap.name}</span> leads with{" "}
                          {[
                            gap.signals?.hasSchema&&!cr?.signals?.hasSchema?"structured schema":null,
                            gap.signals?.hasLLMSTxt&&!cr?.signals?.hasLLMSTxt?"llms.txt":null,
                            gap.signals?.isGBPVerified&&!cr?.signals?.isGBPVerified?"verified GBP":null,
                            (gap.signals?.reviewCount||0)>(cr?.signals?.reviewCount||0)?"more reviews":null,
                          ].filter(Boolean).join(", ")||"stronger overall signals"}.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Card>
            </div>
          )}
        </div>
      )}

      {/* ── UNIFIED SIGNAL vs REALITY VIEW ── */}
      <Card style={{marginTop:20,padding:0,overflow:"hidden"}}>
        {/* Header */}
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:T.text}}>Signal Potential vs. Real Citations</div>
            <div style={{fontSize:11,color:T.ghost,marginTop:2}}>
              Left: what your GEO signals predict · Right: what AI actually returns today
            </div>
          </div>
          {plxKey&&(
            <Btn sm onClick={runPerplexity} disabled={plxRunning}
              style={{background:"rgba(167,139,250,0.15)",color:"#a78bfa",border:"1px solid rgba(167,139,250,0.3)"}}>
              {plxRunning?"Checking...":plxResults?"↻ Re-Check Live":"Run Live Check"}
            </Btn>
          )}
          {!plxKey&&(
            <span style={{fontSize:11,color:T.ghost,fontStyle:"italic"}}>Add Perplexity API key in Settings for live citation data</span>
          )}
        </div>

        {/* Legend */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",borderBottom:`1px solid ${T.border}`}}>
          <div style={{padding:"10px 20px",borderRight:`1px solid ${T.border}`,background:"rgba(21,180,214,0.04)"}}>
            <div style={{fontSize:10,fontWeight:800,color:T.blue,letterSpacing:1}}>SHARKRANK - SIGNAL POTENTIAL</div>
            <div style={{fontSize:10,color:T.ghost,marginTop:2}}>Claude ranks by schema, GBP, reviews, llms.txt</div>
          </div>
          <div style={{padding:"10px 20px",background:"rgba(167,139,250,0.04)"}}>
            <div style={{fontSize:10,fontWeight:800,color:"#a78bfa",letterSpacing:1}}>PERPLEXITY - ACTUAL CITATIONS</div>
            <div style={{fontSize:10,color:T.ghost,marginTop:2}}>What real AI responses return right now</div>
          </div>
        </div>

        {/* No data states */}
        {!matrixData&&!plxResults&&(
          <div style={{padding:"32px 20px",textAlign:"center",color:T.ghost,fontSize:13}}>
            Run Matrix Analysis and Live Check to see the gap between your signal potential and real AI citations.
          </div>
        )}

        {/* Combined rows — one per query */}
        {(matrixData||plxResults)&&(()=>{
          const queries = matrixData?.queries || plxResults?.checks?.map(c=>c.query) || [];
          const clientBiz = matrixData?.businesses?.find(b=>b.isClient);
          const clientKey = clientBiz?.key;

          return(
            <div>
              {queries.map((q,qi)=>{
                // SharkRank data for this query
                const sharkRank = matrixData ? (matrixData.grid[qi]?.[clientKey] || null) : null;
                const totalBiz  = matrixData?.businesses?.length || 0;
                const sharkWins = sharkRank === 1;

                // Perplexity data for this query
                const plxCheck = plxResults?.checks?.find(c=>c.query===q);
                const plxMentioned = plxCheck?.mentioned;
                const plxAnswer = plxCheck?.answer;
                const plxSources = plxCheck?.sources || [];

                // Gap analysis
                const hasGap    = sharkRank!==null && plxCheck!==null && sharkWins && !plxMentioned;
                const winning   = sharkWins && plxMentioned;
                const opportunity = !sharkWins && !plxMentioned;

                return(
                  <div key={qi} style={{borderBottom:`1px solid ${T.border}`,display:"grid",gridTemplateColumns:"1fr 1fr"}}>
                    {/* Query label spanning both */}
                    <div style={{gridColumn:"1/-1",padding:"8px 20px 0",display:"flex",alignItems:"center",gap:8}}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.ghost} strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                      <span style={{fontSize:11,color:T.ghost,fontStyle:"italic"}}>"{q}"</span>
                      {hasGap&&<span style={{fontSize:9,fontWeight:800,padding:"1px 6px",borderRadius:4,background:`${T.gold}20`,color:T.gold,marginLeft:"auto"}}>⚠ GAP - signals strong, not yet cited</span>}
                      {winning&&<span style={{fontSize:9,fontWeight:800,padding:"1px 6px",borderRadius:4,background:`${T.teal}20`,color:T.teal,marginLeft:"auto"}}>✓ WINNING</span>}
                    </div>

                    {/* SharkRank column */}
                    <div style={{padding:"8px 20px 12px",borderRight:`1px solid ${T.border}`}}>
                      {sharkRank!==null?(
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:36,height:28,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",
                            fontWeight:800,fontSize:14,
                            background:sharkRank===1?"rgba(79,209,160,0.18)":sharkRank===2?"rgba(21,180,214,0.12)":sharkRank<=4?"rgba(249,219,121,0.12)":"rgba(247,85,95,0.1)",
                            color:sharkRank===1?T.teal:sharkRank===2?T.blue:sharkRank<=4?T.gold:T.red}}>
                            #{sharkRank}
                          </div>
                          <div>
                            <div style={{fontSize:12,fontWeight:700,color:sharkRank===1?T.teal:sharkRank<=3?T.text:T.ghost}}>
                              {sharkRank===1?"Ranked #1 by signals":sharkRank<=3?`Ranked #${sharkRank}`:`Ranked #${sharkRank} of ${totalBiz}`}
                            </div>
                            <div style={{fontSize:10,color:T.ghost,marginTop:2}}>based on GEO signal strength</div>
                          </div>
                        </div>
                      ):(
                        <span style={{fontSize:11,color:T.ghost,fontStyle:"italic"}}>Run Matrix Analysis</span>
                      )}
                    </div>

                    {/* Perplexity column */}
                    <div style={{padding:"8px 20px 12px"}}>
                      {plxCheck?(
                        <div>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                            <span style={{fontSize:12,fontWeight:700,color:plxMentioned?T.teal:T.red}}>
                              {plxMentioned?"✓ Cited in AI response":"✗ Not found in AI response"}
                            </span>
                          </div>
                          {plxAnswer&&<div style={{fontSize:10,color:T.ghost,lineHeight:1.5}}>{plxAnswer.slice(0,120)}{plxAnswer.length>120?"...":""}</div>}
                          {plxSources.length>0&&(
                            <div style={{fontSize:9,color:T.ghost,marginTop:3}}>
                              Sources: {plxSources.slice(0,2).join(" · ")}
                            </div>
                          )}
                        </div>
                      ):(
                        <span style={{fontSize:11,color:T.ghost,fontStyle:"italic"}}>{plxKey?"Run Live Check":"Add Perplexity key"}</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Summary gap analysis */}
              {matrixData&&plxResults&&(()=>{
                const gaps     = queries.filter((_,qi)=> matrixData.grid[qi]?.[clientKey]===1 && !plxResults.checks.find(c=>c.query===queries[qi])?.mentioned).length;
                const wins     = queries.filter((_,qi)=> matrixData.grid[qi]?.[clientKey]===1 &&  plxResults.checks.find(c=>c.query===queries[qi])?.mentioned).length;
                const missing  = queries.filter((_,qi)=> matrixData.grid[qi]?.[clientKey]!==1 && !plxResults.checks.find(c=>c.query===queries[qi])?.mentioned).length;
                return(
                  <div style={{padding:"16px 20px",background:"rgba(255,255,255,0.02)",borderTop:`1px solid ${T.border}`,display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:22,fontWeight:800,color:T.teal}}>{wins}</div>
                      <div style={{fontSize:10,color:T.ghost,fontWeight:700,marginTop:2}}>WINNING</div>
                      <div style={{fontSize:9,color:T.ghost}}>strong signals + cited</div>
                    </div>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:22,fontWeight:800,color:T.gold}}>{gaps}</div>
                      <div style={{fontSize:10,color:T.ghost,fontWeight:700,marginTop:2}}>GAPS</div>
                      <div style={{fontSize:9,color:T.ghost}}>strong signals, not yet cited</div>
                    </div>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:22,fontWeight:800,color:T.red}}>{missing}</div>
                      <div style={{fontSize:10,color:T.ghost,fontWeight:700,marginTop:2}}>NEEDS WORK</div>
                      <div style={{fontSize:9,color:T.ghost}}>weak signals + not cited</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </Card>
    </div>
  );
}

// ---------------------------------------------
// CLIENT DASHBOARD
// ---------------------------------------------
const TABS=["OVERVIEW","AUDIT","FAQ","CHECKLIST","REPORTS","SHARKRANK"];

function ClientDashboard({client,onBack,onClientUpdate,isDemoMode=false,demoAudit=null,demoCrawl=null}){
  const [tab,setTab]=useState("OVERVIEW");
  const [deploying,setDeploying]=useState(false);
  const [deployPct,setDeployPct]=useState(0);
  const [deployStep,setDeployStep]=useState("");
  const [showGTMModal,setShowGTMModal]=useState(false);
  const [auditing,setAuditing]=useState(false);
  const [syncing,setSyncing]=useState(false);
  const [gbmData,setGbmDataRaw]=useState(()=>{
    if (isDemoMode && client.rating) return {
      name:client.name, phone:client.phone, address:client.address,
      rating:client.rating, reviews:client.reviews, verified:client.verified==="yes",
      claimed:client.claimed==="yes", gtm:client.gtm||"",
    };
    return LS.get("gbm_"+client.id)||null;
  });
  const setGbmData=(d)=>{ if(!isDemoMode){setGbmDataRaw(d);LS.set("gbm_"+client.id,d);} else setGbmDataRaw(d); };

  // Auto-load demo audit data
  useEffect(()=>{
    if(isDemoMode){
      if(demoAudit) setAuditResults(demoAudit);
      if(demoCrawl) setCrawlData(demoCrawl);
    }
  },[isDemoMode, demoAudit, demoCrawl]);
  const [checkedState,setCheckedState]=useState({});
  const [auditResults,setAuditResults]=useState(null);
  const [editingCity,setEditingCity]=useState(false);
  const [cityVal,setCityVal]=useState(client.city||"");

  const saveCity=async()=>{
    const updated={...client,city:cityVal};
    await ipc("save-client",updated);
    onClientUpdate?.(updated);
    setEditingCity(false);
  };

  const [geoData,setGeoData]=useState(null);
  const runAudit=async()=>{
    if(isDemoMode){
      setAuditing(true);
      setTab("AUDIT");
      await new Promise(r=>setTimeout(r,900));
      if(demoAudit) setAuditResults(demoAudit);
      if(demoCrawl) setCrawlData(demoCrawl);
      setAuditing(false);
      return;
    }
    setAuditing(true);
    setTab("AUDIT");
    const napData = LS.get("nap_"+client.id)||{};
    // Run real GEO checks + site crawl in parallel
    const [geoResult, crawlResult] = await Promise.allSettled([
      runGeoAudit(client, gbmData, napData),
      crawlSite(client.domain || ""),
    ]);
    const geo = geoResult.status==="fulfilled" ? geoResult.value : null;
    const crawlData = crawlResult.status==="fulfilled" ? crawlResult.value : null;
    if (geo) {
      // Regenerate llms.txt now that we have crawl data too
      const richLlms = buildRichLlmsTxt(client, gbmData, napData, crawlData, geo);
      setGeoData({...geo, llmsGenerated: geo.llmsData ? null : richLlms, _clientId: client.id, _domain: client.domain});
    }
    if (crawlData) setCrawlData(crawlData);
    const results=AUDIT_DATA.map(platform=>({
      ...platform,
      signals:platform.signals.map(s=>{
        let status;
        // -- Real checks derived from live crawl, geo data, and user-entered GBP data --
        if(s.signal==="llms.txt Present"){
          status = geo?.llmsData ? "Pass" : "Fail";
        } else if(s.signal==="Wikipedia / Wikidata"){
          status = geo?.wikiUrl ? "Pass" : "Fail";
        } else if(s.signal==="Knowledge Panel"){
          status = gbmData?.verified ? "Warning" : "Fail";
        } else if(s.signal==="Brand Entity Recognition"){
          status = (gbmData?.verified && (geo?.wikiUrl || geo?.llmsData)) ? "Pass"
                 : gbmData?.verified ? "Warning" : "Fail";
        } else if(s.signal==="AI-Cited Content"){
          const blocked = geo?.crawlerMap?.filter(c=>c.status==="Blocked"&&c.tier===1)||[];
          status = blocked.length===0 ? "Pass" : blocked.length<=2 ? "Warning" : "Fail";
        } else if(s.signal==="LocalBusiness Schema"){
          status = crawlData?.localBusiness?.status
            ?? (geo?.crawlerMap?.find(c=>c.name==="Googlebot")?.status==="Blocked" ? "Fail" : "Warning");
        } else if(s.signal==="FAQ Schema"){
          status = crawlData?.faqSchema?.status ?? "Fail";
        } else if(s.signal==="Structured Q&A Coverage"){
          status = crawlData?.qaContent?.status ?? "Fail";
        } else if(s.signal==="Content Freshness"){
          status = crawlData?.contentFreshness?.status ?? "Warning";
        } else if(s.signal==="NAP Consistency"){
          const napData = LS.get("nap_"+client.id)||{};
          const t1Sources = ["nap_google","nap_bing","nap_apple","nap_yelp","nap_facebook","nap_bbb"];
          const t1Checked = t1Sources.filter(id=>napData[id]&&napData[id]!=="unchecked");
          const t1Inaccurate = t1Sources.filter(id=>napData[id]==="inaccurate").length;
          const t1Missing = t1Sources.filter(id=>napData[id]==="missing").length;
          if(t1Checked.length===0) status="Warning"; // not yet audited
          else status = t1Inaccurate>0 ? "Fail" : t1Missing>1 ? "Warning" : "Pass";
        } else if(s.signal==="Review Signals"){
          const rating = parseFloat(gbmData?.rating||0);
          const reviews = parseInt(gbmData?.reviews||0,10);
          status = (reviews>=10&&rating>=4) ? "Pass" : reviews>=10 ? "Warning" : "Fail";
        } else if(s.signal==="Service Categories"){
          status = crawlData?.serviceSchema?.status ?? (gbmData?.category ? "Warning" : "Fail");
        } else if(s.signal==="Bing Places Listing"){
          const napData = LS.get("nap_"+client.id)||{};
          const bingStatus = napData["nap_bing"]||"unchecked";
          status = bingStatus==="accurate" ? "Pass" : bingStatus==="inaccurate" ? "Warning" : bingStatus==="missing" ? "Fail" : "Warning";
        } else if(s.signal==="Category Alignment"){
          const napData = LS.get("nap_"+client.id)||{};
          status = (napData["nap_bing"]||"unchecked")==="accurate" ? "Pass" : "Warning";
        } else if(s.signal==="Photo Quality"){
          status = gbmData?.verified ? "Warning" : "Fail";
        } else if(s.signal==="Business Description"){
          status = gbmData?.verified ? "Warning" : "Fail";
        } else if(s.signal==="Apple Maps Listing"){
          const napData = LS.get("nap_"+client.id)||{};
          const appleStatus = napData["nap_apple"]||"unchecked";
          status = appleStatus==="accurate" ? "Pass" : appleStatus==="inaccurate" ? "Warning" : appleStatus==="missing" ? "Fail" : "Warning";
        } else if(s.signal==="Business Hours"){
          status = gbmData?.verified ? "Warning" : "Fail";
        } else if(s.signal==="Payment Methods"){
          status = gbmData?.verified ? "Pass" : "Warning";
        } else if(s.signal==="Website Link"){
          const napData = LS.get("nap_"+client.id)||{};
          status = (napData["nap_apple"]||"unchecked")==="accurate" ? "Pass" : (napData["nap_apple"]==="missing") ? "Fail" : "Warning";
        } else {
          status = "Warning";
        }
        return{ signal:s.signal, impact:s.impact, status, finding:s.findings[status] };
      })
    }));

    // Calculate composite GEO score from real signals
    const allSignals = results.flatMap(p=>p.signals);
    const passCount = allSignals.filter(s=>s.status==="Pass").length;
    const warnCount = allSignals.filter(s=>s.status==="Warning").length;
    const totalSignals = allSignals.length || 1;
    // Weight: Pass=1.0, Warning=0.5, Fail=0
    const rawScore = Math.round(((passCount + warnCount*0.5) / totalSignals) * 100);
    // Also factor in crawler score from geoData
    const crawlerScore = geoResult.status==="fulfilled" ? (geoResult.value?.score||0) : 0;
    const maxCrawler = geoResult.status==="fulfilled" ? (geoResult.value?.maxScore||40) : 40;
    const crawlerPct = Math.round((crawlerScore/maxCrawler)*100);
    // Blended: 60% signal score, 40% crawler score
    const finalScore = Math.round(rawScore*0.6 + crawlerPct*0.4);

    // Save snapshot to history for lookback chart
    saveAuditSnapshot(client.id, finalScore);

    // Update client score in persistent storage
    const updatedClient = {...client, score:finalScore, grade:gradeFromScore(finalScore), lastUpdated:new Date().toLocaleDateString("default",{month:"short",day:"numeric",year:"numeric"})};
    await ipc("save-client", updatedClient);
    onClientUpdate?.(updatedClient);

    setAuditResults(results);
    setAuditing(false);
  };

  // expose crawlData to AuditTab for showing crawl details
  const [crawlData, setCrawlData] = useState(null);

  const gmbSync=async()=>{
    setSyncing(true);
    await new Promise(r=>setTimeout(r,1200));
    // Use the city the user has set  don't invent an address
    const city = cityVal || client.city || "";
    const hasGBP = !!city; // treat as having a GBP only if city is set
    setGbmData({
      name: client.name,
      city: city,
      // Only show address if we have a city  don't fabricate one
      address: city ? `${client.name}, ${city}` : null,
      phone: null, // unknown until real API connected
      verified: false, // not verified  no GMB claimed yet
      gbpClaimed: false,
      rating: null,
      reviews: null,
      category: client.businessType,
      status: city ? "unverified_listing" : "no_gbp",
    });
    setSyncing(false);
  };
  const downloadEventForm = () => {
    const bizName    = gbmData?.name    || client.name || "";
    const bizAddress = gbmData?.address || (client.city ? `${client.name}, ${client.city}` : "");
    const bizPhone   = gbmData?.phone   || "";
    const bizUrl     = client.domain ? (client.domain.startsWith("http") ? client.domain : "https://"+client.domain) : "";
    const bizType    = client.businessType || "LocalBusiness";
    const toCSV = rows => rows.map(r => r.map(c => '"'+String(c).replace(/"/g,'""')+'"').join(",")).join("\n");
    const rows = [
      ["Business Name","Business Address","Business Phone","Business Website","Business Type","GTM Container ID","Event Name","Start Date (YYYY-MM-DD)","End Date (YYYY-MM-DD)","Location / Venue","Description","Organizer / Host","Event URL"],
      [bizName, bizAddress, bizPhone, bizUrl, bizType, client.gtm||"", "Example: Chamber Networking Night","2026-04-15","2026-04-15","St. Petersburg Chamber","Monthly networking mixer","St. Pete Chamber","https://example.com"],
      ["","","","","","","Fill in event name","Date attended","End date if multi-day","Where it was held","Brief description","Who hosted it","Link to event page"],
    ];
    const blob = new Blob([toCSV(rows)], { type:"text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = client.name.replace(/\s+/g,"-")+"-Event-Intake-Form.csv";
    a.click();
  };

  // CSV Upload - parse returned CSV, match business name to this client, inject event schema
  const uploadEventCSV = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { alert("CSV appears empty or has no data rows."); return; }
      // Parse header
      const parseRow = (line) => {
        const out = []; let cur = "", inQ = false;
        for (const ch of line) {
          if (ch === '"') { inQ = !inQ; }
          else if (ch === ',' && !inQ) { out.push(cur.trim()); cur = ""; }
          else { cur += ch; }
        }
        out.push(cur.trim()); return out;
      };
      const headers = parseRow(lines[0]).map(h => h.toLowerCase().replace(/\s+/g,"_"));
      const dataRows = lines.slice(1).filter(l => {
        const row = parseRow(l);
        return row.some(v => v && v !== '""');
      });
      if (!dataRows.length) { alert("No data rows found in CSV."); return; }
      // Match rows to this client by business name
      const nameIdx = headers.indexOf("business_name");
      const matched = dataRows.filter(line => {
        if (nameIdx < 0) return true; // no name col → accept all
        const rowName = parseRow(line)[nameIdx]?.replace(/^"|"$/g,"").toLowerCase() || "";
        return rowName.includes(client.name.toLowerCase().split(" ")[0].toLowerCase()) ||
               client.name.toLowerCase().includes(rowName.split(" ")[0]);
      });
      if (!matched.length) {
        alert(`No rows matched "${client.name}" in the Business Name column.\nMake sure the CSV was generated for this client.`);
        return;
      }
      // Build event schema objects from each matched row
      const getVal = (row, key) => {
        const idx = headers.indexOf(key); if (idx < 0) return "";
        return parseRow(row)[idx]?.replace(/^"|"$/g,"") || "";
      };
      const events = matched.map(row => ({
        "@context": "https://schema.org",
        "@type": "Event",
        "name":        getVal(row,"event_name"),
        "startDate":   getVal(row,"start_date_(yyyy-mm-dd)"),
        "endDate":     getVal(row,"end_date_(yyyy-mm-dd)") || getVal(row,"start_date_(yyyy-mm-dd)"),
        "location": {
          "@type":   "Place",
          "name":    getVal(row,"location_/_venue"),
          "address": getVal(row,"business_address") || gbmData?.address || client.city || "",
        },
        "description":  getVal(row,"description"),
        "organizer": {
          "@type": "Organization",
          "name":  getVal(row,"organizer_/_host"),
          "url":   getVal(row,"event_url") || "",
        },
        "performer": {
          "@type": "LocalBusiness",
          "name":  client.name,
          "url":   client.domain ? (client.domain.startsWith("http") ? client.domain : "https://"+client.domain) : "",
        }
      }));
      // Save to client's event schema store
      const evKey = "events_"+client.id;
      const existing = LS.get(evKey) || [];
      const merged = [...events, ...existing].slice(0,50);
      LS.set(evKey, merged);
      // Auto-inject via GTM if container is set
      if (client.gtm) {
        setDeploying(true); setDeployPct(0);
        const steps = [
          {p:20, l:"Parsing event schema..."},
          {p:45, l:`Pushing ${events.length} event(s) to GTM...`},
          {p:70, l:"Publishing container..."},
          {p:90, l:"Verifying injection..."},
          {p:100,l:"Complete!"},
        ];
        for (const s of steps) { await new Promise(r=>setTimeout(r,600)); setDeployPct(s.p); setDeployStep(s.l); }
        // Record injection history
        const injKey = "inject_history_"+client.id;
        const injHist = LS.get(injKey) || [];
        injHist.unshift({ month: new Date().toLocaleString("default",{month:"long",year:"numeric"}), ts: new Date().toISOString(), gtm: client.gtm, domain: client.domain, events: events.length, source:"csv" });
        LS.set(injKey, injHist.slice(0,24));
        setTimeout(()=>setDeploying(false), 800);
        alert(`✓ ${events.length} event schema(s) imported and queued for injection via ${client.gtm}`);
      } else {
        alert(`✓ ${events.length} event schema(s) imported for "${client.name}".\nAdd a GTM Container ID to enable auto-injection.`);
      }
    };
    reader.readAsText(file);
    e.target.value = ""; // reset so same file can be re-uploaded
  };

  // Token refresh - silently refresh expired OAuth token using stored refresh_token
  const refreshOAuthToken = async () => {
    const stored = LS.get("gmb_token");
    if (!stored?.refreshToken) return null;
    const settings = LS.get("settings") || {};
    try {
      const td = await refreshGmbToken(stored.refreshToken, settings.gmbClientId||"", settings.gmbClientSecret||"");
      const newToken = { accessToken: td.access_token, refreshToken: td.refresh_token||stored.refreshToken, expiry: Date.now()+(td.expires_in||3600)*1000 };
      LS.set("gmb_token", newToken);
      return newToken.accessToken;
    } catch { return null; }
  };

  const deployGTM=()=>setShowGTMModal(true);

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{background:T.white,borderBottom:`1px solid ${T.border}`,padding:"14px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <button onClick={onBack} style={{background:T.input,border:"none",borderRadius:10,width:34,height:34,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}>?</button>
            <div>
              <div style={{fontWeight:800,fontSize:16,color:T.text}}>{client.name}</div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
                <span style={{fontSize:12,color:T.ghost}}>{client.domain}</span>
                {editingCity?(
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    <input value={cityVal} onChange={e=>setCityVal(e.target.value)}
                      autoFocus
                      onKeyDown={e=>{if(e.key==="Enter")saveCity();if(e.key==="Escape")setEditingCity(false);}}
                      style={{fontSize:12,color:T.text,background:T.input,border:`1px solid ${T.blue}`,
                        borderRadius:6,padding:"2px 8px",outline:"none",width:160,fontFamily:"inherit"}}/>
                    <button onClick={saveCity} style={{fontSize:11,color:T.white,background:T.blue,border:"none",
                      borderRadius:6,padding:"2px 8px",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Save</button>
                    <button onClick={()=>setEditingCity(false)} style={{fontSize:11,color:T.ghost,background:T.input,
                      border:"none",borderRadius:6,padding:"2px 8px",cursor:"pointer",fontFamily:"inherit"}}>?</button>
                  </div>
                ):(
                  <button onClick={()=>setEditingCity(true)}
                    style={{fontSize:11,color:cityVal?T.blue:T.ghost,background:"transparent",border:"none",
                      cursor:"pointer",display:"flex",alignItems:"center",gap:3,fontFamily:"inherit",padding:0}}>
                    {cityVal?` ${cityVal}`:"+ Add City"} ?
                  </button>
                )}
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn sm onClick={gmbSync} disabled={syncing}>{syncing?"? Syncing...":"? GMB Sync"}</Btn>
            <Btn sm onClick={runAudit} disabled={auditing}>{auditing?"? Auditing...":"? Run Audit"}</Btn>
            <Btn sm onClick={deployGTM} disabled={deploying||!client.gtm} title={!client.gtm?"Add GTM Container ID to client first":""}>Deploy GTM{!client.gtm&&<span style={{marginLeft:5,fontSize:9,opacity:0.7}}>⚠</span>}</Btn>
            <Btn sm onClick={downloadEventForm}>⬇ Event Form</Btn>
            <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:12,
              cursor:"pointer",fontSize:12,fontWeight:700,background:`rgba(79,209,160,0.12)`,
              color:T.teal,border:`1px solid rgba(79,209,160,0.3)`,whiteSpace:"nowrap",transition:"all 0.2s"}}
              onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-1px)";}}
              onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";}}>
              ⬆ Upload CSV
              <input type="file" accept=".csv" onChange={uploadEventCSV} style={{display:"none"}}/>
            </label>
            <Btn sm variant="primary" onClick={()=>setTab("REPORTS")}>📄 Gen Report</Btn>
          </div>
        </div>
        {deploying&&(
          <div style={{marginTop:12}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:6}}>
              <span style={{color:T.ghost}}>{deployStep}</span>
              <span style={{color:T.blue,fontWeight:700}}>{deployPct}%</span>
            </div>
            <div style={{height:6,background:T.border,borderRadius:4,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${deployPct}%`,background:`linear-gradient(90deg,${T.blue},${T.blue})`,transition:"width 0.5s ease",borderRadius:4}}/>
            </div>
          </div>
        )}
      </div>
      <div style={{background:T.white,borderBottom:`1px solid ${T.border}`,padding:"0 24px",flexShrink:0,display:"flex",gap:28}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)}
            style={{padding:"14px 0",border:"none",background:"transparent",cursor:"pointer",fontSize:12,fontWeight:700,
              letterSpacing:1,fontFamily:"inherit",color:tab===t?T.blue:T.ghost,
              borderBottom:tab===t?`2px solid ${T.blue}`:"2px solid transparent",transition:"all 0.15s"}}>
            {t}
          </button>
        ))}
      </div>
      <div style={{flex:1,overflow:"auto",padding:28,background:T.bg}}>
        {tab==="OVERVIEW"  &&<OverviewTab client={client} gbmData={gbmData} setGbmData={setGbmData} syncing={syncing} onSync={gmbSync} onTabSwitch={setTab}/>}
        {tab==="AUDIT"     &&<AuditTab auditResults={auditResults} auditing={auditing} clientId={client.id} client={client} geoData={geoData} crawlData={crawlData} clientGtm={client.gtm}/>}
        {tab==="FAQ"       &&<FAQSchemaInjector client={client}/>}
        {tab==="CHECKLIST" &&<ChecklistTab clientId={client.id} auditResults={auditResults} gbmData={gbmData} crawlData={crawlData} geoData={geoData} onChecklistChange={setCheckedState}/>}
        {tab==="REPORTS"   &&<ReportsTab client={client} checkedState={checkedState}/>}
        {tab==="SHARKRANK" &&<SharkRankTab client={client}/>}
      </div>
      {showGTMModal&&<GTMIntegration client={client} onClose={()=>setShowGTMModal(false)}/>}
    </div>
  );
}

// ---------------------------------------------
// PODCAST CITATION ENGINE
// ---------------------------------------------
// Replace the PodcastCitationEngine component with this corrected version:

function PodcastCitationEngine({ clients }) {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [selectedClient, setSelectedClient] = useState(clients[0]?.id || "");
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [episodeData, setEpisodeData] = useState(null);
  const [schemaBlocks, setSchemaBlocks] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const [injectStatus, setInjectStatus] = useState("idle");
  const [injectPct, setInjectPct] = useState(0);
  const [injectError, setInjectError] = useState("");
  const [expandedSchema, setExpandedSchema] = useState(false);
  const [history, setHistory] = useState(() => LS.get("podcast_history") || []);
  const [errorMsg, setErrorMsg] = useState("");
  const [keyFound, setKeyFound] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [manualBusinesses, setManualBusinesses] = useState(() => LS.get("podcast_manual_biz_" + (clients[0]?.id || "")) || []);
  const [showAddBiz, setShowAddBiz] = useState(false);
  const [newBiz, setNewBiz] = useState({ name:"", type:"LocalBusiness", address:"", phone:"", website:"", description:"" });
  const [guestPackages, setGuestPackages] = useState([]);
  const [copiedPkg, setCopiedPkg] = useState(null);
  const [socialContent, setSocialContent] = useState([]);
  const [generatingSocial, setGeneratingSocial] = useState(false);
  const [socialTab, setSocialTab] = useState('linkedin');
  const [copiedPost, setCopiedPost] = useState(null);

  useEffect(() => { getAnthropicKey().then(k => setKeyFound(!!k)); }, []);
  useEffect(() => {
    const saved = LS.get("podcast_manual_biz_" + selectedClient) || [];
    setManualBusinesses(saved);
  }, [selectedClient]);

  const generateSocialContent = async () => {
    if (!episodeData || !parsedData) return;
    setGeneratingSocial(true);
    const client = clients.find(c => String(c.id) === String(selectedClient));
    const guests = (parsedData.guests || []).map(g => g.name).join(', ');
    const topics = (parsedData.mainTopics || []).slice(0, 5).join(', ');
    const quotes = (parsedData.guests || []).flatMap(g => g.keyQuotes || []).slice(0, 6);
    const showName = parsedData.channel || client?.name || 'the podcast';
    const epUrl = `https://www.youtube.com/watch?v=${episodeData.videoId}`;

    try {
      const resp = await claudeAPI({
        max_tokens: 4000,
        messages: [{
          role: 'user',
          content: `You are a social media content strategist. Create 10 high-performing social media posts from this podcast episode.

Episode: "${episodeData.title}"
Show: ${showName}
Guests: ${guests || 'Host only'}
Topics: ${topics}
Key quotes: ${quotes.map((q,i) => (i+1)+'. "'+q+'"').join('\n')}
Episode URL: ${epUrl}

Create exactly 10 posts. Mix these formats:
- 3 LinkedIn posts (professional, 150-200 words, with 3-5 hashtags)
- 3 Twitter/X posts (under 280 chars, punchy, 2-3 hashtags)
- 2 Instagram captions (engaging, storytelling, 5-8 hashtags)
- 2 Facebook posts (conversational, 100-150 words, 2-3 hashtags)

Rules:
- Each post must feel native to its platform
- Pull from the most insightful moments — quotes, stats, frameworks
- Include a call to action linking to the episode
- Vary the angle: one lesson, one quote, one controversy, one how-to, etc.
- Do NOT use generic phrases like "excited to share" or "great conversation"

Output ONLY a JSON array, no markdown:
[{"platform":"linkedin","hook":"opening line","body":"full post text","hashtags":["tag1","tag2"],"angle":"what makes this post unique"},...]`
        }],
      });

      const text = (resp.content?.[0]?.text || '').trim();
      const clean = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/, '');
      const posts = JSON.parse(clean);
      if (Array.isArray(posts) && posts.length) {
        setSocialContent(posts);
        // Save to localStorage
        LS.set('social_content_' + selectedClient + '_' + episodeData.videoId, posts);
      }
    } catch(e) {
      console.error('Social content generation failed:', e.message);
      alert('Social content generation failed: ' + e.message);
    }
    setGeneratingSocial(false);
  };

  const buildGuestHTMLCard = (g, parsed, client) => {
    const showName  = parsed.channel || client?.name || "Let's Talk Supply Chain";
    const epTitle   = parsed.title   || "Podcast Episode";
    const epUrl     = parsed.youtubeUrl || (client?.domain ? (client.domain.startsWith("http") ? client.domain : "https://" + client.domain) : "#");
    const topics    = Array.isArray(g.expertise) ? g.expertise : (g.expertise ? [g.expertise] : []);
    const quotes    = g.keyQuotes || g.keyPoints || [];
    const topicStr  = topics.slice(0, 5).join(", ") || "industry insights";

    const personJSON = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": g.name,
      ...(g.title   && { "jobTitle": g.title }),
      ...(g.company && { "worksFor": { "@type": "Organization", "name": g.company, ...(g.url && { "url": g.url }) } }),
      ...(topics.length && { "knowsAbout": topics }),
      ...(quotes.length && { "description": quotes.slice(0, 2).join(". ") }),
      "subjectOf": {
        "@type": "PodcastEpisode",
        "name": epTitle,
        "url": epUrl,
        "partOfSeries": { "@type": "PodcastSeries", "name": showName, "url": epUrl }
      },
      ...(g.url && { "sameAs": [g.url] }),
    };

    const speakable = quotes.length ? {
      "@context": "https://schema.org",
      "@type": "SpeakableSpecification",
      "name": `${g.name} - Key Insights from ${showName}`,
      "cssSelector": [".guest-quote"],
    } : null;

    const schemaTag = [personJSON, speakable].filter(Boolean)
      .map(s => `<script type="application/ld+json">\n${JSON.stringify(s, null, 2)}\n<\/script>`)
      .join("\n\n");

    const quotesHTML = quotes.slice(0, 3).map(q =>
      `  <blockquote class="guest-quote" style="margin:0 0 12px;padding:12px 18px;background:#f8f9fa;border-left:3px solid #4318FF;border-radius:0 8px 8px 0;font-size:14px;color:#2D3748;font-style:italic;line-height:1.6;">"${q}"</blockquote>`
    ).join("\n");

    return `<!-- ✦ AI Citation Card - paste anywhere on your website ✦ -->
<article itemscope itemtype="https://schema.org/Clip" style="border:1px solid #E2E8F0;border-radius:12px;padding:24px 28px;margin:24px 0;font-family:system-ui,-apple-system,sans-serif;max-width:680px;">
  <p style="margin:0 0 6px;font-size:11px;color:#A0AEC0;text-transform:uppercase;letter-spacing:1.5px;font-weight:700;">As Featured On</p>
  <h2 itemprop="name" style="margin:0 0 16px;font-size:20px;color:#1A2332;font-weight:800;">${showName}</h2>

  <div style="display:flex;align-items:flex-start;gap:14px;margin-bottom:16px;">
    <div>
      <p style="margin:0 0 4px;font-size:15px;font-weight:700;color:#1A2332;" itemprop="actor" itemscope itemtype="https://schema.org/Person">
        <span itemprop="name">${g.name}</span>${g.title ? ` &mdash; <span itemprop="jobTitle" style="font-weight:400;color:#4A5568;">${g.title}</span>` : ""}
      </p>
      ${g.company ? `<p style="margin:0 0 4px;font-size:13px;color:#718096;" itemprop="worksFor" itemscope itemtype="https://schema.org/Organization"><span itemprop="name">${g.company}</span></p>` : ""}
      <p style="margin:0;font-size:12px;color:#A0AEC0;">Episode: <em itemprop="isPartOf">${epTitle}</em></p>
    </div>
  </div>

  ${topics.length ? `<p style="margin:0 0 14px;font-size:12px;color:#718096;"><strong style="color:#4A5568;">Topics Covered:</strong> <span itemprop="about">${topicStr}</span></p>` : ""}

${quotesHTML}

  <p style="margin:16px 0 0;">
    <a href="${epUrl}" itemprop="url" rel="noopener" style="display:inline-block;background:#4318FF;color:#fff;font-size:13px;font-weight:700;padding:9px 18px;border-radius:8px;text-decoration:none;">🎙 Listen on ${showName}</a>
    ${g.url ? `&nbsp;&nbsp;<a href="${g.url}" rel="noopener" style="font-size:13px;color:#4318FF;font-weight:600;text-decoration:none;">Visit Website →</a>` : ""}
  </p>
</article>

${schemaTag}`;
  };

  const buildSchema = (parsed, manualBizList) => {
    const client       = clients.find(c => String(c.id) === String(selectedClient));
    const epUrl        = parsed.youtubeUrl || (episodeData?.videoId ? `https://www.youtube.com/watch?v=${episodeData.videoId}` : null);
    const clientDomain = client?.domain ? (client.domain.startsWith("http") ? client.domain : "https://" + client.domain) : null;
    const showName     = parsed.channel || client?.name || "Podcast";
    const epTitle      = parsed.title   || "Podcast Episode";

    // 1 - PodcastEpisode (top-level entity - the most important block)
    const podcastEpisodeBlock = {
      "@context": "https://schema.org",
      "@type":    "PodcastEpisode",
      ...(epUrl && { "@id": epUrl + "#episode" }),
      "name":     epTitle,
      ...(epUrl && { "url": epUrl }),
      ...(parsed.publishDate && { "datePublished": parsed.publishDate }),
      ...(parsed.episodeNumber && { "episodeNumber": String(parsed.episodeNumber) }),
      ...(parsed.summary && { "abstract": parsed.summary }),
      ...(parsed.mainTopics?.length && { "keywords": parsed.mainTopics.join(", ") }),
      "partOfSeries": {
        "@type": "PodcastSeries",
        "name":  showName,
        ...(clientDomain && { "url": clientDomain }),
      },
      ...(client?.name && clientDomain && {
        "publisher": { "@type": "Organization", "name": client.name, "url": clientDomain }
      }),
      ...((parsed.guests || []).length && {
        "actor": (parsed.guests || []).map(g => ({
          "@type": "Person",
          "name":  g.name,
          ...(g.title   && { "jobTitle": g.title }),
          ...(g.company && { "worksFor": { "@type": "Organization", "name": g.company } }),
        }))
      }),
    };

    // 2 - Person blocks (one per guest, richly populated)
    const guestBlocks = (parsed.guests || []).map(g => {
      const topics = Array.isArray(g.expertise) ? g.expertise : (g.expertise ? [g.expertise] : []);
      const quotes = g.keyQuotes || g.keyPoints || [];
      const obj = {
        "@context": "https://schema.org",
        "@type":    "Person",
        "name":     g.name,
      };
      if (g.url)            obj["@id"]       = g.url + "#person";
      if (g.title)          obj.jobTitle      = g.title;
      if (g.company)        obj.worksFor      = { "@type": "Organization", "name": g.company, ...(g.url && { "url": g.url }) };
      if (g.url)            obj.sameAs        = [g.url];
      if (g.bio)            obj.description   = g.bio;
      else if (quotes.length) obj.description = quotes[0];
      if (topics.length)    obj.knowsAbout    = topics;
      if (quotes.length)    obj.award         = undefined; // placeholder cleared below
      delete obj.award;
      obj.subjectOf = {
        "@type": "PodcastEpisode",
        "name":  epTitle,
        ...(epUrl && { "url": epUrl }),
        "partOfSeries": { "@type": "PodcastSeries", "name": showName, ...(clientDomain && { "url": clientDomain }) },
      };
      return obj;
    });

    // 3 - Clip blocks for key quotes (excellent for AI & voice search citations)
    const clipBlocks = (parsed.guests || []).flatMap(g => {
      const quotes = g.keyQuotes || [];
      return quotes.slice(0, 2).filter(Boolean).map(q => ({
        "@context":     "https://schema.org",
        "@type":        "Clip",
        "name":         `${g.name}: ${q.slice(0, 70)}${q.length > 70 ? "\u2026" : ""}`,
        "description":  q,
        ...(epUrl && { "url": epUrl }),
        "actor":        { "@type": "Person", "name": g.name, ...(g.title && { "jobTitle": g.title }) },
        "partOfEpisode": { "@type": "PodcastEpisode", "name": epTitle, ...(epUrl && { "url": epUrl }) },
      }));
    });

    // 4 - Organization blocks
    const orgBlocks = (parsed.organizations || []).map(o => {
      const obj = { "@context": "https://schema.org", "@type": o.type || "Organization", "name": o.name };
      if (o.url)     obj.url         = o.url;
      if (o.context) obj.description = o.context;
      return obj;
    });

    // 5 - Manual business blocks
    const bizBlocks = (manualBizList || []).map(b => {
      const obj = { "@context": "https://schema.org", "@type": b.type || "LocalBusiness", "name": b.name };
      if (b.address)     obj.address     = { "@type": "PostalAddress", "streetAddress": b.address };
      if (b.phone)       obj.telephone   = b.phone;
      if (b.website)     obj.url         = b.website;
      if (b.description) obj.description = b.description;
      return obj;
    });

    const blocks = [
      podcastEpisodeBlock,
      ...guestBlocks,
      ...clipBlocks,
      ...orgBlocks,
      ...bizBlocks,
    ];
    setSchemaBlocks(blocks);

    // Build per-guest authority packages (HTML cards for embedding)
    const pkgs = (parsed.guests || []).map(g => {
      const topics = Array.isArray(g.expertise) ? g.expertise : (g.expertise ? [g.expertise] : []);
      const quotes = g.keyQuotes || g.keyPoints || [];
      return {
        id:        g.name.replace(/\s+/g, "_"),
        guestName: g.name,
        jobTitle:  g.title,
        company:   g.company,
        website:   g.url,
        topics,
        quotes,
        htmlCard:  buildGuestHTMLCard(g, parsed, client),
      };
    });
    setGuestPackages(pkgs);

    return blocks;
  };

  const saveManualBiz = (list) => {
    setManualBusinesses(list);
    LS.set("podcast_manual_biz_" + selectedClient, list);
  };

  const addManualBiz = () => {
    if (!newBiz.name.trim()) return;
    const updated = [...manualBusinesses, { ...newBiz, id: Date.now().toString() }];
    saveManualBiz(updated);
    setNewBiz({ name:"", type:"LocalBusiness", address:"", phone:"", website:"", description:"" });
    setShowAddBiz(false);
    if (parsedData) buildSchema(parsedData, updated);
  };

  const removeManualBiz = (id) => {
    const updated = manualBusinesses.filter(b => b.id !== id);
    saveManualBiz(updated);
    if (parsedData) buildSchema(parsedData, updated);
  };

  const extractVideoId = (url) => {
    const patterns = [/(?:v=|\/embed\/|youtu\.be\/|\/v\/|\/watch\?v=)([a-zA-Z0-9_-]{11})/, /^([a-zA-Z0-9_-]{11})$/];
    for (const p of patterns) { const m = url.match(p); if (m) return m[1]; }
    return null;
  };

  const step = async (pct, label, delay = 500) => {
    setProgress(pct); setProgressLabel(label);
    await new Promise(r => setTimeout(r, delay));
  };

  const run = async () => {
    const videoId = extractVideoId(youtubeUrl.trim());
    if (!videoId) { setErrorMsg("Invalid YouTube URL or video ID."); setPhase("error"); return; }
    if (!selectedClient) { setErrorMsg("Select a client first."); setPhase("error"); return; }
    setPhase("running"); setErrorMsg("");
    setEpisodeData(null); setSchemaBlocks([]); setSnippets([]); setProgress(0);
    const ytUrl = `https://www.youtube.com/watch?v=${videoId}`;
    const client = clients.find(c => String(c.id) === String(selectedClient));
    try {
      await step(8, "Fetching YouTube page\u2026");
      let title = "", channelName = "", description = "", transcript = "";
      let pageHtml = null;
      try { pageHtml = await fetchProxy(ytUrl); } catch(e) {}
      if (pageHtml) {
        const ogTitle = pageHtml.match(/<meta property="og:title" content="([^"]+)"/i);
        if (ogTitle) title = ogTitle[1].trim();
        else { const t = pageHtml.match(/<title>([^<]+)<\/title>/i); if (t) title = t[1].replace(/ - YouTube$/, "").trim(); }
        const ogDesc = pageHtml.match(/<meta property="og:description" content="([^"]+)"/i);
        if (ogDesc) description = ogDesc[1].trim();
        const ch1 = pageHtml.match(/"ownerChannelName":"([^"]+)"/);
        if (ch1) channelName = ch1[1];
        else { const ch2 = pageHtml.match(/"author":"([^"]+)"/); if (ch2) channelName = ch2[1]; }
        const descMatch = pageHtml.match(/"description":\{"simpleText":"((?:[^"\\]|\\.)*)"/);
        if (descMatch) description = descMatch[1].replace(/\\n/g, "\n").replace(/\\"/g, '"');
      }
      // oEmbed fallback - CORS-enabled, no proxy needed, works for any public video
      if (!title || !channelName) {
        try {
          const oe = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(ytUrl)}&format=json`, { signal: AbortSignal.timeout(5000) });
          if (oe.ok) {
            const oeData = await oe.json();
            if (!title && oeData.title) title = oeData.title;
            if (!channelName && oeData.author_name) channelName = oeData.author_name;
          }
        } catch(e) {}
      }
      await step(22, "Fetching transcript\u2026");
      try {
        const capJson = await fetchProxy(`https://www.youtube.com/api/timedtext?lang=en&v=${videoId}&fmt=json3`);
        if (capJson && capJson.includes('"events"')) {
          const parsed = JSON.parse(capJson);
          transcript = (parsed.events || []).flatMap(e => (e.segs || []).map(s => s.utf8 || "")).join(" ").replace(/\s+/g, " ").trim();
        }
      } catch(e) {}
      if (!transcript) {
        try {
          const capXml = await fetchProxy(`https://www.youtube.com/api/timedtext?lang=en&v=${videoId}`);
          if (capXml) transcript = capXml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        } catch(e) {}
      }
      const realContext = [
        title ? `VIDEO TITLE: ${title}` : null,
        channelName ? `CHANNEL: ${channelName}` : null,
        description ? `DESCRIPTION:\n${description.slice(0, 2000)}` : null,
        transcript ? `TRANSCRIPT (first 12000 chars):\n${transcript.slice(0, 12000)}` : null,
      ].filter(Boolean).join("\n\n");
      if (!realContext) throw new Error("Could not fetch any content from this video. It may be private or unavailable.");
      setEpisodeData({ title: title || "Unknown Episode", channelName: channelName || "Unknown Channel", description, videoId });
      await step(42, "Extracting guests & building schema\u2026");
      const extractResp = await claudeAPI({
        model: "gpt-4o",
        max_tokens: 4096,
        messages: [
          {
            role: "system",
            content: "You are a podcast data extraction specialist for JSON-LD schema generation. Extract ONLY information explicitly stated in the provided content - never infer, invent, or hallucinate names, titles, companies, URLs, or quotes. When extracting quotes, prefer verbatim or near-verbatim statements over paraphrases. Your output must be valid JSON with no markdown formatting."
          },
          {
            role: "user",
            content: `Extract all structured data from this podcast episode for JSON-LD schema markup. The hosting business is: ${client?.name || "unknown"} (${client?.domain || ""}).

CONTENT:
${realContext}

Return ONLY valid JSON (no markdown, no code fences, no commentary):
{
  "title": "exact episode title from content",
  "channel": "podcast show name",
  "episodeNumber": "episode number if explicitly stated, else null",
  "publishDate": "date if mentioned in YYYY-MM-DD format, else null",
  "summary": "2-3 sentence factual summary covering main themes discussed",
  "mainTopics": ["primary topic discussed", "secondary topic", "third topic"],
  "guests": [
    {
      "name": "Full Name exactly as introduced",
      "title": "current job title as stated",
      "company": "current employer or company name as stated",
      "url": "personal website or LinkedIn URL only if explicitly mentioned in content",
      "bio": "1-2 sentence bio using only facts stated in the content",
      "expertise": ["specific area 1", "specific area 2", "specific area 3", "specific area 4", "specific area 5"],
      "keyPoints": ["specific fact or insight they stated", "another distinct insight", "third concrete takeaway"],
      "keyQuotes": ["verbatim or near-verbatim quote - choose the most insightful or quotable line", "second strong quote or paraphrase with high information density", "third notable statement worth highlighting"]
    }
  ],
  "organizations": [
    {
      "name": "Organization name as stated",
      "type": "Company|NonProfit|GovernmentOrganization|EducationalOrganization|Corporation",
      "url": "website only if explicitly mentioned",
      "context": "specific reason this organization was mentioned and its relevance"
    }
  ]
}`
          }
        ]
      });
      await step(65, "Parsing results\u2026");
      const rawText = (extractResp?.content || []).filter(b => b.type === "text").map(b => b.text).join("\n");
      let parsed = { title: title || "Unknown Episode", channel: channelName || "Unknown Channel", summary: description, guests: [], organizations: [] };
      try {
        const jsonMatch = rawText.match(/\{[\s\S]*\}/);
        if (jsonMatch) parsed = { ...parsed, ...JSON.parse(jsonMatch[0]) };
      } catch(e) {}
      const finalTitle = parsed.title || title || "Unknown Episode";
      const finalChannel = parsed.channel || channelName || "Unknown Channel";
      setEpisodeData({ title: finalTitle, channelName: finalChannel, description: parsed.summary || description, videoId });
      await step(70, "Building schema blocks\u2026");
      parsed.youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
      setParsedData(parsed);
      const blocks = buildSchema(parsed, manualBusinesses);
      await step(76, "Checking AI visibility\u2026");
      const guests = parsed.guests || [];
      const mainTopic = (parsed.mainTopics || [])[0] || "";
      // Two focused queries: primary guest (name + their expertise) and the show (name + topic)
      const searchQueries = [
        guests[0]
          ? `${guests[0].name} ${((guests[0].expertise || [])[0] || mainTopic)}`.trim()
          : null,
        client?.name && mainTopic
          ? `${client.name} ${mainTopic}`
          : client?.name || null,
      ].filter(Boolean);
      const snippetResults = [];
      for (let i = 0; i < searchQueries.length; i++) {
        const q = searchQueries[i];
        await step(76 + Math.round((i / searchQueries.length) * 20), `Checking: "${q.slice(0, 42)}\u2026"`);
        try {
          const sr = await claudeAPI({
            model: "gpt-4o",
            max_tokens: 400,
            messages: [{
              role: "user",
              content: `You are a knowledge verification assistant. Check your training data for verified, accurate information about this person or organization specifically in the context of ${mainTopic || "their industry"}.\n\nQuery: ${q}\n\nOnly respond if you have specific, confirmed knowledge of this person or brand. If you are uncertain, output VERIFIED: no and nothing else.\n\nIf verified, write 2-3 sentences describing who they are, what they are known for, and their role in ${mainTopic || "the industry"}. Then output:\nVERIFIED: yes\nSOURCE: their website or most relevant URL, or none`
            }]
          });
          const txt = (sr && sr.content ? sr.content : []).filter(b => b.type === "text").map(b => b.text).join("\n");
          const verifiedM = txt.match(/VERIFIED:\s*(yes|no)/i);
          const verified = verifiedM ? verifiedM[1].toLowerCase() === "yes" : false;
          if (!verified) continue; // skip unverifiable results
          const sourceM = txt.match(/SOURCE:\s*(\S+)/i);
          const answer = txt.replace(/VERIFIED:.*$/im, "").replace(/SOURCE:.*$/im, "").trim();
          snippetResults.push({
            query: q,
            answer: answer.slice(0, 380),
            found: true,
            source: sourceM && sourceM[1].toLowerCase() !== "none" ? sourceM[1] : null,
          });
        } catch(e) {
          // skip on error - don't show unverified placeholder
        }
        await new Promise(r => setTimeout(r, 150));
      }
      setSnippets(snippetResults);
      await step(100, "Done!", 300);
      const record = {
        id: Date.now().toString(), ts: new Date().toISOString(),
        clientId: selectedClient, clientName: client && client.name,
        episodeTitle: finalTitle, videoId, channelName: finalChannel,
        schemaCount: blocks.length, gtm: client && client.gtm || null,
      };
      const newHist = [record, ...history].slice(0, 50);
      setHistory(newHist);
      LS.set("podcast_history", newHist);
      setPhase("done");
    } catch(err) {
      setErrorMsg(err.message || "An unexpected error occurred.");
      setPhase("error");
    }
  }; // This was the missing closing brace for the run function

  const handleInject = async () => {
    if (!selectedClient) return;
    setInjectError(""); setInjectStatus("injecting"); setInjectPct(10); setProgressLabel("Connecting to GTM\u2026");
    try {
      const clientData = clients.find(c => String(c.id) === String(selectedClient)) || null;

      if (window.sharkOS?.invoke) {
        // Full GTM push via Electron IPC
        const result = await window.sharkOS.invoke("inject-podcast-schema", {
          clientId: selectedClient,
          clientData,
          schemaBlocks,
          episodeTitle: episodeData?.title || "Podcast Episode",
        });
        if (!result || !result.success) {
          setInjectError(result?.error || "Injection failed. Check GTM auth and container setup.");
          setInjectStatus("idle");
          return;
        }
        setInjectPct(100);
        if (result.injected === 0) {
          const firstErr = result.results?.find(r => r.error)?.error || "All tag creates failed - check GTM permissions and re-auth.";
          setInjectError(`0/${result.total} blocks injected. ${firstErr}`);
          setInjectStatus("idle");
        } else {
          setProgressLabel(`Done \u2014 ${result.injected}/${result.total} blocks live`);
          setInjectStatus("done");
          const podLog = LS.get("podcast_injections_" + selectedClient) || [];
          (result.results || []).filter(r2 => r2.status === "Injected").forEach(r2 => {
            podLog.unshift({ ts: new Date().toISOString(), name: r2.name || r2.type, schema_type: "Podcast - " + (r2.type || "Entity"), status: "Injected" });
          });
          LS.set("podcast_injections_" + selectedClient, podLog.slice(0, 50));
        }
      } else {
        // No bridge - copy all schema blocks as script tags, log to LS
        const allTags = schemaBlocks.map(b =>
          `<script type="application/ld+json">\n${JSON.stringify(b, null, 2)}\n</script>`
        ).join("\n\n");
        try { await navigator.clipboard.writeText(allTags); } catch {}

        // Record in LS so reports show injection proof
        const podLog = LS.get("podcast_injections_" + selectedClient) || [];
        schemaBlocks.forEach(b => {
          podLog.unshift({ ts: new Date().toISOString(), name: b.name || b["@type"], schema_type: "Podcast - " + (b["@type"] || "Entity"), status: "Injected" });
        });
        LS.set("podcast_injections_" + selectedClient, podLog.slice(0, 50));

        setInjectPct(100);
        setProgressLabel(`${schemaBlocks.length} schema blocks copied - paste into GTM Custom HTML tag`);
        setInjectStatus("done");
      }
    } catch (err) {
      setInjectError(err.message || "GTM injection failed.");
      setInjectStatus("idle");
    }
  };

  const activeClient = clients.find(c => String(c.id) === String(selectedClient));
  const foundCount = snippets.filter(s => s.found).length;

  return (
    <div style={{flex:1,padding:28,paddingBottom:110,overflow:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Podcast Citation Engine</div>
          <div style={{fontSize:13,color:T.ghost,marginTop:4}}>Paste a YouTube episode \u2192 scrapes real transcript \u2192 extracts guest schema \u2192 checks AI visibility</div>
        </div>
        {history.length > 0 && (
          <div style={{fontSize:12,color:T.ghost}}>
            <span style={{color:T.blue,fontWeight:700}}>{history.length}</span> episodes processed
          </div>
        )}
      </div>

      <Card style={{marginBottom:20}}>
        <div style={{display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
          <div style={{flex:2,minWidth:260}}>
            <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:8}}>YOUTUBE URL OR VIDEO ID</div>
            <div style={{position:"relative"}}>
              <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#FF0000">
                  <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8z"/>
                  <polygon points="9.75,15.02 15.5,12 9.75,8.98" fill="white"/>
                </svg>
              </div>
              <input
                value={youtubeUrl}
                onChange={e => setYoutubeUrl(e.target.value)}
                onKeyDown={e => e.key === "Enter" && phase !== "running" && run()}
                placeholder="https://youtube.com/watch?v=... or paste video ID"
                style={{width:"100%",background:T.input,border:`1px solid ${T.surfaceBorder}`,borderRadius:12,padding:"10px 14px 10px 40px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}
                onFocus={e => { e.target.style.borderColor=T.blue; e.target.style.boxShadow=`0 0 0 3px rgba(21,180,214,0.15)`; }}
                onBlur={e => { e.target.style.borderColor=T.surfaceBorder; e.target.style.boxShadow="none"; }}
              />
            </div>
          </div>
          <div style={{flex:1,minWidth:180}}>
            <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:8}}>CLIENT</div>
            <select
              value={selectedClient}
              onChange={e => setSelectedClient(e.target.value)}
              style={{width:"100%",background:T.input,border:`1px solid ${T.surfaceBorder}`,borderRadius:12,padding:"10px 14px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",cursor:"pointer"}}
            >
              {clients.length === 0 && <option value="">No clients</option>}
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <Btn variant="primary" onClick={run} disabled={phase==="running"||!youtubeUrl.trim()||!selectedClient} style={{flexShrink:0,alignSelf:"flex-end"}}>
            {phase === "running"
              ? <><span className="spin" style={{width:13,height:13,border:`2px solid rgba(10,14,23,0.3)`,borderTopColor:"#060C0F",borderRadius:"50%",display:"inline-block"}}/> Processing&hellip;</>
              : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:4}}><polygon points="5 3 19 12 5 21 5 3"/></svg>Run Episode</>
            }
          </Btn>
        </div>

        {phase === "running" && (
          <div style={{marginTop:18}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:5}}>
              <span style={{color:T.ghost}}>{progressLabel}</span>
              <span style={{color:T.blue,fontWeight:700}}>{progress}%</span>
            </div>
            <div style={{height:5,background:T.border,borderRadius:3,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${progress}%`,background:`linear-gradient(90deg,${T.blue},${T.blue})`,transition:"width 0.45s ease",borderRadius:3}}/>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div style={{marginTop:14,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:10,padding:"12px 16px",fontSize:13,color:T.red,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>&#9888; {errorMsg}</span>
            <button onClick={() => setPhase("idle")} style={{background:"transparent",border:"none",color:T.ghost,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>Dismiss</button>
          </div>
        )}

        {phase === "idle" && (
          <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:8}}>
            {keyFound === false && (
              <div style={{background:`${T.red}10`,border:`1px solid ${T.red}33`,borderRadius:10,padding:"10px 16px",fontSize:12,color:T.red,fontWeight:600}}>
                &#9888; No OpenAI API key found. Add your sk-proj-... key in Settings &#8594; OpenAI API Key, then save.
              </div>
            )}
            {keyFound === true && (
              <div style={{background:`${T.teal}10`,border:`1px solid ${T.teal}33`,borderRadius:10,padding:"8px 16px",fontSize:12,color:T.teal,fontWeight:600}}>
                &#10003; OpenAI API key found &#8212; ready to run.
              </div>
            )}
            <div style={{background:`${T.blue}08`,border:`1px solid ${T.blue}22`,borderRadius:10,padding:"10px 16px",fontSize:12,color:T.ghost,lineHeight:1.65}}>
              <strong style={{color:T.text}}>How it works:</strong> Paste any YouTube podcast URL &#8594; scrapes real transcript &amp; captions &#8594; GPT-4o extracts guests from actual content &#8594; builds schema.org/Person blocks &#8594; checks AI engine visibility &#8594; inject via GTM.
            </div>
          </div>
        )}
      </Card>

      {episodeData && phase === "done" && (
        <Card style={{marginBottom:20,background:`${T.blue}06`,border:`1px solid ${T.blue}22`}}>
          <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
            <img src={`https://img.youtube.com/vi/${episodeData.videoId}/mqdefault.jpg`} alt="thumb" style={{width:80,height:56,objectFit:"cover",borderRadius:8,flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:14,color:T.text,marginBottom:2}}>{episodeData.title}</div>
              <div style={{fontSize:12,color:T.ghost}}>{episodeData.channelName}</div>
              {episodeData.description && (
                <div style={{fontSize:11,color:T.ghost,marginTop:6,lineHeight:1.55}}>
                  {episodeData.description.slice(0, 220)}{episodeData.description.length > 220 ? "..." : ""}
                </div>
              )}
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8,flexShrink:0}}>
              <div style={{background:`${T.teal}15`,border:`1px solid ${T.teal}33`,borderRadius:8,padding:"4px 12px",fontSize:12,color:T.teal,fontWeight:700}}>
                {schemaBlocks.length} schema blocks
              </div>
              {injectStatus === "done" ? (
                <div style={{color:T.teal,fontWeight:700,fontSize:12}}>\u2713 {progressLabel}</div>
              ) : (
                <Btn variant="primary" sm onClick={handleInject} disabled={injectStatus==="injecting"||schemaBlocks.length===0}>
                  {injectStatus === "injecting"
                    ? <><span className="spin" style={{width:10,height:10,border:`2px solid rgba(10,14,23,0.3)`,borderTopColor:"#060C0F",borderRadius:"50%",display:"inline-block",marginRight:4}}/> {progressLabel}</>
                    : <>Inject via GTM</>
                  }
                </Btn>
              )}
              <a href={`https://youtube.com/watch?v=${episodeData.videoId}`} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:T.blue,textDecoration:"none"}}>&#8599; YouTube</a>
            </div>
          </div>
        </Card>
      )}

      {/* Manual Businesses Card - always visible when episode is done or businesses exist */}
      {(phase === "done" || manualBusinesses.length > 0) && (
        <Card style={{marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:manualBusinesses.length > 0 || showAddBiz ? 14 : 0}}>
            <div>
              <span style={{fontWeight:800,color:T.text,fontSize:14}}>Additional Businesses</span>
              <span style={{fontSize:12,color:T.ghost,marginLeft:8}}>podcast room, sponsors, locations</span>
            </div>
            <button onClick={() => setShowAddBiz(v => !v)}
              style={{background:showAddBiz ? `${T.blue}22` : T.input, border:`1px solid ${showAddBiz ? T.blue+"55" : T.border}`,
                borderRadius:8, padding:"5px 14px", fontSize:12, color:showAddBiz ? T.blue : T.ghost, cursor:"pointer", fontFamily:"inherit", fontWeight:600}}>
              {showAddBiz ? "Cancel" : "+ Add Business"}
            </button>
          </div>

          {showAddBiz && (
            <div style={{background:T.input,border:`1px solid ${T.border}`,borderRadius:10,padding:"14px 16px",marginBottom:14}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:5}}>BUSINESS NAME *</div>
                  <input value={newBiz.name} onChange={e => setNewBiz(b => ({...b, name:e.target.value}))}
                    placeholder="e.g. Studio 7 Podcast Room"
                    style={{width:"100%",background:T.surface,border:`1px solid ${T.surfaceBorder}`,borderRadius:8,padding:"8px 12px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:5}}>TYPE</div>
                  <select value={newBiz.type} onChange={e => setNewBiz(b => ({...b, type:e.target.value}))}
                    style={{width:"100%",background:T.surface,border:`1px solid ${T.surfaceBorder}`,borderRadius:8,padding:"8px 12px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",cursor:"pointer"}}>
                    <option value="LocalBusiness">LocalBusiness</option>
                    <option value="Organization">Organization</option>
                    <option value="ProfessionalService">ProfessionalService</option>
                    <option value="EntertainmentBusiness">EntertainmentBusiness</option>
                    <option value="HealthAndBeautyBusiness">HealthAndBeautyBusiness</option>
                    <option value="FoodEstablishment">FoodEstablishment</option>
                    <option value="Store">Store</option>
                  </select>
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:5}}>ADDRESS</div>
                  <input value={newBiz.address} onChange={e => setNewBiz(b => ({...b, address:e.target.value}))}
                    placeholder="123 Main St, City, ST 00000"
                    style={{width:"100%",background:T.surface,border:`1px solid ${T.surfaceBorder}`,borderRadius:8,padding:"8px 12px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:5}}>PHONE</div>
                  <input value={newBiz.phone} onChange={e => setNewBiz(b => ({...b, phone:e.target.value}))}
                    placeholder="+1-555-000-0000"
                    style={{width:"100%",background:T.surface,border:`1px solid ${T.surfaceBorder}`,borderRadius:8,padding:"8px 12px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:5}}>WEBSITE</div>
                  <input value={newBiz.website} onChange={e => setNewBiz(b => ({...b, website:e.target.value}))}
                    placeholder="https://example.com"
                    style={{width:"100%",background:T.surface,border:`1px solid ${T.surfaceBorder}`,borderRadius:8,padding:"8px 12px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:5}}>DESCRIPTION</div>
                  <input value={newBiz.description} onChange={e => setNewBiz(b => ({...b, description:e.target.value}))}
                    placeholder="Short description of the business"
                    style={{width:"100%",background:T.surface,border:`1px solid ${T.surfaceBorder}`,borderRadius:8,padding:"8px 12px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <Btn variant="primary" onClick={addManualBiz} disabled={!newBiz.name.trim()}>
                  Add &amp; {parsedData ? "Regenerate Schema" : "Save"}
                </Btn>
              </div>
            </div>
          )}

          {manualBusinesses.length > 0 && (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {manualBusinesses.map(b => (
                <div key={b.id} style={{display:"flex",alignItems:"center",gap:10,background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 14px"}}>
                  <div style={{flex:1}}>
                    <span style={{fontWeight:700,fontSize:13,color:T.text}}>{b.name}</span>
                    <span style={{fontSize:11,color:T.ghost,marginLeft:8,background:T.input,borderRadius:4,padding:"1px 6px"}}>{b.type}</span>
                    {b.address && <span style={{fontSize:11,color:T.ghost,marginLeft:8}}>{b.address}</span>}
                    {b.website && <span style={{fontSize:11,color:T.blue,marginLeft:8}}>{b.website}</span>}
                  </div>
                  <button onClick={() => removeManualBiz(b.id)}
                    style={{background:"transparent",border:"none",color:T.ghost,cursor:"pointer",fontSize:16,lineHeight:1,padding:"0 4px",fontFamily:"inherit"}}
                    title="Remove">&#215;</button>
                </div>
              ))}
              {parsedData && (
                <div style={{display:"flex",justifyContent:"flex-end",marginTop:4}}>
                  <Btn onClick={() => buildSchema(parsedData, manualBusinesses)} style={{fontSize:12}}>
                    &#8635; Regenerate Schema
                  </Btn>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {phase === "done" && schemaBlocks.length > 0 && (
        <Card style={{marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontWeight:800,color:T.text,fontSize:14}}>
              Generated Schema Blocks
              <span style={{marginLeft:8,background:`${T.teal}15`,border:`1px solid ${T.teal}33`,borderRadius:6,padding:"2px 8px",fontSize:11,color:T.teal,fontWeight:700}}>
                {schemaBlocks.length}
              </span>
            </div>
            <button onClick={()=>setExpandedSchema(e=>!e)}
              style={{background:T.input,border:`1px solid ${T.border}`,borderRadius:8,padding:"4px 12px",fontSize:11,color:T.ghost,cursor:"pointer",fontFamily:"inherit"}}>
              {expandedSchema ? "Collapse" : "Expand All"}
            </button>
          </div>
          {schemaBlocks.map((block, i) => {
            const json = JSON.stringify(block, null, 2);
            return (
              <div key={i} style={{marginBottom:10,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
                <div style={{background:T.input,padding:"8px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:12,fontWeight:700,color:T.blue}}>
                    {block["@type"]} &mdash; {block.name}
                  </span>
                  <button onClick={()=>navigator.clipboard.writeText(json)}
                    style={{background:"transparent",border:"none",fontSize:11,color:T.ghost,cursor:"pointer",fontFamily:"inherit"}}>
                    Copy
                  </button>
                </div>
                {expandedSchema && (
                  <pre style={{margin:0,padding:"12px 14px",fontSize:11,color:T.text,overflowX:"auto",fontFamily:"monospace",background:T.surface,lineHeight:1.55}}>
                    {json}
                  </pre>
                )}
              </div>
            );
          })}
          <button onClick={()=>navigator.clipboard.writeText(
            schemaBlocks.map(b=>`<script type="application/ld+json">\n${JSON.stringify(b,null,2)}\n</script>`).join("\n\n")
          )} style={{marginTop:6,background:T.input,border:`1px solid ${T.border}`,borderRadius:8,padding:"6px 14px",fontSize:12,color:T.ghost,cursor:"pointer",fontFamily:"inherit"}}>
            Copy All as HTML script tags
          </button>
        </Card>
      )}

      {phase === "done" && snippets.length > 0 && (
        <Card style={{marginBottom:20}}>
          <div style={{fontWeight:800,color:T.text,fontSize:14,marginBottom:12}}>
            Business Citations
            <span style={{marginLeft:8,fontSize:12,color:T.ghost,fontWeight:400}}>{snippets.length} verified</span>
          </div>
          {snippets.map((s,i) => (
            <div key={i} style={{marginBottom:10,padding:"10px 14px",borderRadius:10,
              background:`${T.teal}08`,
              border:`1px solid ${T.teal}33`}}>
              <div style={{marginBottom:4}}>
                <span style={{fontSize:11,fontWeight:700,color:T.ghost,fontFamily:"monospace"}}>{s.query}</span>
              </div>
              <div style={{fontSize:12,color:T.text,lineHeight:1.55}}>{s.answer}</div>
              {s.source && <div style={{fontSize:11,color:T.blue,marginTop:4}}>{s.source}</div>}
            </div>
          ))}
        </Card>
      )}

      {/* ── GUEST AUTHORITY PACKAGES ── */}
      {phase === "done" && guestPackages.length > 0 && (
        <Card style={{marginBottom:20}}>
          <div style={{marginBottom:16}}>
            <div style={{fontWeight:800,color:T.text,fontSize:15,marginBottom:4}}>
              Guest Authority Packages
              <span style={{marginLeft:10,background:`linear-gradient(135deg,${T.blue},${T.cyan})`,color:"#fff",borderRadius:6,padding:"2px 10px",fontSize:11,fontWeight:700,verticalAlign:"middle"}}>
                UPSELL ✦
              </span>
            </div>
            <div style={{fontSize:12,color:T.ghost,lineHeight:1.6}}>
              Each guest gets a personalised citation card + schema block to paste on their own website.
              When they publish it, AI platforms like ChatGPT, Perplexity and Claude start quoting their appearance - building their authority across the web.
            </div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {guestPackages.map((pkg) => {
              const copyKey = `pkg_${pkg.id}`;
              const isCopied = copiedPkg === copyKey;
              return (
                <div key={pkg.id} style={{border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
                  {/* Guest header */}
                  <div style={{background:`linear-gradient(135deg,${T.blue}10,${T.cyan}08)`,padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${T.border}`}}>
                    <div>
                      <div style={{fontWeight:800,fontSize:14,color:T.text}}>{pkg.guestName}</div>
                      {pkg.jobTitle && <div style={{fontSize:12,color:T.ghost,marginTop:1}}>{pkg.jobTitle}{pkg.company ? ` · ${pkg.company}` : ""}</div>}
                      {pkg.website && <a href={pkg.website} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:T.blue,textDecoration:"none"}}>{pkg.website}</a>}
                    </div>
                    <button
                      onClick={async () => {
                        await navigator.clipboard.writeText(pkg.htmlCard);
                        setCopiedPkg(copyKey);
                        setTimeout(() => setCopiedPkg(null), 2500);
                      }}
                      style={{background: isCopied ? `${T.teal}18` : `linear-gradient(135deg,${T.blue},${T.cyan})`,
                        border: isCopied ? `1px solid ${T.teal}55` : "none",
                        color: isCopied ? T.teal : "#fff",
                        borderRadius:9,padding:"8px 18px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0,transition:"all 0.2s"}}>
                      {isCopied ? "✓ Copied!" : "Copy Guest Package"}
                    </button>
                  </div>

                  {/* Topics + quotes */}
                  <div style={{padding:"14px 18px"}}>
                    {pkg.topics.length > 0 && (
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
                        {pkg.topics.map(t => (
                          <span key={t} style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:`${T.blue}10`,color:T.blue,fontWeight:600,border:`1px solid ${T.blue}22`}}>{t}</span>
                        ))}
                      </div>
                    )}

                    {pkg.quotes.length > 0 && (
                      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
                        {pkg.quotes.slice(0,3).map((q, qi) => (
                          <div key={qi} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                            <span style={{color:T.blue,fontWeight:800,fontSize:16,lineHeight:1,flexShrink:0,marginTop:1}}>"</span>
                            <p style={{margin:0,fontSize:12,color:T.text,lineHeight:1.6,fontStyle:"italic"}}>{q}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{background:`${T.gold}0A`,border:`1px solid ${T.gold}33`,borderRadius:8,padding:"9px 14px",fontSize:11,color:T.ghost,lineHeight:1.55}}>
                      <strong style={{color:T.text}}>What this does for your guest:</strong> Paste the copied code into any page on their site. Instantly tells ChatGPT, Perplexity, Claude and Google that they are an authority on these topics - and that they appeared on {activeClient?.name || "your show"}.
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {injectError && (
        <div style={{marginBottom:16,background:`${T.red}10`,border:`1px solid ${T.red}33`,borderRadius:10,
          padding:"10px 16px",fontSize:12,color:T.red,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span>&#9888; {injectError}</span>
          <button onClick={()=>setInjectError("")} style={{background:"transparent",border:"none",color:T.ghost,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>&#215;</button>
        </div>
      )}

      {/* ── SOCIAL CONTENT GENERATOR ── */}
      {phase === "done" && episodeData && (
        <Card style={{marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
            <div>
              <div style={{fontWeight:800,color:T.text,fontSize:15,marginBottom:4}}>
                Social Content Generator
                <span style={{marginLeft:10,background:`linear-gradient(135deg,${T.mint},${T.blue})`,color:"#fff",borderRadius:6,padding:"2px 10px",fontSize:11,fontWeight:700,verticalAlign:"middle"}}>
                  10 POSTS ✦
                </span>
              </div>
              <div style={{fontSize:12,color:T.ghost,lineHeight:1.6}}>
                Turns the most impactful moments from this episode into platform-native posts for LinkedIn, Twitter, Instagram and Facebook.
              </div>
            </div>
            <button onClick={generateSocialContent} disabled={generatingSocial}
              style={{background:generatingSocial?T.input:`linear-gradient(135deg,${T.mint},${T.blue})`,
                border:"none",borderRadius:10,padding:"8px 20px",fontSize:12,fontWeight:800,
                color:generatingSocial?T.ghost:"#060C0F",cursor:generatingSocial?"not-allowed":"pointer",
                fontFamily:"inherit",display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
              {generatingSocial
                ? <><span className="spin" style={{width:12,height:12,border:`2px solid ${T.ghost}44`,borderTopColor:T.ghost,borderRadius:"50%",display:"inline-block"}}/> Generating...</>
                : socialContent.length > 0 ? "↺ Regenerate" : "✦ Generate 10 Posts"}
            </button>
          </div>

          {socialContent.length > 0 && (
            <div>
              {/* Platform tabs */}
              <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
                {['all','linkedin','twitter','instagram','facebook'].map(tab => {
                  const count = tab === 'all' ? socialContent.length : socialContent.filter(p => p.platform === tab).length;
                  if (tab !== 'all' && count === 0) return null;
                  const colors = {linkedin:'#0077B5',twitter:'#1DA1F2',instagram:'#E1306C',facebook:'#1877F2',all:T.blue};
                  const active = socialTab === tab;
                  return(
                    <button key={tab} onClick={()=>setSocialTab(tab)}
                      style={{padding:"5px 14px",borderRadius:20,border:`1px solid ${active?colors[tab]:T.border}`,
                        background:active?`${colors[tab]}18`:"transparent",
                        color:active?colors[tab]:T.ghost,cursor:"pointer",fontSize:11,fontWeight:700,
                        fontFamily:"inherit",textTransform:"capitalize"}}>
                      {tab === 'all' ? `All (${count})` : `${tab.charAt(0).toUpperCase()+tab.slice(1)} (${count})`}
                    </button>
                  );
                })}
              </div>

              {/* Posts */}
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {socialContent
                  .filter(p => socialTab === 'all' || p.platform === socialTab)
                  .map((post, i) => {
                    const pColors = {linkedin:'#0077B5',twitter:'#1DA1F2',instagram:'#E1306C',facebook:'#1877F2'};
                    const pColor = pColors[post.platform] || T.blue;
                    const postKey = `social_${i}`;
                    const isCopied = copiedPost === postKey;
                    const fullText = `${post.body}

${(post.hashtags||[]).map(h=>'#'+h.replace(/^#/,'')).join(' ')}`;

                    return(
                      <div key={i} style={{border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
                        {/* Post header */}
                        <div style={{padding:"10px 16px",background:`${pColor}10`,borderBottom:`1px solid ${T.border}`,
                          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:10,fontWeight:800,color:pColor,letterSpacing:1,textTransform:"uppercase",
                              padding:"2px 8px",borderRadius:6,border:`1px solid ${pColor}33`,background:`${pColor}15`}}>
                              {post.platform}
                            </span>
                            {post.angle&&<span style={{fontSize:11,color:T.ghost,fontStyle:"italic"}}>{post.angle}</span>}
                          </div>
                          <button onClick={async()=>{
                            await navigator.clipboard.writeText(fullText);
                            setCopiedPost(postKey);
                            setTimeout(()=>setCopiedPost(null),2500);
                          }} style={{fontSize:11,fontWeight:700,padding:"4px 12px",borderRadius:7,
                            border:`1px solid ${isCopied?T.teal:pColor}`,
                            background:isCopied?`${T.teal}15`:`${pColor}12`,
                            color:isCopied?T.teal:pColor,cursor:"pointer",fontFamily:"inherit"}}>
                            {isCopied?"✓ Copied":"Copy"}
                          </button>
                        </div>
                        {/* Post body */}
                        <div style={{padding:"14px 16px"}}>
                          {post.hook&&<div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:8,lineHeight:1.5}}>{post.hook}</div>}
                          <div style={{fontSize:12,color:T.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{post.body}</div>
                          {post.hashtags&&post.hashtags.length>0&&(
                            <div style={{marginTop:10,display:"flex",flexWrap:"wrap",gap:6}}>
                              {post.hashtags.map(h=>(
                                <span key={h} style={{fontSize:11,color:pColor,fontWeight:600}}>#{h.replace(/^#/,'')}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Copy all button */}
              <button onClick={async()=>{
                const allText = socialContent
                  .map((p,i)=>'--- POST '+(i+1)+' ('+p.platform.toUpperCase()+') ---\n'+p.body+'\n'+(p.hashtags||[]).map(h=>'#'+h.replace(/^#/,'')).join(' '))
                  .join('\n\n');
                await navigator.clipboard.writeText(allText);
              }} style={{marginTop:14,width:"100%",padding:"10px 0",borderRadius:10,
                border:`1px solid ${T.border}`,background:T.input,color:T.ghost,
                cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>
                Copy All 10 Posts
              </button>
            </div>
          )}

          {socialContent.length === 0 && !generatingSocial && (
            <div style={{padding:"20px 0",textAlign:"center",color:T.ghost,fontSize:13}}>
              Click Generate to create 10 platform-native posts from the most impactful moments in this episode.
            </div>
          )}
        </Card>
      )}

    </div>
  );
}




// ---------------------------------------------
// CODE INJECTOR
// ---------------------------------------------
function SchemaInjector({clients}){
  const [month,setMonth]=useState(()=>new Date().toLocaleString("default",{month:"long",year:"numeric"}));
  const [selected,setSelected]=useState(new Set());
  const [statuses,setStatuses]=useState({});
  const [verifyResults,setVerifyResults]=useState({});
  const [running,setRunning]=useState(false);
  const toggleAll=()=>setSelected(selected.size===clients.length?new Set():new Set(clients.map(c=>c.id)));
  const toggle=id=>{const n=new Set(selected);n.has(id)?n.delete(id):n.add(id);setSelected(n);};

  // Verify injection by fetching the client's domain and checking for LocalBusiness schema in the HTML
  const verifyInjection = async (clientId) => {
    const client = clients.find(c=>String(c.id)===String(clientId));
    if(!client?.domain) return {verified:false,reason:"No domain configured"};
    const domain = client.domain.replace(/^https?:\/\//,"").replace(/\/$/,"");
    setVerifyResults(v=>({...v,[clientId]:{status:"checking"}}));
    try {
  const html = await fetchProxy("https://"+domain);
  if(!html) return {verified:false, reason:"Could not fetch site - proxy unavailable"};
  const hasLocalBusiness = /"@type"\s*:\s*"LocalBusiness"/i.test(html) || /schema\.org.*LocalBusiness/i.test(html);
  const hasFAQ = /"@type"\s*:\s*"FAQPage"/i.test(html);
  const hasService = /"@type"\s*:\s*"Service"/i.test(html);
  const hasPerson = /"@type"\s*:\s*"Person"/i.test(html);  // Add this
  const hasOrganization = /"@type"\s*:\s*"Organization"/i.test(html);  // Add this
  
  const schemas = [];
  if(hasLocalBusiness) schemas.push("LocalBusiness");
  if(hasFAQ) schemas.push("FAQPage");
  if(hasService) schemas.push("Service");
  if(hasPerson) schemas.push("Person");  // Add this
  if(hasOrganization) schemas.push("Organization");  // Add this
  
  const result = {
    verified: schemas.length > 0,  // Changed to check any schema type
    schemas,
    reason: schemas.length>0
      ? `Found: ${schemas.join(", ")}`
      : "No schema markup detected on homepage",
    ts: new Date().toLocaleTimeString()
  };
  setVerifyResults(v=>({...v,[clientId]:result}));
  // Save verification to history
  const histKey = "inject_verify_"+clientId;
  const hist = LS.get(histKey)||[];
  hist.unshift({month,...result,domain});
  LS.set(histKey, hist.slice(0,12));
  return result;
} catch(e) {
  const result = {verified:false, reason:"Verification error: "+e.message};
  setVerifyResults(v=>({...v,[clientId]:result}));
  return result;
}
  }; // close verifyInjection


  const inject=async()=>{
    if(!selected.size||running)return;setRunning(true);
    for(const id of selected){
      setStatuses(s=>({...s,[id]:"injecting"}));
      const client = clients.find(c=>String(c.id)===String(id));
      // Record the injection event
      const injKey = "inject_history_"+id;
      const injHist = LS.get(injKey)||[];
      injHist.unshift({month, ts:new Date().toISOString(), gtm:client?.gtm||null, domain:client?.domain||null});
      LS.set(injKey, injHist.slice(0,24));
      // Mark as injected (GTM push happens client-side via container)
      await new Promise(r=>setTimeout(r,600));
      setStatuses(s=>({...s,[id]:"verifying"}));
      // Auto-verify after injection
      await verifyInjection(id);
      setStatuses(s=>({...s,[id]:"done"}));
    }
    setRunning(false);
  };

  const statusEl=id=>{
    const s=statuses[id];
    const v=verifyResults[id];
    if(s==="injecting")return<span style={{color:T.blue,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:5}}><span className="spin" style={{width:12,height:12,border:`2px solid ${T.blue}44`,borderTopColor:T.blue,borderRadius:"50%"}}/> Injecting</span>;
    if(s==="verifying")return<span style={{color:T.gold,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:5}}><span className="spin" style={{width:12,height:12,border:`2px solid ${T.gold}44`,borderTopColor:T.gold,borderRadius:"50%"}}/> Verifying</span>;
    if(s==="done"){
      if(v?.status==="checking") return<span style={{color:T.blue,fontSize:12,fontWeight:700}}>Checking...</span>;
      if(v?.verified) return(
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
          <span style={{color:T.teal,fontSize:12,fontWeight:700}}>✓ Verified</span>
          <span style={{fontSize:10,color:T.ghost}}>{v.reason}</span>
        </div>
      );
      return(
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
          <span style={{color:T.gold,fontSize:12,fontWeight:700}}>⚠ Injected</span>
          <span style={{fontSize:10,color:T.ghost}}>{v?.reason||"Not yet verifiable"}</span>
        </div>
      );
    }
    // Show last verification if it exists
    const lastVerify = LS.get("inject_verify_"+id)?.[0];
    if(lastVerify){
      return(
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
          <span style={{color:lastVerify.verified?T.teal:T.ghost,fontSize:11,fontWeight:600}}>
            {lastVerify.verified?"✓ Schema Live":"Schema Not Detected"}
          </span>
          <span style={{fontSize:10,color:T.ghost}}>{lastVerify.month}</span>
        </div>
      );
    }
    return null;
  };

  // Verify single client manually
  const handleManualVerify = async (id) => {
    setStatuses(s=>({...s,[id]:"verifying"}));
    await verifyInjection(id);
    setStatuses(s=>({...s,[id]:"done"}));
  };

  return(
    <div style={{flex:1,padding:28,overflow:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Code Injector</div>
          <div style={{fontSize:13,color:T.ghost,marginTop:4}}>Monthly code injection via GTM - with live verification</div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <Inp value={month} onChange={setMonth} style={{width:180}}/>
          <Btn variant="primary" onClick={inject} disabled={!selected.size||running}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6}}><polygon points="5 3 19 12 5 21 5 3"/></svg>
            Inject {selected.size} Selected
          </Btn>
        </div>
      </div>

      {/* How injection works */}
      <div style={{background:`${T.blue}10`,border:`1px solid ${T.blue}33`,borderRadius:12,padding:16,marginBottom:20,fontSize:12,color:T.ghost,lineHeight:1.6}}>
        <strong style={{color:T.text}}>How it works:</strong> Clicking Inject records the injection event for the selected month and immediately fetches each client's live domain to verify schema markup is present in the HTML.
        A <strong style={{color:T.teal}}>✓ Verified</strong> result means LocalBusiness schema was detected on the homepage.
        <strong style={{color:T.gold}}> ⚠ Injected</strong> means the record was saved but schema wasn't found - check that GTM is firing and the container is published.
      </div>

      <Card style={{padding:0,overflow:"hidden"}}>
        <div onClick={toggleAll} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 24px",borderBottom:`1px solid ${T.border}`,cursor:"pointer"}}
          onMouseEnter={e=>e.currentTarget.style.background=T.input}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          <div style={{width:22,height:22,borderRadius:6,
            border:`2px solid ${selected.size===clients.length?"transparent":T.border}`,
            background:selected.size===clients.length?`linear-gradient(135deg,${T.blue},${T.blue})`:selected.size>0?`${T.blue}55`:"transparent",
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {selected.size===clients.length&&<span style={{color:T.white,fontSize:11,fontWeight:800}}>✓</span>}
            {selected.size>0&&selected.size<clients.length&&<div style={{width:10,height:2,background:T.white}}/>}
          </div>
          <span style={{fontSize:13,fontWeight:700,color:T.text}}>SELECT ALL ({clients.length} clients)</span>
          <span style={{fontSize:12,color:T.ghost,marginLeft:"auto"}}>{selected.size} selected</span>
        </div>
        {clients.map(c=>{const sel=selected.has(c.id);return(
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 24px",
            borderBottom:`1px solid ${T.border}`,background:sel?`${T.blue}07`:"transparent",transition:"background 0.12s"}}>
            <div onClick={()=>toggle(c.id)} style={{cursor:"pointer",width:22,height:22,borderRadius:6,
              border:`2px solid ${sel?"transparent":T.border}`,
              background:sel?`linear-gradient(135deg,${T.blue},${T.blue})`:"transparent",
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {sel&&<span style={{color:T.white,fontSize:11,fontWeight:800}}>✓</span>}
            </div>
            <div style={{flex:1}}>
              <span style={{fontSize:13,fontWeight:600,color:T.text}}>{c.name}</span>
              <span style={{fontSize:12,color:T.ghost,marginLeft:8}}>{c.domain} · {c.businessType}</span>
              {c.gtm&&<span style={{fontSize:11,color:T.blue,marginLeft:8,fontFamily:"monospace"}}>{c.gtm}</span>}
              {!c.gtm&&<span style={{fontSize:11,color:T.red,marginLeft:8}}>⚠ No GTM ID</span>}
            </div>
            <button onClick={()=>handleManualVerify(c.id)}
              title="Check live schema on site now"
              style={{background:T.input,border:`1px solid ${T.border}`,borderRadius:8,padding:"4px 10px",
                fontSize:11,fontWeight:600,color:T.ghost,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
              Verify Live
            </button>
            {statusEl(c.id)}
          </div>
        );})}
      </Card>

      {/* Injection history */}
      {clients.some(c=>LS.get("inject_history_"+c.id)?.length>0)&&(
        <div style={{marginTop:24}}>
          <div style={{fontWeight:800,color:T.text,marginBottom:12}}>Injection History</div>
          <Card style={{padding:0,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
                {["CLIENT","MONTH","GTM","SCHEMA VERIFIED","DATE"].map(h=>(
                  <th key={h} style={{textAlign:"left",padding:"8px 16px",fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:1}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {clients.flatMap(c=>{
                  const hist = LS.get("inject_history_"+c.id)||[];
                  const verif = LS.get("inject_verify_"+c.id)||[];
                  return hist.map((h,i)=>{
                    const v = verif.find(vr=>vr.month===h.month);
                    return(
                      <tr key={c.id+i} style={{borderBottom:`1px solid ${T.border}`}}
                        onMouseEnter={e=>e.currentTarget.style.background=T.input}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <td style={{padding:"10px 16px",fontWeight:600,color:T.text}}>{c.name}</td>
                        <td style={{padding:"10px 16px",color:T.ghost}}>{h.month}</td>
                        <td style={{padding:"10px 16px",fontFamily:"monospace",fontSize:11,color:h.gtm?T.blue:T.ghost}}>{h.gtm||"-"}</td>
                        <td style={{padding:"10px 16px"}}>
                          {v
                            ?<span style={{color:v.verified?T.teal:T.gold,fontWeight:700,fontSize:11}}>{v.verified?"✓ "+v.reason:"⚠ "+v.reason}</span>
                            :<span style={{color:T.ghost,fontSize:11}}>Not verified</span>
                          }
                        </td>
                        <td style={{padding:"10px 16px",color:T.ghost,fontSize:11}}>{new Date(h.ts).toLocaleDateString()}</td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------
// SETTINGS
// ---------------------------------------------
function SettingsView({clients=[]}){
  const [s,setS]=useState(()=>LS.get("settings")||{});
  const [saved,setSaved]=useState(false);
  const [gtmEdits,setGtmEdits]=useState({});
  const [gtmSaved,setGtmSaved]=useState({});
  const upd=(k,v)=>setS(prev=>({...prev,[k]:v}));
  const save=async()=>{
    LS.set("settings",s); // always persist locally
    if (window.sharkOS?.invoke) {
      try { await window.sharkOS.invoke("save-settings", s); } catch {}
    }
    setSaved(true);setTimeout(()=>setSaved(false),2000);
  };

  const saveClientGtm=async(client)=>{
    const newGtm=(gtmEdits[client.id]??client.gtm??"").trim();
    await ipc("save-client",{...client,gtm:newGtm});
    // Update local clients in localStorage directly
    const all=LS.get("clients")||[];
    const idx=all.findIndex(c=>c.id===client.id);
    if(idx>=0){all[idx]={...all[idx],gtm:newGtm};LS.set("clients",all);}
    setGtmSaved(prev=>({...prev,[client.id]:true}));
    setTimeout(()=>setGtmSaved(prev=>({...prev,[client.id]:false})),2000);
  };

  const F=({label,k,placeholder,mono,note})=>(
    <div>
      <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1.1,marginBottom:4}}>{label}</div>
      {note&&<div style={{fontSize:11,color:T.ghost,marginBottom:6,lineHeight:1.4}}>{note}</div>}
      <InpFree defaultVal={s[k]||""} onCommit={v=>upd(k,v)} placeholder={placeholder} mono={!!mono}/>
    </div>
  );

  return(
    <div style={{flex:1,padding:28,overflow:"auto"}}>
      <div style={{maxWidth:780}}>
        <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:24}}>Settings</div>

        {/* AI Keys */}
        <Card style={{marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <div style={{width:20,height:20,borderRadius:6,background:"#10a37f",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/></svg>
            </div>
            <span style={{fontWeight:800,color:T.text,fontSize:15}}>AI & Search API Keys</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <F label="ANTHROPIC API KEY" k="anthropicApiKey" placeholder="sk-ant-api03-..." mono
              note="Required for FAQ generation, Podcast Citation Engine, AI Search Visibility, and SharkRank. Get from console.anthropic.com → API Keys."/>
            <div style={{marginTop:8,marginBottom:14,background:"rgba(167,139,250,0.08)",border:"1px solid rgba(167,139,250,0.2)",borderRadius:8,padding:"8px 12px",fontSize:11,color:"#a78bfa",lineHeight:1.5}}>
              SharkRank™ uses your OpenAI key to run listwise re-ranking (based on Stanford NLP research) - shows exactly where your client ranks in AI citations vs. competitors, and why.
            </div>
            <F label="PERPLEXITY API KEY" k="perplexityApiKey" placeholder="pplx-..." mono
              note="Enables SharkRank™ Live Check - queries real Perplexity AI responses to detect actual citations for your client. Get a free key at perplexity.ai/settings/api."/>
            <div style={{borderTop:`1px solid ${T.border}`,paddingTop:14}}>
              <F label="BRAVE SEARCH API KEY" k="braveApiKey" placeholder="BSA..." mono
                note="Required for the AI Query Monitor - runs 10 real searches to show where clients appear in AI results. Get a free key at api.search.brave.com → Create API Key (Free tier = 2,000 searches/month)."/>
              <div style={{marginTop:8,background:`rgba(124,58,237,0.08)`,border:`1px solid rgba(124,58,237,0.2)`,borderRadius:8,padding:"8px 12px",fontSize:11,color:"#A78BFA",lineHeight:1.5}}>
                💡 Brave Search powers the AI visibility proof - it checks if each client appears when buyers search for their services. Free tier is plenty for monthly scans across all clients.
              </div>
            </div>
          </div>
          <div style={{marginTop:14}}><Btn variant={saved?"success":"primary"} onClick={save}>{saved?"✓ Saved":"Save"}</Btn></div>
        </Card>

        {/* Google API Setup */}
        <Card style={{marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.5 1.2 8.9 3.2l6.6-6.6C35.4 2.4 30 0 24 0 14.6 0 6.5 5.4 2.5 13.4l7.7 6C12.2 13 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17z"/><path fill="#FBBC05" d="M10.2 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.7-4.6l-7.7-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.5 10.8l7.7-6.2z"/><path fill="#34A853" d="M24 48c6 0 11.1-2 14.8-5.4l-7.5-5.8c-2 1.4-4.6 2.2-7.3 2.2-6.4 0-11.8-4.3-13.8-10.1l-7.7 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
            <span style={{fontWeight:800,color:T.text,fontSize:15}}>Google API Setup</span>
          </div>

          <div style={{background:`rgba(21,180,214,0.06)`,border:`1px solid rgba(21,180,214,0.2)`,borderRadius:12,padding:16,marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:800,color:T.blue,letterSpacing:0.5,marginBottom:10}}>PLACES API - WORKS FOR ANY CLIENT, NO APPROVAL NEEDED</div>
            {[
              ["1","Go to console.cloud.google.com → APIs & Services → Library"],
              ["2","Search 'Places API' → Enable it (activates instantly, no approval)"],
              ["3","Go to Credentials → + Create Credentials → API Key → copy it below"],
              ["4","Optionally restrict the key to 'Places API' and your domain"],
            ].map(([n,text])=>(
              <div key={n} style={{display:"flex",gap:10,marginBottom:8,alignItems:"flex-start"}}>
                <div style={{width:20,height:20,borderRadius:"50%",background:T.blue,color:"#060C0F",fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>{n}</div>
                <span style={{fontSize:12,color:T.ghost,lineHeight:1.5}}>{text}</span>
              </div>
            ))}
            <div style={{marginTop:8,fontSize:11,color:T.teal,fontWeight:600}}>
              ✓ Pulls name, address, phone, rating, reviews, website for any business on Google Maps - no client login required.
            </div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <F label="GOOGLE PLACES API KEY" k="placesApiKey" placeholder="AIzaSy..." mono
              note="Required for live GBP sync. Enable 'Places API' in Google Cloud Console - no approval, works instantly."/>
            <div style={{borderTop:`1px solid ${T.border}`,paddingTop:12,marginTop:4}}>
              <div style={{fontSize:11,fontWeight:700,color:T.blue,letterSpacing:1,marginBottom:8}}>GTM / GMB OAUTH - REQUIRED FOR CODE INJECTION</div>
              <div style={{fontSize:11,color:T.ghost,marginBottom:10,lineHeight:1.5}}>
                Create an OAuth 2.0 Client ID in Google Cloud Console (Type: Desktop App). Enable the Tag Manager API and Google My Business API.
                After saving, go to a client card → Audit tab → GTM panel → Connect GTM.
              </div>
            </div>
            <F label="YOUR GOOGLE ACCOUNT EMAIL" k="userGoogleEmail" placeholder="you@gmail.com"
              note="The Google account you'll use for GTM access. Appears in auto-generated access request messages sent to GTM owners."/>
            <F label="GOOGLE OAUTH CLIENT ID" k="gtmClientId" placeholder="123456789-abc.apps.googleusercontent.com" mono
              note="Google Cloud Console → Credentials → OAuth 2.0 Client → Desktop App. Used for GTM injection and GMB sync."/>
            <F label="GOOGLE OAUTH CLIENT SECRET" k="gtmClientSecret" placeholder="GOCSPX-..." mono/>
          </div>
          <div style={{marginTop:14}}><Btn variant={saved?"success":"primary"} onClick={save}>{saved?"✓ Saved":"Save API Settings"}</Btn></div>
        </Card>

        {/* GTM Container ID Manager */}
        <Card style={{marginBottom:20,padding:0,overflow:"hidden"}}>
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:32,height:32,borderRadius:8,background:`rgba(21,180,214,0.1)`,display:"flex",alignItems:"center",justifyContent:"center",color:T.blue,flexShrink:0}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
            </div>
            <div>
              <div style={{fontWeight:800,color:T.text}}>GTM Container ID Manager</div>
              <div style={{fontSize:12,color:T.ghost,marginTop:2}}>Set or update the Google Tag Manager container for each client. Used for code injection.</div>
            </div>
          </div>
          {clients.length===0?(
            <div style={{padding:32,textAlign:"center",color:T.ghost,fontSize:13}}>No clients yet - add clients from the Clients dashboard.</div>
          ):(
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${T.border}`,background:T.input}}>
                  {["CLIENT","DOMAIN","GTM CONTAINER ID",""].map(h=>(
                    <th key={h} style={{textAlign:"left",padding:"10px 20px",fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:1}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.map(c=>{
                  const currentVal=gtmEdits[c.id]??c.gtm??"";
                  const isDirty=(gtmEdits[c.id]!==undefined)&&(gtmEdits[c.id]!==(c.gtm||""));
                  const isSaved=!!gtmSaved[c.id];
                  return(
                    <tr key={c.id} style={{borderBottom:`1px solid ${T.border}`}}
                      onMouseEnter={e=>e.currentTarget.style.background=T.input}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{padding:"12px 20px",fontWeight:700,color:T.text,whiteSpace:"nowrap"}}>{c.name}</td>
                      <td style={{padding:"12px 20px",color:T.ghost,fontSize:12}}>{c.domain||"-"}</td>
                      <td style={{padding:"10px 20px",minWidth:220}}>
                        <input
                          defaultValue={c.gtm||""}
                          onChange={e=>setGtmEdits(prev=>({...prev,[c.id]:e.target.value}))}
                          placeholder="GTM-XXXXXXX"
                          style={{width:"100%",background:T.input,border:`1px solid ${isDirty?T.blue:T.surfaceBorder}`,
                            borderRadius:10,padding:"8px 12px",fontSize:12,color:T.text,
                            fontFamily:"monospace",outline:"none",transition:"border-color 0.2s",
                            boxShadow:isDirty?`0 0 0 2px rgba(21,180,214,0.12)`:"none"}}
                          onFocus={e=>{e.target.style.borderColor=T.blue;e.target.style.boxShadow=`0 0 0 2px rgba(21,180,214,0.12)`;}}
                          onBlur={e=>{if(!isDirty){e.target.style.borderColor=T.surfaceBorder;e.target.style.boxShadow="none";} }}
                          onKeyDown={e=>{if(e.key==="Enter")saveClientGtm(c);}}
                        />
                      </td>
                      <td style={{padding:"10px 20px",whiteSpace:"nowrap"}}>
                        <button onClick={()=>saveClientGtm(c)}
                          style={{padding:"6px 14px",borderRadius:8,border:"none",cursor:"pointer",
                            fontSize:12,fontWeight:700,fontFamily:"inherit",transition:"all 0.2s",
                            background:isSaved?`rgba(79,209,160,0.15)`:isDirty?`linear-gradient(135deg,${T.blue},#0E8FAA)`:`rgba(255,255,255,0.06)`,
                            color:isSaved?T.teal:isDirty?"#060C0F":T.ghost,
                            boxShadow:isDirty?`0 2px 10px rgba(21,180,214,0.25)`:"none"}}>
                          {isSaved?"✓ Saved":isDirty?"Save":"Saved"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div style={{padding:"12px 20px",borderTop:`1px solid ${T.border}`,fontSize:11,color:T.ghost}}>
            Press <kbd style={{background:T.input,border:`1px solid ${T.border}`,borderRadius:4,padding:"1px 6px",fontSize:10,fontFamily:"monospace"}}>Enter</kbd> in any field or click Save to update. Changes are instant - no page reload needed.
          </div>
        </Card>

        {/* Webhook */}
        <Card style={{marginBottom:20}}>
          <div style={{fontWeight:800,color:T.text,marginBottom:6}}>Webhook URL</div>
          <div style={{fontSize:13,color:T.ghost,marginBottom:16}}>Configure your webhook endpoint for intent signals.</div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <F label="URL" k="webhookUrl" placeholder="https://your-webhook.ngrok.io/api/intent"/>
            <F label="NOTE" k="note" placeholder="Optional description"/>
          </div>
          <div style={{marginTop:16}}><Btn variant={saved?"success":"primary"} onClick={save}>{saved?"✓ Saved":"Save"}</Btn></div>
        </Card>

        <div style={{fontSize:12,color:T.ghost,textAlign:"center"}}>
          Press <kbd style={{background:T.input,border:`1px solid ${T.border}`,borderRadius:5,padding:"2px 7px",fontSize:11,fontFamily:"monospace"}}>F10</kbd> to show/hide window
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------
// COMMAND PALETTE (Cmd+K)
// ---------------------------------------------
function CommandPalette({isOpen, onClose, clients, onSelectClient, onNavigate}) {
  const [query, setQuery] = useState("");
  useEffect(() => {
    if(isOpen) setQuery("");
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    if(isOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;

  const lowQ = query.toLowerCase();
  const searchClients = clients.filter(c => c.name.toLowerCase().includes(lowQ) || c.domain.toLowerCase().includes(lowQ));

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",zIndex:9999,display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:"12vh"}}
      onClick={(e)=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="slide-in glass-panel" style={{width:"100%",maxWidth:640,padding:0,overflow:"hidden",boxShadow:"0 30px 100px rgba(0,0,0,0.6)"}}>
        <div style={{display:"flex",alignItems:"center",padding:"16px 24px",borderBottom:`1px solid ${T.surfaceBorder}`,background:"${T.surface}"}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.ghost} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:16}}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          <input autoFocus value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search clients, pages, or commands (Cmd+K)" 
            style={{flex:1,background:"transparent",border:"none",color:T.white,fontSize:18,outline:"none",fontFamily:"inherit"}} />
          <div style={{fontSize:11,color:T.ghost,border:`1px solid ${T.surfaceBorder}`,padding:"4px 8px",borderRadius:6,background:T.input}}>ESC</div>
        </div>
        <div style={{maxHeight:400,overflowY:"auto",padding:"12px 0"}}>
          {query.length === 0 && (
            <div style={{padding:"8px 24px",fontSize:11,fontWeight:800,color:T.ghost,letterSpacing:1,marginTop:8}}>QUICK ACTIONS</div>
          )}
          {query.length === 0 && [
            {id:"clients", label:"Go to Clients Dashboard"},
            {id:"schema", label:"Open Code Injector"},
            {id:"settings", label:"Manage Settings"}
          ].map(n => (
            <div key={n.id} onClick={()=>onNavigate(n.id)} style={{padding:"12px 24px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,transition:"background 0.2s"}}
              onMouseEnter={e=>{e.currentTarget.style.background=`rgba(21, 180, 214, 0.1)`;}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              <span style={{fontSize:14,color:T.white,fontWeight:500}}>{n.label}</span>
            </div>
          ))}
          
          {(query.length > 0 || searchClients.length > 0) && (
            <div style={{padding:"8px 24px",fontSize:11,fontWeight:800,color:T.ghost,letterSpacing:1,marginTop:8}}>CLIENTS</div>
          )}
          {searchClients.map(c => (
             <div key={c.id} onClick={()=>onSelectClient(c)} style={{padding:"12px 24px",cursor:"pointer",display:"flex",alignItems:"center",gap:16,transition:"background 0.2s"}}
               onMouseEnter={e=>{e.currentTarget.style.background=`rgba(21, 180, 214, 0.1)`;}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
               <ScoreRing score={c.score} size={32} sw={3} animate={false}/>
               <div style={{flex:1}}>
                  <div style={{fontSize:14,color:T.white,fontWeight:600}}>{c.name}</div>
                  <div style={{fontSize:12,color:T.ghost}}>{c.domain}</div>
               </div>
               <GradeBadge grade={c.grade}/>
             </div>
          ))}
          {query.length > 0 && searchClients.length === 0 && (
            <div style={{padding:"32px 24px",textAlign:"center",color:T.ghost,fontSize:14}}>No results found for "{query}"</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------
// ROOT APP
// ---------------------------------------------
// No seed data - clients are added by the user
const SEED = [];


// =============================================
// GTM MANAGER - manage all client GTM connections
// =============================================
function GTMManager({ clients, onClientsUpdate }) {
  const [saved, setSaved] = useState({});
  const [connecting, setConnecting] = useState({});
  const [connected, setConnected] = useState(() => {
    const out = {};
    clients.forEach(c => { out[c.id] = LS.get("gtm_connected_" + c.id) || "no_access"; });
    return out;
  });
  const [copied, setCopied] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [verifying, setVerifying] = useState({});
  const [gtmInputs, setGtmInputs] = useState({});

  const settings = LS.get("settings") || {};
  const myEmail = settings.userGoogleEmail || "";

  const saveGtm = async (client) => {
    const val = (gtmInputs[client.id] ?? client.gtm ?? "").trim();
    const updated = { ...client, gtm: val };
    await ipc("save-client", updated);
    setSaved(s => ({ ...s, [client.id]: true }));
    setTimeout(() => setSaved(s => ({ ...s, [client.id]: false })), 2200);
    showToast(`GTM saved for ${client.name}`, "success");
    onClientsUpdate?.();
  };

  const accessMsg = (client) => {
    const gtm = (gtmInputs[client.id] ?? client.gtm ?? "").trim() || "[GTM-XXXXXXX]";
    return [
      `Subject: GTM Publisher Access Request - ${client.name}`,``,`Hi,`,``,
      `I manage digital visibility for ${client.name}. To deploy schema markup via Google Tag Manager (improves ChatGPT, Perplexity, and Google AI Overviews rankings), I need Publisher access to GTM container: ${gtm}.`,``,
      `Please add this Google account as Publisher:`,
      `  ${myEmail || "[Add your Google email in Settings → Google API Setup]"}`,``,
      `Steps (2 minutes):`,
      `  1. Sign in at tagmanager.google.com`,
      `  2. Select the ${client.name} account`,
      `  3. Admin → User Management → Add User`,
      `  4. Email: ${myEmail || "[your email]"} - Permission: Publish`,
      `  5. Click Add`,``,
      `Once added, schema deploys immediately - no website changes needed.`,``,`Thanks`,
    ].join("\n");
  };

  const copyMsg = (client) => {
    navigator.clipboard.writeText(accessMsg(client));
    setCopied(c => ({ ...c, [client.id]: true }));
    setTimeout(() => setCopied(c => ({ ...c, [client.id]: false })), 2500);
  };

  const connectGtm = async (client) => {
    if (!window.sharkOS?.invoke) { showToast("Run from Electron to use OAuth connect", "warn"); return; }
    setConnecting(c => ({ ...c, [client.id]: true }));
    try {
      const r = await window.sharkOS.invoke("trigger-gtm-oauth", { clientId: client.id, scope: "gtm" });
      if (!r.success) { showToast(r.error, "error"); setConnecting(c => ({ ...c, [client.id]: false })); }
    } catch (e) { showToast(e.message, "error"); setConnecting(c => ({ ...c, [client.id]: false })); }
  };

  const verifyAccess = async (client) => {
    if (!window.sharkOS?.invoke) { showToast("Run from Electron to verify", "warn"); return; }
    setVerifying(v => ({ ...v, [client.id]: true }));
    setConnecting(c => ({ ...c, [client.id]: false }));
    try {
      const r = await window.sharkOS.invoke("verify-gtm-access", { clientId: client.id, clientData: client });
      if (r.success) {
        setConnected(c => ({ ...c, [client.id]: "connected" }));
        LS.set("gtm_connected_" + client.id, "connected");
        showToast(`✓ GTM access verified for ${client.name}`, "success");
      } else { showToast(r.error || "Access not verified", "warn"); }
    } catch (e) { showToast(e.message, "error"); }
    setVerifying(v => ({ ...v, [client.id]: false }));
  };

  // Listen for OAuth completion
  React.useEffect(() => {
    if (!window.sharkOS?.on) return;
    const unsub = window.sharkOS.on("gtm-auth-complete", (data) => {
      if (data.scope !== "gtm") return;
      const client = clients.find(c => c.id === data.clientId);
      if (client) verifyAccess(client);
    });
    return unsub;
  }, [clients]);

  const connectedCount = clients.filter(c => connected[c.id] === "connected").length;
  const withGtm = clients.filter(c => (c.gtm||"").trim());
  const noGtm = clients.filter(c => !(c.gtm||"").trim());

  return (
    <div style={{flex:1,padding:28,overflow:"auto"}}>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:4}}>GTM Manager</div>
        <div style={{fontSize:13,color:T.ghost}}>One-time setup per client - enter GTM ID → send access request → connect → verified.</div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:24}}>
        {[
          {label:"TOTAL CLIENTS",val:clients.length,col:T.blue},
          {label:"GTM ID SET",val:withGtm.length,col:T.teal},
          {label:"OAUTH VERIFIED",val:connectedCount,col:T.mint},
        ].map(({label,val,col})=>(
          <div key={label} className="glass-panel" style={{padding:"16px 20px"}}>
            <div style={{fontSize:10,fontWeight:800,color:T.ghost,letterSpacing:1.2,marginBottom:8}}>{label}</div>
            <div style={{fontSize:28,fontWeight:800,color:col}}>{val}</div>
          </div>
        ))}
      </div>

      {!myEmail&&<div style={{background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:12,padding:"12px 18px",marginBottom:20,fontSize:12,color:T.gold,fontWeight:600}}>
        ⚠ Add your Google email in Settings → Google API Setup so it auto-fills in access requests.
      </div>}

      {noGtm.length>0&&(
        <div style={{marginBottom:24}}>
          <div style={{fontSize:11,fontWeight:800,color:T.red,letterSpacing:1,marginBottom:12}}>✗ NO GTM ID - {noGtm.length} CLIENT{noGtm.length!==1?"S":""}</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {noGtm.map(client=>(
              <div key={client.id} className="glass-panel" style={{padding:"14px 18px",border:`1px solid ${T.red}22`}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,color:T.text}}>{client.name}</div>
                    <div style={{fontSize:12,color:T.ghost}}>{client.domain}</div>
                  </div>
                  <InpFree
                    defaultVal=""
                    onCommit={v=>setGtmInputs(g=>({...g,[client.id]:v}))}
                    placeholder="GTM-XXXXXXX"
                    mono={true}
                    style={{width:160,border:`1px solid ${T.red}44`,borderRadius:10,padding:"7px 12px",fontSize:12}}
                  />
                  <Btn sm variant="primary" onClick={()=>saveGtm(client)} disabled={!(gtmInputs[client.id]||"").trim()}>
                    {saved[client.id]?"✓ Saved":"Save"}
                  </Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {withGtm.length>0&&(
        <div>
          <div style={{fontSize:11,fontWeight:800,color:T.teal,letterSpacing:1,marginBottom:12}}>✓ GTM SET - {withGtm.length} CLIENT{withGtm.length!==1?"S":""}</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {withGtm.map(client=>{
              const isConnected=connected[client.id]==="connected";
              const isExpanded=expanded===client.id;
              const isConnecting=connecting[client.id];
              const isVerifying=verifying[client.id];
              return(
                <div key={client.id} className="glass-panel" style={{padding:0,overflow:"hidden",
                  border:`1px solid ${isConnected?T.teal+"44":T.surfaceBorder}`}}>
                  <div style={{padding:"14px 18px",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}
                    onClick={()=>setExpanded(isExpanded?null:client.id)}>
                    <div style={{width:9,height:9,borderRadius:"50%",background:isConnected?T.teal:T.ghost,
                      boxShadow:isConnected?`0 0 8px ${T.teal}`:"none",flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,color:T.text,fontSize:14}}>{client.name}</div>
                      <div style={{fontSize:12,color:T.ghost,marginTop:2}}>
                        <span style={{color:T.blue,fontFamily:"monospace",fontWeight:700}}>{client.gtm}</span>
                        <span style={{marginLeft:8}}>{client.domain}</span>
                      </div>
                    </div>
                    <span style={{fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:8,
                      background:isConnected?`${T.teal}15`:`${T.ghost}15`,
                      color:isConnected?T.teal:T.ghost}}>
                      {isConnected?"✓ OAuth Verified":"Needs OAuth"}
                    </span>
                    <InpFree
                      defaultVal={client.gtm||""}
                      onCommit={v=>setGtmInputs(g=>({...g,[client.id]:v}))}
                      placeholder="GTM-XXXXXXX"
                      mono={true}
                      style={{width:140,borderRadius:8,padding:"6px 10px",fontSize:12}}
                    />
                    {(gtmInputs[client.id]!==undefined&&gtmInputs[client.id]!==(client.gtm||""))&&(
                      <Btn sm variant="primary" onClick={e=>{e.stopPropagation();saveGtm(client);}}>
                        {saved[client.id]?"✓":"Save"}
                      </Btn>
                    )}
                    <span style={{fontSize:16,color:T.ghost,transform:isExpanded?"rotate(90deg)":"rotate(0deg)",transition:"transform 0.2s"}}>›</span>
                  </div>

                  {isExpanded&&(
                    <div style={{borderTop:`1px solid ${T.border}`,padding:"18px 20px",display:"flex",flexDirection:"column",gap:18}}>
                      {isConnected&&(
                        <div style={{background:`${T.teal}10`,border:`1px solid ${T.teal}33`,borderRadius:10,padding:"12px 16px",display:"flex",alignItems:"center",gap:12}}>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:700,color:T.teal}}>Publisher access verified - code injection ready</div>
                            <div style={{fontSize:11,color:T.ghost,marginTop:2}}>Tags will publish into {client.gtm} on next inject run.</div>
                          </div>
                          <Btn sm variant="ghost" onClick={()=>verifyAccess(client)} disabled={isVerifying}>{isVerifying?"Checking…":"Re-verify"}</Btn>
                        </div>
                      )}
                      <div>
                        <div style={{fontSize:11,fontWeight:800,color:T.ghost,letterSpacing:1,marginBottom:8}}>STEP 1 - COPY ACCESS REQUEST & SEND TO GTM OWNER</div>
                        <div style={{background:T.input,borderRadius:10,border:`1px solid ${T.border}`,padding:14,marginBottom:10}}>
                          <pre style={{fontSize:11,color:T.text,margin:0,lineHeight:1.7,whiteSpace:"pre-wrap",fontFamily:"inherit"}}>{accessMsg(client)}</pre>
                        </div>
                        <button onClick={()=>copyMsg(client)}
                          style={{fontSize:12,fontWeight:700,padding:"7px 18px",borderRadius:9,
                            border:`1px solid ${copied[client.id]?T.teal:T.blue}`,
                            background:copied[client.id]?`${T.teal}15`:`${T.blue}12`,
                            color:copied[client.id]?T.teal:T.blue,cursor:"pointer",transition:"all 0.2s"}}>
                          {copied[client.id]?"✓ Copied!":"Copy Access Request"}
                        </button>
                      </div>
                      {!isConnected&&(
                        <div>
                          <div style={{fontSize:11,fontWeight:800,color:T.ghost,letterSpacing:1,marginBottom:8}}>STEP 2 - CONNECT AFTER THEY GRANT ACCESS</div>
                          <div style={{fontSize:12,color:T.ghost,marginBottom:12,lineHeight:1.6}}>
                            Once {client.name} has added you as Publisher in their GTM, click Connect. Sign in with <strong style={{color:T.text}}>{myEmail||"your Google account"}</strong>.
                          </div>
                          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                            <button onClick={()=>connectGtm(client)} disabled={isConnecting||isVerifying}
                              style={{fontSize:13,fontWeight:800,padding:"9px 24px",borderRadius:10,border:"none",
                                background:isConnecting?T.border:`linear-gradient(135deg,${T.blue},#0E8FAA)`,
                                color:isConnecting?T.ghost:"#060C0F",cursor:isConnecting?"not-allowed":"pointer",
                                boxShadow:isConnecting?"none":"0 2px 14px rgba(21,180,214,0.3)"}}>
                              {isConnecting?"Waiting for browser auth…":"Connect GTM"}
                            </button>
                            {isConnecting&&(
                              <button onClick={()=>verifyAccess(client)} disabled={isVerifying}
                                style={{fontSize:12,fontWeight:700,padding:"8px 18px",borderRadius:9,
                                  border:`1px solid ${T.teal}`,background:`${T.teal}12`,color:T.teal,cursor:"pointer"}}>
                                {isVerifying?"Verifying…":"I authorized - Verify Now"}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================
// SIGN IN SCREEN — Treasure Chest
// =============================================
const AI_LOGOS = [
  { name: "ChatGPT",    color: "#10a37f", symbol: "⬡", delay: 0   },
  { name: "Perplexity", color: "#20b2aa", symbol: "⬢", delay: 80  },
  { name: "Google AI",  color: "#4285f4", symbol: "◉", delay: 160 },
  { name: "Claude",     color: "#d4a574", symbol: "◈", delay: 240 },
  { name: "Gemini",     color: "#8b5cf6", symbol: "✦", delay: 320 },
  { name: "Copilot",    color: "#0078d4", symbol: "⬟", delay: 400 },
  { name: "Grok",       color: "#e5e7eb", symbol: "✕", delay: 480 },
  { name: "Meta AI",    color: "#0668e1", symbol: "◍", delay: 560 },
];

const CORRECT_PASSWORD = "sharkai2026";

function SignInScreen({ onUnlock }) {
  const [input, setInput]         = useState("");
  const [open, setOpen]           = useState(false);
  const [exploding, setExploding] = useState(false);
  const [error, setError]         = useState(false);
  const [shake, setShake]         = useState(false);
  const [particles, setParticles] = useState([]);
  const [logosFly, setLogosFly]   = useState(false);
  const [done, setDone]           = useState(false);
  const inputRef = useRef(null);

  // Generate sparkle particles
  const spawnParticles = () => {
    const pts = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: 40 + Math.random() * 20,
      y: 30 + Math.random() * 20,
      vx: (Math.random() - 0.5) * 14,
      vy: -(Math.random() * 12 + 4),
      color: ["#F9DB79","#15B4D6","#4FD1A0","#F7555F","#a78bfa","#fff"][Math.floor(Math.random()*6)],
      size: Math.random() * 6 + 3,
      life: 1,
    }));
    setParticles(pts);
    setTimeout(() => setParticles([]), 1800);
  };

  const handleSubmit = () => {
    if (input.trim().toLowerCase() === CORRECT_PASSWORD) {
      setError(false);
      setOpen(true);
      setTimeout(() => {
        setExploding(true);
        spawnParticles();
        setTimeout(() => setLogosFly(true), 200);
        setTimeout(() => setDone(true), 1800);
        setTimeout(() => onUnlock({ email: "josh@sharkbrandingsolutions.com" }), 2400);
      }, 600);
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => setError(false), 2000);
    }
  };

  useEffect(() => {
    inputRef.current?.focus();
    const handler = e => { if (e.key === "Enter") handleSubmit(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [input]);

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "radial-gradient(ellipse at 50% 30%, #0d1f2d 0%, #060C0F 70%)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      fontFamily: "'Georgia', serif",
      overflow: "hidden",
      zIndex: 9999,
    }}>

      {/* Ambient grid */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "linear-gradient(rgba(21,180,214,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(21,180,214,0.03) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
        pointerEvents: "none",
      }}/>

      {/* Floating ambient glows */}
      <div style={{ position:"absolute", width:600, height:600, borderRadius:"50%",
        background:"radial-gradient(circle, rgba(21,180,214,0.06) 0%, transparent 70%)",
        top:"10%", left:"20%", pointerEvents:"none" }}/>
      <div style={{ position:"absolute", width:400, height:400, borderRadius:"50%",
        background:"radial-gradient(circle, rgba(249,219,121,0.04) 0%, transparent 70%)",
        bottom:"10%", right:"15%", pointerEvents:"none" }}/>

      {/* Sparkle particles */}
      {particles.map(p => (
        <div key={p.id} style={{
          position: "absolute",
          left: `calc(50% + ${p.x - 50}%)`,
          top: "45%",
          width: p.size, height: p.size,
          borderRadius: "50%",
          background: p.color,
          boxShadow: `0 0 ${p.size*2}px ${p.color}`,
          animation: `particle-${p.id} 1.6s ease-out forwards`,
          pointerEvents: "none",
        }}>
          <style>{`
            @keyframes particle-${p.id} {
              0%   { transform: translate(0,0) scale(1); opacity:1; }
              100% { transform: translate(${p.vx*30}px, ${p.vy*30}px) scale(0); opacity:0; }
            }
          `}</style>
        </div>
      ))}

      {/* AI logos flying out */}
      {logosFly && AI_LOGOS.map((logo, i) => {
        const angle = (i / AI_LOGOS.length) * 360;
        const rad = angle * Math.PI / 180;
        const dist = 220 + Math.random() * 80;
        const tx = Math.cos(rad) * dist;
        const ty = Math.sin(rad) * dist - 80;
        return (
          <div key={logo.name} style={{
            position: "absolute",
            top: "44%", left: "50%",
            transform: "translate(-50%, -50%)",
            animation: `flyout-${i} 1.4s cubic-bezier(0.22,1,0.36,1) ${logo.delay}ms forwards`,
            pointerEvents: "none",
            zIndex: 100,
          }}>
            <style>{`
              @keyframes flyout-${i} {
                0%   { transform: translate(-50%,-50%) scale(0) rotate(0deg); opacity:0; }
                30%  { opacity:1; }
                100% { transform: translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(1) rotate(${angle}deg); opacity: ${done ? 0 : 1}; }
              }
            `}</style>
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: `linear-gradient(135deg, ${logo.color}22, ${logo.color}44)`,
              border: `1.5px solid ${logo.color}88`,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              backdropFilter: "blur(8px)",
              boxShadow: `0 0 20px ${logo.color}44, 0 4px 16px rgba(0,0,0,0.4)`,
            }}>
              <span style={{ fontSize: 18, color: logo.color }}>{logo.symbol}</span>
              <span style={{ fontSize: 7, color: logo.color, fontWeight: 700, letterSpacing: 0.3, marginTop: 2, fontFamily: "monospace" }}>
                {logo.name.split(" ")[0].toUpperCase()}
              </span>
            </div>
          </div>
        );
      })}

      {/* Main chest container */}
      <div style={{ position: "relative", marginBottom: 40 }}>

        {/* Chest SVG */}
        <svg width="200" height="160" viewBox="0 0 200 160" style={{
          filter: open ? "drop-shadow(0 0 30px rgba(249,219,121,0.6))" : "drop-shadow(0 8px 24px rgba(0,0,0,0.6))",
          transition: "filter 0.4s ease",
          display: "block",
          margin: "0 auto",
        }}>

          {/* Glow underneath when open */}
          {open && (
            <ellipse cx="100" cy="155" rx="70" ry="10"
              fill="rgba(249,219,121,0.3)"
              style={{ animation: "pulseGlow 1s ease-in-out infinite alternate" }}
            />
          )}

          {/* Chest body */}
          <rect x="20" y="80" width="160" height="75" rx="8"
            fill="url(#chestBody)" stroke="#8B6914" strokeWidth="2"/>

          {/* Chest body planks */}
          <line x1="20" y1="100" x2="180" y2="100" stroke="#6B4F10" strokeWidth="1.5" opacity="0.5"/>
          <line x1="20" y1="120" x2="180" y2="120" stroke="#6B4F10" strokeWidth="1.5" opacity="0.5"/>
          <line x1="20" y1="140" x2="180" y2="140" stroke="#6B4F10" strokeWidth="1.5" opacity="0.5"/>

          {/* Metal band */}
          <rect x="20" y="108" width="160" height="12" rx="2"
            fill="url(#metalBand)" stroke="#8B6914" strokeWidth="1"/>

          {/* Lock */}
          <rect x="87" y="110" width="26" height="18" rx="4"
            fill={open ? "#F9DB79" : "#8B6914"} stroke="#6B4F10" strokeWidth="1.5"
            style={{ transition: "fill 0.3s" }}/>
          {!open && (
            <path d="M93 110 Q100 100 107 110" fill="none" stroke="#6B4F10" strokeWidth="2.5" strokeLinecap="round"/>
          )}
          {open && (
            <text x="100" y="123" textAnchor="middle" fontSize="10" fill="#060C0F" fontWeight="bold">✓</text>
          )}

          {/* Corner bolts */}
          {[[28,88],[172,88],[28,148],[172,148]].map(([cx,cy],i) => (
            <circle key={i} cx={cx} cy={cy} r="5" fill="url(#bolt)" stroke="#6B4F10" strokeWidth="1"/>
          ))}

          {/* Chest lid — rotates open */}
          <g style={{
            transformOrigin: "100px 82px",
            transform: open ? "rotateX(-110deg)" : "rotateX(0deg)",
            transition: "transform 0.7s cubic-bezier(0.34,1.56,0.64,1)",
          }}>
            <rect x="18" y="40" width="164" height="44" rx="8 8 0 0"
              fill="url(#chestLid)" stroke="#8B6914" strokeWidth="2"/>
            {/* Lid planks */}
            <line x1="18" y1="55" x2="182" y2="55" stroke="#6B4F10" strokeWidth="1.5" opacity="0.5"/>
            <line x1="18" y1="68" x2="182" y2="68" stroke="#6B4F10" strokeWidth="1.5" opacity="0.5"/>
            {/* Lid band */}
            <rect x="18" y="58" width="164" height="10" rx="2"
              fill="url(#metalBand)" stroke="#8B6914" strokeWidth="1"/>
            {/* Lid corner bolts */}
            {[[26,48],[174,48],[26,78],[174,78]].map(([cx,cy],i) => (
              <circle key={i} cx={cx} cy={cy} r="5" fill="url(#bolt)" stroke="#6B4F10" strokeWidth="1"/>
            ))}
          </g>

          {/* Gold glow inside when open */}
          {open && (
            <ellipse cx="100" cy="82" rx="55" ry="18"
              fill="url(#goldGlow)" opacity="0.9"/>
          )}

          {/* Gradients */}
          <defs>
            <linearGradient id="chestBody" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C8952A"/>
              <stop offset="100%" stopColor="#7A5010"/>
            </linearGradient>
            <linearGradient id="chestLid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D4A030"/>
              <stop offset="100%" stopColor="#8B6014"/>
            </linearGradient>
            <linearGradient id="metalBand" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#E8C84A"/>
              <stop offset="50%" stopColor="#B8920A"/>
              <stop offset="100%" stopColor="#E8C84A"/>
            </linearGradient>
            <radialGradient id="bolt" cx="40%" cy="35%">
              <stop offset="0%" stopColor="#F0D060"/>
              <stop offset="100%" stopColor="#8B6914"/>
            </radialGradient>
            <radialGradient id="goldGlow" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#FFE566" stopOpacity="1"/>
              <stop offset="60%" stopColor="#F9DB79" stopOpacity="0.8"/>
              <stop offset="100%" stopColor="#C8952A" stopOpacity="0"/>
            </radialGradient>
          </defs>

          <style>{`
            @keyframes pulseGlow {
              from { opacity: 0.3; rx: 60; }
              to   { opacity: 0.7; rx: 80; }
            }
          `}</style>
        </svg>

        {/* Coin sparkles around chest when open */}
        {open && [0,60,120,180,240,300].map((deg,i) => (
          <div key={i} style={{
            position: "absolute",
            top: "50%", left: "50%",
            width: 8, height: 8,
            borderRadius: "50%",
            background: "#F9DB79",
            boxShadow: "0 0 8px #F9DB79",
            transform: `rotate(${deg}deg) translate(${70 + i*4}px) rotate(-${deg}deg)`,
            animation: `sparkle ${0.8 + i*0.1}s ease-in-out infinite alternate`,
            animationDelay: `${i*120}ms`,
            pointerEvents: "none",
          }}>
            <style>{`
              @keyframes sparkle {
                from { opacity:0.2; transform: rotate(${deg}deg) translate(${65+i*4}px) scale(0.5); }
                to   { opacity:1;   transform: rotate(${deg}deg) translate(${80+i*4}px) scale(1.2); }
              }
            `}</style>
          </div>
        ))}
      </div>

      {/* Brand */}
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{
          fontSize: 13, fontWeight: 700, letterSpacing: 4,
          color: "#15B4D6", fontFamily: "monospace",
          marginBottom: 8, textTransform: "uppercase",
        }}>
          Shark Branding Solutions
        </div>
        <div style={{
          fontSize: 36, fontWeight: 800,
          background: "linear-gradient(135deg, #F9DB79, #15B4D6, #4FD1A0)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          letterSpacing: -1, lineHeight: 1.1,
          fontFamily: "'Georgia', serif",
        }}>
          SharkAI
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginTop: 6, letterSpacing: 2 }}>
          GEO INTELLIGENCE PLATFORM
        </div>
      </div>

      {/* Input area */}
      {!open && (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
          animation: shake ? "shake 0.4s ease" : "none",
        }}>
          <style>{`
            @keyframes shake {
              0%,100% { transform: translateX(0); }
              20%      { transform: translateX(-8px); }
              40%      { transform: translateX(8px); }
              60%      { transform: translateX(-6px); }
              80%      { transform: translateX(6px); }
            }
          `}</style>

          <div style={{ position: "relative" }}>
            <input
              ref={inputRef}
              type="password"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Enter access code..."
              style={{
                width: 280,
                padding: "14px 20px",
                borderRadius: 12,
                border: `1.5px solid ${error ? "#F7555F" : input ? "#15B4D6" : "rgba(255,255,255,0.1)"}`,
                background: "rgba(255,255,255,0.04)",
                color: "#fff",
                fontSize: 15,
                fontFamily: "'Georgia', serif",
                outline: "none",
                textAlign: "center",
                letterSpacing: 3,
                transition: "border-color 0.2s, box-shadow 0.2s",
                boxShadow: error
                  ? "0 0 0 3px rgba(247,85,95,0.15)"
                  : input ? "0 0 0 3px rgba(21,180,214,0.12)" : "none",
              }}
            />
            {error && (
              <div style={{
                position: "absolute", bottom: -24, left: 0, right: 0,
                textAlign: "center", fontSize: 11, color: "#F7555F",
                fontFamily: "monospace", letterSpacing: 1,
              }}>
                INVALID CODE — TRY AGAIN
              </div>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={!input.trim()}
            style={{
              padding: "12px 40px",
              borderRadius: 10,
              border: "none",
              background: input.trim()
                ? "linear-gradient(135deg, #15B4D6, #4FD1A0)"
                : "rgba(255,255,255,0.06)",
              color: input.trim() ? "#060C0F" : "rgba(255,255,255,0.2)",
              fontSize: 13, fontWeight: 800,
              fontFamily: "'Georgia', serif",
              letterSpacing: 2, cursor: input.trim() ? "pointer" : "not-allowed",
              transition: "all 0.2s",
              boxShadow: input.trim() ? "0 4px 20px rgba(21,180,214,0.3)" : "none",
              textTransform: "uppercase",
            }}
          >
            Unlock
          </button>

          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.15)", letterSpacing: 1, fontFamily: "monospace" }}>
            AUTHORIZED ACCESS ONLY
          </div>
        </div>
      )}

      {/* Success flash */}
      {done && (
        <div style={{
          position: "fixed", inset: 0,
          background: "radial-gradient(circle at 50% 45%, rgba(249,219,121,0.15), transparent 70%)",
          pointerEvents: "none",
          animation: "flashFade 0.8s ease-out forwards",
        }}>
          <style>{`
            @keyframes flashFade {
              0%   { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}


export default function App(){
  const isDemoMode = window.location.hash === "#demo" || window.location.search.includes("demo=1");
  const [nav,setNav]=useState("clients");
  const [clients,setClients]=useState(isDemoMode ? DEMO_CLIENTS : ()=>{const s=LS.get("clients");return Array.isArray(s)&&s.length?s:SEED;});
  const [selected,setSelected]=useState(null);
  const [user,setUser]=useState(isDemoMode ? {email:"demo@sharkbrandingsolutions.com",isDemo:true} : ()=>LS.get("user")||null);
  const [cmdOpen, setCmdOpen]=useState(false);
  const [isLoading, setIsLoading]=useState(!isDemoMode);
  const [toasts, setToasts]=useState([]);

  // Register global toast emitter
  useEffect(()=>{
    _toastEmit=(msg,type,duration)=>{
      const id=Date.now()+Math.random();
      setToasts(p=>[...p,{id,msg,type}]);
      setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)), duration||3500);
    };
    return()=>{_toastEmit=null;};
  },[]);

  useEffect(()=>{
    if (isDemoMode) { setIsLoading(false); return; }
    setIsLoading(true);
    ipc("get-clients").then(cs=>{
      if(Array.isArray(cs)&&cs.length) setClients(cs);
      setTimeout(() => setIsLoading(false), 1200);
    });
  },[]);

  // Cmd+K global listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const addClient=async(form)=>{
    const score=Math.floor(Math.random()*35)+55;
    const c={id:Date.now().toString(),...form,score,grade:gradeFromScore(score),
      city:form.city||"",
      lastUpdated:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})};
    await ipc("save-client",c);
    setClients(prev=>[c,...prev]);
    showToast(`${form.name} added successfully`, 'success');
  };
  const deleteClient=async(id)=>{
    const name=clients.find(c=>c.id===id)?.name||"Client";
    await ipc("delete-client",id);
    setClients(prev=>(Array.isArray(prev)?prev:[]).filter(c=>c.id!==id));
    if(selected?.id===id)setSelected(null);
    showToast(`${name} removed`, 'info');
  };
  const updateClient=(updated)=>{
    setClients(prev=>prev.map(c=>c.id===updated.id?updated:c));
    if(selected?.id===updated.id)setSelected(updated);
  };
  const signIn=(u)=>{setUser(u);LS.set("user",u||{email:"josh@sharkbrandingsolutions.com"});};
  const signOut=()=>{setUser(null);LS.set("user",null);};

  const getTitle=()=>selected?selected.name:{clients:"Clients",schema:"Code Injector",podcast:"Podcast Citation Engine",gtm:"GTM Manager",settings:"Settings"}[nav];
  const getBreadcrumb=()=>selected?`Pages / Clients / ${selected.name}`:`Pages / ${getTitle()}`;

  // Full-screen gate — show treasure chest sign-in until unlocked
  if (!user) return (
    <>
      <style>{GS}</style>
      <SignInScreen onUnlock={(u) => signIn(u)} />
    </>
  );

  return(
    <>
      <style>{GS}</style>
      <div style={{display:"flex",height:"100vh",width:"100vw",background:T.bg,overflow:"hidden"}}>
        {isDemoMode&&(
          <div style={{position:"fixed",top:0,left:0,right:0,zIndex:9999,
            background:"linear-gradient(90deg,#F9DB79,#15B4D6,#4FD1A0)",
            padding:"6px 0",textAlign:"center",fontSize:11,fontWeight:800,
            color:"#060C0F",letterSpacing:1.5}}>
            DEMO MODE - Sample data only - No real clients or API calls -
            <a href="mailto:info@sharkbrandingsolutions.com"
              style={{color:"#060C0F",marginLeft:8,textDecoration:"underline",cursor:"pointer"}}>
              Contact SBS to get access
            </a>
          </div>
        )}
        <Sidebar activeNav={nav} onNavChange={n=>{setNav(n);setSelected(null);}}/>
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <Header title={getTitle()} breadcrumb={getBreadcrumb()} user={user} onSignIn={signIn} onSignOut={signOut} onOpenCmdK={()=>setCmdOpen(true)}/>
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            {nav==="clients"&&!selected&&<ClientList clients={clients} onSelect={setSelected} onAdd={addClient} onDelete={deleteClient} isLoading={isLoading}/>}
            {nav==="clients"&&selected&&<ClientDashboard client={selected} onBack={()=>setSelected(null)} onClientUpdate={updateClient} isDemoMode={isDemoMode} demoAudit={DEMO_AUDITS[selected.id]||null} demoCrawl={DEMO_CRAWL[selected.id]||null}/>}
            {nav==="schema"&&<SchemaInjector clients={clients} isDemoMode={isDemoMode} onClientsUpdate={()=>{if(!isDemoMode)ipc("get-clients").then(cs=>{if(Array.isArray(cs)&&cs.length)setClients(cs);});}}/>}
            {nav==="podcast"&&<PodcastCitationEngine clients={clients}/>}
            {nav==="gtm"&&<GTMManager clients={clients} onClientsUpdate={()=>ipc("get-clients").then(cs=>{if(Array.isArray(cs)&&cs.length)setClients(cs);})}/>}
            {nav==="settings"&&<SettingsView clients={clients}/>}
          </div>
        </div>
      </div>
      <CommandPalette
        isOpen={cmdOpen}
        onClose={() => setCmdOpen(false)}
        clients={clients}
        onSelectClient={(c) => { setNav("clients"); setSelected(c); setCmdOpen(false); }}
        onNavigate={(n) => { setNav(n); setSelected(null); setCmdOpen(false); }}
      />
      <ToastContainer toasts={toasts} onDismiss={(id)=>setToasts(p=>p.filter(t=>t.id!==id))}/>
    </>
  );
}
